/* eslint prefer-const: 0 */
import { CSSProperties } from '@material-ui/styles';
import { DragLayerMonitor, DropTargetMonitor, XYCoord } from 'react-dnd';
import { orderBy, partition, round } from 'lodash';

import {
  ClipboardDraggableObject,
  CoordinatePoint,
  Coordinates,
  DraggableFixture,
  DraggableFixtureParams,
  DraggableItemCoordinates,
  DraggablePegItem,
  FacingPack,
  FixtureDraggableObject,
  FixtureHeight,
  FixturePosition,
  FixtureSpace,
  ItemCoordinates,
  PegboardStartHoles,
  SegmentSpaces,
  SplicerPosition,
  Splicers,
} from '../../../types/editor';
import {
  acceptedFixtureType,
  CLIPBOARD_HORIZONTAL_OFFSET,
  CLIPBOARD_TOP_OFFSET,
  DISPLAY_HEIGHT,
  DISTRICT_TYPES,
  DraggableClipboardFixtureItems,
  DraggableClipboardFixtureSkuItems,
  DraggableFixtureClipboardItems,
  DraggableFixtureItems,
  DraggableSkuClipboardItems,
  DraggableWorkbenchItems,
  DROP_EFFECTS,
  FIXTURE_TYPES,
  FixtureType,
  HORIZONTAL_SPACING,
  PEGBOARD_TEMPLATE,
  PEGBOARD_Y_OFFSET,
  PEGGABLE_CODES_TO_DROP,
  SEGMENT_WIDTH,
  ShelfHighlightColors,
  WORKBENCH_EXTRA_HEIGHT,
  WORKBENCH_FACING_SPACE,
  WORKBENCH_HEIGHT,
  WORKBENCH_MARGIN_TOP,
} from '../editorConstants';
import {
  ARROW_MOVE_VALUE,
  ARROW_MOVE_VALUE_PEGBOARD,
  criticalAlertTypes,
  excludedProductAlertTypes,
  EXTRA_ESL_HEIGHT_OUTSIDE_FACING,
  MAX_VALUE_MAX_BINCAP,
  MAX_VALUE_MIN_BINCAP,
  userActionAlertTypes
} from '../../../utils/constants';
import {
  ExcludedProductWarnings,
  FacingGropOrigin,
  FacingGroup,
  FacingGroup as FacingGroupExtended,
  FdDwoBincapWarnings,
  HeavyProductHeightWarnings,
  LinearWhitespaceWarnings,
  LocatedFixture,
  MoveBasketToSelfWarnings,
  MoveCrossbarToPegWarnings,
  Nesting,
  Peghole,
  Point,
  ProductMissingBlockingDataWarnings,
  TransformPeghole,
  WarningData,
  WarningsData,
  WeightRestrictionWarnings,
} from '../../../state/job/job.types';
import { Product } from '../../../state/products/products.types';
import { Fixture, FixtureById } from '../../../state/fixtures/fixtures.types';
import { escapeRegExp, generateUniqueId } from '../../../helpers/utils';
import { historyStepItem } from '../../../state/viewAndEdit/viewAndEdit.reducer';
import { AlertType, AssortmentItemOptimized, TabInfo } from '../../../types/models';
import { Direction } from '../../../hooks/useKeyNavigation';
import { getDropEffectFromStorage } from '../../../helpers/sessionStorage.helper';

export const getCurrentOffset = (monitor: DragLayerMonitor) => {
  const sourceClientOffset = monitor.getSourceClientOffset();
  const item = monitor.getItem();

  if (item && sourceClientOffset) {
    const { currentRightOffset = 0, currentTopOffset = 0 } = item;
    const x = sourceClientOffset.x - currentRightOffset;
    const y = sourceClientOffset.y - currentTopOffset;

    return { x, y };
  }

  return null;
};

export const getProcessedPositionsFromMonitor = (
  monitor: DragLayerMonitor | DropTargetMonitor,
  parentElement: HTMLDivElement | null,
) => {
  const { left, top, bottom, width, height } = parentElement?.getBoundingClientRect() || { left: 0, top: 0, bottom: 0, width: 0, height: 0 };

  const item = monitor.getItem();
  const currentCursorPosition = monitor.getClientOffset();
  const currentOffset = monitor.getDifferenceFromInitialOffset();
  const sourceClientOffset = monitor.getSourceClientOffset() as CoordinatePoint;

  if (item && currentCursorPosition && currentOffset && sourceClientOffset) {
    const { x, y } = sourceClientOffset;
    const { currentLeftOffset = 0, currentTopOffset = 0 } = item;
    const rootX = x - left + currentLeftOffset;
    const rootY = bottom - y + currentTopOffset;
    const correctedMouseX = currentCursorPosition.x - left;
    const correctedMouseY = currentCursorPosition.y - top;
    const correcteClipboardX = currentCursorPosition.x - x;
    const correcteClipboardY = currentCursorPosition.y - y;

    return {
      correctedMousePosition: { x: correctedMouseX, y: correctedMouseY },
      itemRootPosition: { x: rootX, y: rootY },
      containerBoundary: { left: 0, right: width, top: height },
      correctedClipboardItem: { x: correcteClipboardX, y: correcteClipboardY }
    };
  }

  return null;
};

export const getProductData = (
  product: FacingGroupExtended,
  pixelsPerInch: number
) => ({
  horizontalFacings: product.horizontal_facings,
  verticalFacings: product.vertical_facings,
  width: product.packaging.width * pixelsPerInch,
  height: product.packaging.height * pixelsPerInch,
  xPosition: product.x * pixelsPerInch,
  yPosition: product.y * pixelsPerInch,
  nestedHeight: product.packaging.nested_height * pixelsPerInch,
  nesting: product.cap_nested as Nesting,
  depth: product.packaging.depth,
  cappedVertical: product.capped_vertical_facings,
  cappedOrientationCode: product.capped_orientation_code,
  isCrossbar: Boolean(product.fixtureType === 'CROSSBAR'),
  isPegboard: Boolean(product.fixtureType === 'PEGBOARD'),
  isSpecialty: Boolean(product.fixtureType === 'SPECIALTY')
});

export const getTargetBincapData = (assortment: Array<AssortmentItemOptimized>) => {
  const response = { minBincap: 0, maxBincap: 0, currentBincap: 0, targetMinBincap: 0, targetMaxBincap: 0 };

  if (assortment.length === 0) return response;

  const record = assortment[0];
  const { corpStatus, totalCommitments, optimized } = record;

  if (!optimized) return response;

  const { bincap, newMaxTargetBincapOptimized, newMinTargetBincapOptimized } = optimized;
  const isFdDwo = ['FD', 'DWO'].includes(corpStatus);

  response.currentBincap = bincap;
  response.minBincap = newMinTargetBincapOptimized || 0;
  response.maxBincap = newMaxTargetBincapOptimized || isFdDwo ? totalCommitments : 0;
  response.targetMinBincap = isFdDwo ? totalCommitments : MAX_VALUE_MIN_BINCAP;
  response.targetMaxBincap = isFdDwo ? totalCommitments : MAX_VALUE_MAX_BINCAP;

  return response;
};

export const getDashedBoxPosition = (
  productData: FacingGroupExtended,
  selectedFacingEntities: Record<string, Array<number>>,
  pixelsPerInch: number
) => {
  if (!productData) return {};
  const {
    id,
    x,
    y,
    horizontal_facings: h,
    vertical_facings: v,
    cap_nested: nesting,
    capped_vertical_facings: cappedVertical,
    packaging: {
      width,
      height,
      nested_height: nestedHeight,
    },
    cappedPackaging: {
      height: cappedHeight
    }
  } = productData;
  const {
    isMultipleSelected,
    leftOffsetByFacings,
    horizontalFacings
  } = detectSelectionByContextMenu(selectedFacingEntities, id, h);

  if (isMultipleSelected) {
    return {
      display: 'none'
    };
  }

  const leftOffset = leftOffsetByFacings * width;
  const currentHeight = getFacingHeight(
    height,
    v,
    nesting,
    nestedHeight,
    cappedHeight,
    cappedVertical
  );

  return {
    width: width * horizontalFacings * pixelsPerInch + 8,
    height: currentHeight * pixelsPerInch + 8,
    left: (leftOffset + x) * pixelsPerInch - 4,
    bottom: y * pixelsPerInch - 4
  };
};

type DraggableRect = {
  draggableLeft: number;
  draggableRight: number;
  draggableBottom: number;
  draggableTop: number;
};

export const makeDraggableRect = (x: number, y: number, width: number, height: number): DraggableRect => ({
  draggableLeft: x,
  draggableRight: x + width,
  draggableBottom: y - height,
  draggableTop: y
});

export const makeDraggableRectShift = (
  x: number,
  y: number,
  width: number,
  height: number,
  rightBoundary: number): DraggableRect => {
  let nextX = x;
  const draggableRight = x + width;

  if (nextX < 0) {
    nextX = 0;
  }

  if (draggableRight > rightBoundary) {
    nextX = rightBoundary - width;
  }

  return makeDraggableRect(nextX, y, width, height);
};

export const makeRect = (x: number, y: number, width: number, height: number) => ({
  x1: x,
  x2: x + width,
  y1: y,
  y2: y + height
});

export const removeActiveClasses = (ids: string[]) => {
  ids?.forEach(id => {
    const el = document.getElementById(id) as HTMLElement;

    if (el) {
      el.classList.remove('active');
    }
  });
};

export const setActiveClasses = (ids: string[]) => {
  ids.forEach(id => {
    const el = document.getElementById(id) as HTMLElement;

    if (el) {
      el.classList.add('active');
    }
  });
};

export const generateIdsInRange = (start: number, end: number, step: number, id: string) => {
  return end > start
    ? Array.from(Array.from(Array(Math.ceil((end - start) / step)).keys()), x => `facing-group-item-${id}-${start + x * step}`)
    : [];
};

export const generateFacingsInRange = (start: number, end: number, step: number = 1) => {
  return Array.from(Array.from(Array(Math.ceil((end - start) / step)).keys()), x => start + x * step);
};

const getClipboardXOffset = (xOffset: number, type: string): number => xOffset >= 0 && Object.values(DraggableSkuClipboardItems).includes(type) ? xOffset : 0;

export const getActiveProductsFromMonitor = (
  monitor: DragLayerMonitor | DropTargetMonitor,
  parentElement: HTMLDivElement | null,
  itemsCoordinates: ItemCoordinates[],
  fixturesSpaces: FixtureSpace[],
  scale: number,
  facingsByIds: Record<string, FacingGroupExtended>,
  itemsIds: string[],
  segmentSpaces: SegmentSpaces[],
  distinctType: string = '',
  isEslPog: boolean = false,
  splicer: Splicers
) => {
  const sourceClientOffset = monitor.getSourceClientOffset() as CoordinatePoint;
  const dragObject = monitor.getItem();
  const positions = getProcessedPositionsFromMonitor(monitor, parentElement);
  const clientOffset = monitor.getClientOffset();
  const type = monitor.getItemType() as string;

  if (sourceClientOffset && dragObject && positions && clientOffset && type) {
    if (
      Object.values(DraggableFixtureClipboardItems).includes(type) ||
      Object.values(DraggableClipboardFixtureItems).includes(type) ||
      Object.values(DraggableFixtureItems).includes(type)
    ) return null;

    const { itemRootPosition: { x: rootX, y: rootY }, correctedClipboardItem } = positions;
    const {
      fixtureType,
      hmax,
      groupedWidth,
      clipboardOffsetRight,
      lastItemWidthScaled,
      clipboardOffsetTop,
      facingsIds,
      id
    } = dragObject;
    const horizontalSpacing = facingsIds?.length > 1 ? HORIZONTAL_SPACING : 0;
    const xOffset = correctedClipboardItem.x + clipboardOffsetRight - groupedWidth + lastItemWidthScaled + horizontalSpacing * scale;
    const clipboardXOffset = getClipboardXOffset(xOffset, type);
    const clipboardYOffset = Object.values(DraggableSkuClipboardItems).includes(type)
      ? clipboardOffsetTop - correctedClipboardItem.y
      : 0;
    const x = (rootX + clipboardXOffset) / scale;
    const y = (rootY + clipboardYOffset) / scale;
    const width = groupedWidth / scale;
    const height = hmax / scale;
    const draggableItem = makeDraggableRect(x, y, width, height);
    const { draggableLeft, draggableRight } = draggableItem;
    const eslExtraHeight = (isEslPog ? EXTRA_ESL_HEIGHT_OUTSIDE_FACING : 0);

    removeActiveClasses(itemsIds);
    removeActiveSplicerClasses(splicer?.splicerIds);

    const isCrossbarFacing = isPeggableFixture(fixtureType);
    const isPlanogram = distinctType === DISTRICT_TYPES.PLANOGRAM || distinctType === DISTRICT_TYPES.WORKBENCH;
    const isPegOnVisual = fixtureType === FIXTURE_TYPES.PEGBOARD && isPlanogram;

    if (isPegOnVisual) {
      const draggableItems = draggablePegItems(x, y, facingsByIds, facingsIds, id, isEslPog);

      const activeFacingGroupIds = draggableItems.reduce((collection, item) => {
        const draggableItem = {
          draggableLeft: item.x1,
          draggableRight: item.x2,
          draggableBottom: item.y1,
          draggableTop: item.y2
        };

        const activeFacings = itemsCoordinates.reduce((acc, item) => {

          if (item.isCrossbar) {
            const isOverlapping = isItemsOverlap(item, draggableItem);

            if (isOverlapping) acc.push(item.id);
          }

          return acc;
        }, [] as string[]);

        collection.push(...activeFacings);

        return collection;
      }, [] as string[]);

      const { splicerCoordinates } = splicer;

      const activeSplicerIds = draggableItems.reduce((collection, item) => {
        if (!item.peghole) return [];
        const { peghole: { x, y, width, height }} = item;
        const draggableBottom = y < 0 ? item.y2 - height - eslExtraHeight : item.y2 - height - y - eslExtraHeight;
        const draggableTop = y < 0 ? item.y2 + Math.abs(y) - eslExtraHeight : item.y2 - Math.abs(y) + height - eslExtraHeight;
        const draggableItem = {
          draggableLeft: item.x1 + x,
          draggableRight: item.x1 + x + width,
          draggableBottom,
          draggableTop
        };

        const activeFacings = splicerCoordinates.reduce((acc, item) => {
          const isOverlapping = isItemsOverlap(item, draggableItem);

          if (isOverlapping) acc.push(`${item.id}`);

          return acc;
        }, [] as string[]);

        collection.push(...activeFacings);

        return collection;
      }, [] as string[]);

      const extendedFacingIds = getActiveIdsFromFacingGroup(
        activeFacingGroupIds,
        facingsByIds,
        1,
        draggableLeft,
        draggableRight
      );

      if (extendedFacingIds && extendedFacingIds.length) setActiveClasses(extendedFacingIds);
      if (activeSplicerIds.length) setActiveSplicerClasses(activeSplicerIds);
    } else if (isCrossbarFacing) {
      const y = (rootY + clipboardYOffset) / scale + (isEslPog ? EXTRA_ESL_HEIGHT_OUTSIDE_FACING : 0);
      const height = hmax / scale + (isEslPog ? EXTRA_ESL_HEIGHT_OUTSIDE_FACING : 0);
      const draggableItem = makeDraggableRect(x, y, width, height);
      const activeFacingGroupIds = itemsCoordinates.reduce((acc, item) => {
        if (item.isCrossbar) {
          const isOverlapping = isItemsOverlap(item, draggableItem);

          if (isOverlapping) acc.push(item.id);
        }

        return acc;
      }, [] as string[]);

      const extendedFacingIds = getActiveIdsFromFacingGroup(
        activeFacingGroupIds,
        facingsByIds,
        1,
        draggableLeft,
        draggableRight
      );

      const activeSplicerIds = draggablePegItems(x, y, facingsByIds, facingsIds, id, isEslPog).reduce((collection, item) => {
        if (!item.peghole) return [];
        const { peghole: { x, y, width, height }} = item;
        const draggableBottom = y < 0 ? item.y2 - height - 2 * eslExtraHeight : item.y2 - height - y - 2 * eslExtraHeight;
        const draggableTop = y < 0 ? item.y2 + Math.abs(y) - 2 * eslExtraHeight : item.y2 - Math.abs(y) + height - 2 * eslExtraHeight;
        const draggableItem = {
          draggableLeft: item.x1 + x,
          draggableRight: item.x1 + x + width,
          draggableBottom,
          draggableTop
        };
        const { splicerCoordinates } = splicer;

        const activeFacings = splicerCoordinates.reduce((acc, item) => {
          const isOverlapping = isItemsOverlap(item, draggableItem);

          if (isOverlapping) acc.push(`${item.id}`);

          return acc;
        }, [] as string[]);

        collection.push(...activeFacings);

        return collection;
      }, [] as string[]);

      if (extendedFacingIds && extendedFacingIds.length) setActiveClasses(extendedFacingIds);

      if (activeSplicerIds.length) setActiveSplicerClasses(activeSplicerIds);

    } else {
      const activeFacingGroupIds = itemsCoordinates.reduce((acc, item) => {
        const isOverlapping = isItemsOverlap(item, draggableItem);

        if (isOverlapping) acc.push(item.id);

        return acc;
      }, [] as string[]);

      const occupiedFixtures = getOccupiedFixtures(fixturesSpaces, draggableItem);
      const occupiedSpaces = getSegmentSpaces(segmentSpaces, draggableItem, scale);
      const isFixturesCorrespondToSegments = occupiedFixtures.length === occupiedSpaces.length;
      const isFacingFits = isFixturesOnOneLevel(occupiedFixtures) && isFixturesCorrespondToSegments;

      if (isFacingFits) {
        const extendedFacingIds = getActiveIdsFromFacingGroup(
          activeFacingGroupIds,
          facingsByIds,
          1,
          draggableLeft,
          draggableRight
        );

        setActiveClasses(extendedFacingIds);
      }
    }

    return true;
  }

  return null;
};

export const getActiveIdsFromFacingGroup = (
  activeFacingGroupIds: string[],
  facingsByIds: Record<string, FacingGroupExtended>,
  scale: number,
  draggableLeft: number,
  draggableRight: number
): string[] => {
  return activeFacingGroupIds.reduce((acc, val) => {
    const {
      x,
      horizontal_facings: h,
      packaging: {
        width
      }
    } = facingsByIds[val];

    if (h > 1) {
      const xStart = x * scale;
      const scaledWidth = width * scale;
      const xEnd = xStart + h * scaledWidth;

      if (
        draggableLeft < xStart &&
        draggableRight < xEnd
      ) {
        const xOffset = draggableRight - xStart;
        const touchingFacings = Math.ceil(xOffset / scaledWidth);
        const touchingFacingsArr = generateIdsInRange(0, touchingFacings, 1, val);

        acc.push(...touchingFacingsArr);
      } else if (
        draggableLeft >= xStart &&
        draggableRight >= xEnd
      ) {
        const hEnd = Math.ceil((xEnd - draggableLeft) / scaledWidth);
        const touchingFacings = generateIdsInRange(h - hEnd, h, 1, val);

        acc.push(...touchingFacings);
      } else if (
        draggableLeft >= xStart &&
        draggableRight <= xEnd
      ) {
        const hStart = Math.floor((draggableLeft - xStart) / scaledWidth);
        const hEnd = Math.ceil((draggableRight - xStart) / scaledWidth);
        const touchingFacings = generateIdsInRange(hStart, hEnd, 1, val);

        acc.push(...touchingFacings);
      } else if (
        draggableLeft <= xStart &&
        draggableRight >= xEnd
      ) {
        acc.push(val);
      }
    } else {
      acc.push(val);
    }

    return acc;
  }, [] as string[]);
};

type Block = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  type: Nesting;
};

type Blocks = Array<Block>;

export const getFixtureBlocks = (facingGroup: FacingGroupExtended): Blocks => {
  const blocks: Blocks = [];

  for (let v = 0; v < facingGroup.vertical_facings; v++) {
    if (facingGroup.cap_nested === Nesting.YNested && v > 0) {
      const x1 = facingGroup.x + facingGroup.packaging.width;
      const y1 = facingGroup.y + facingGroup.packaging.height + facingGroup.packaging.nested_height * (v - 1);

      blocks.push({
        x1: x1,
        y1: y1,
        x2: x1 + facingGroup.packaging.width,
        y2: y1 + facingGroup.packaging.nested_height,
        type: Nesting.YNested,
      });
    } else {
      const x1 = facingGroup.x + facingGroup.packaging.width;
      const y1 = facingGroup.y + facingGroup.packaging.height * v;

      blocks.push({
        x1,
        y1,
        x2: x1 + facingGroup.packaging.width,
        y2: y1 + facingGroup.packaging.height,
        type: Nesting.None,
      });
    }
  }

  if (facingGroup.cap_nested === Nesting.YCapped) {
    const x = facingGroup.x;
    const y = facingGroup.y + facingGroup.packaging.height * facingGroup.vertical_facings;
    const capped = facingGroup.cappedPackaging;

    for (let v = 0; v < facingGroup.capped_vertical_facings; v++) {
      const x1 = x + capped.width;
      const y1 = y + capped.height * v;

      blocks.push({
        x1,
        y1,
        x2: x1 + capped.width,
        y2: y1 + capped.height,
        type: Nesting.YCapped,
      });
    }
  }

  return blocks;
};

type MaxVerticalStack = {
  maxVerticalFacings: number;
  maxCappedVerticalFacings: number;
};

export const maxVerticalStack = (
  facingGroup: FacingGroupExtended,
  locatedFixture: LocatedFixture,
): MaxVerticalStack => {
  let maxVerticalFacings = facingGroup.vertical_facings;
  let maxCappedVerticalFacings = facingGroup.capped_vertical_facings;
  const topGrillePosition = locatedFixture.grille_height;
  const facingGroupByFixture = {
    ...facingGroup,
    x: 0,
    y: 0,
  };
  const fixtureBlocks = getFixtureBlocks(facingGroupByFixture);

  if (locatedFixture.type === 'BASKET' && facingGroup.cap_nested !== Nesting.YNested) {
    maxVerticalFacings = 0;
    maxCappedVerticalFacings = 0;
    fixtureBlocks.forEach(block => {
      if (block.y1 < topGrillePosition) {
        if (block.type === Nesting.None) {
          maxVerticalFacings += 1;
        }

        if (block.type === Nesting.YCapped) {
          maxCappedVerticalFacings += 1;
        }
      }
    });
  }

  return { maxVerticalFacings, maxCappedVerticalFacings };
};

export const getFacingHeight = (
  height: number,
  v: number,
  nested: Nesting | string,
  nestedHeight: number,
  cappedHeight: number,
  cappedVertical: number,
): number => {
  switch (nested) {
    case Nesting.YCapped: {
      return height * v + cappedHeight * cappedVertical;
    }

    case Nesting.YNested: {
      return height + (v - 1) * nestedHeight;
    }

    default: {
      return height * v;
    }
  }
};

export const getBaseFixture = (
  fixtureSpaces: FixtureSpace[],
  scale: number = 1,
  draggableLeft: number,
  draggableRight: number,
  draggableTop: number,
  draggableBottom: number,
) => {
  const [fixture] = fixtureSpaces.filter(({ x1, x2, y1, y2 }) => (
    draggableLeft <= x2 && draggableRight >= x1 &&
    draggableTop <= y2 && draggableBottom >= y1
  ));

  return fixture;
};

export const getFixture = (
  fixtureSpaces: FixtureSpace[],
  draggableLeft: number,
  draggableRight: number,
  draggableTop: number,
  draggableBottom: number,
) => {
  const [fixture] = fixtureSpaces.filter(({ x1, x2, y1, y2 }) => (
    draggableLeft <= x2 && draggableRight >= x1 &&
    draggableTop <= y2 && draggableBottom >= y1
  ));

  return fixture;
};

export const getFixtureFlatbar = (
  fixturesFlatbar: FixtureSpace[],
  x1: number,
  x2: number,
  y1: number,
  y2: number,
) => {
  const [fixture] = fixturesFlatbar.filter(item => {
    const left = Math.max(x1, item.x1);
    const bottom = Math.max(y1, item.y1);
    const right = Math.min(x2, item.x2);
    const top = Math.min(y2, item.y2);

    return right > left && top > bottom;
  }).sort((a, b) => a.x1 - b.x1);

  return fixture;
};

export const getFixtureCrossbar = (
  fixturesCrossbar: FixtureSpace[],
  x1: number,
  x2: number,
  y1: number,
  y2: number,
) => {
  const [fixture] = fixturesCrossbar.filter(item => {
    const left = Math.max(x1, item.x1);
    const bottom = Math.max(y1, item.y1);
    const right = Math.min(x2, item.x2);
    const top = Math.min(y2, item.y2);

    return right > left && top > bottom;
  }).sort((a, b) => a.x1 - b.x1);

  return fixture;
};

export const getFixturePegboard = (
  fixturesPegboard: FixtureSpace[],
  x1: number,
  x2: number,
  y1: number,
  y2: number,
) => {
  const [fixture] = fixturesPegboard.filter(item => {
    const left = Math.max(x1, item.x1);
    const bottom = Math.max(y1, item.y1);
    const right = Math.min(x2, item.x2);
    const top = Math.min(y2, item.y2);

    return right > left && top > bottom;
  }).sort((a, b) => a.x1 - b.x1);

  return fixture;
};

export const formatSkuNumber = (sku: string) => sku.replace(/(\d{3})(\d)/, '$1-$2');

export const getRegExpForClipboardSearching = (value: string) => new RegExp(escapeRegExp(value), 'gi');

export const isClipboardItem = (type: string): boolean => Object.values(DraggableSkuClipboardItems).includes(type);
export const isWorkbenchItem = (type: string): boolean => Object.values(DraggableWorkbenchItems).includes(type);

export const processRemovingFacing = (
  facing: FacingGroupExtended,
  fixturesCrossbar: FixtureSpace[],
  fixturePegboard: FixtureSpace[],
  fixtureFlatbar: FixtureSpace[],
  fixtureSpaces: FixtureSpace[],
  selectedFacing: number | null,
) => {
  const facings: Array<FacingGroupExtended> = [];
  const {
    horizontal_facings: horizontalFacings,
    vertical_facings: verticalFacings,
    capped_vertical_facings: cappedVertical,
    // capped_orientation_code: cappedOrientationCode,
    cap_nested: capNested,
  } = facing;

  if (horizontalFacings > 1 && selectedFacing !== null) {
    const facingHeight = getFacingHeight(
      facing.packaging.height,
      verticalFacings,
      capNested as Nesting,
      facing.packaging.nested_height,
      facing.cappedPackaging.height,
      cappedVertical,
    );
    const isCrossbarFacing = facing.fixtureType === FIXTURE_TYPES.CROSSBAR;
    const isPegboardFacing = facing.fixtureType === FIXTURE_TYPES.PEGBOARD;
    const isFlatbarFacing = facing.fixtureType === FIXTURE_TYPES.FLATBAR;
    const isDefaultFacing = !isCrossbarFacing && !isPegboardFacing;
    const isFirst = selectedFacing === 0;
    const isLast = selectedFacing === horizontalFacings - 1;
    const isMiddle = !isFirst && !isLast;

    if (isFirst) {
      const { x1, x2, y1, y2 } = makeRect(
        facing.x + facing.packaging.width,
        facing.y,
        facing.packaging.width * (horizontalFacings - 1),
        facingHeight
      );
      let baseFixture = null;

      if (isCrossbarFacing) {
        baseFixture = getFixtureCrossbar(fixturesCrossbar, x1, x2, y1, y2);
      }

      if (isFlatbarFacing) {
        baseFixture = getFixtureFlatbar(fixtureFlatbar, x1, x2, y1, y2);
      }

      if (isPegboardFacing) {
        baseFixture = getFixturePegboard(fixturePegboard, x1, x2, y1, y2);
      }

      if (isDefaultFacing) {
        baseFixture = getFixture(fixtureSpaces, x1, x2, y1, y2);
      }

      if (baseFixture) {
        const newFacing: FacingGroupExtended = {
          ...facing,
          id: generateUniqueId(),
          segment: baseFixture?.segment,
          located_fixture_id: baseFixture?.id,
          horizontal_facings: horizontalFacings - 1,
          x: facing.x + facing.packaging.width,
        };

        facings.push(newFacing);
      }
    }

    if (isLast) {
      const { x1, x2, y1, y2 } = makeRect(
        facing.x,
        facing.y,
        facing.packaging.width * (horizontalFacings - 1),
        facingHeight
      );
      let baseFixture = null;

      if (isCrossbarFacing) {
        baseFixture = getFixtureCrossbar(fixturesCrossbar, x1, x2, y1, y2);
      }

      if (isFlatbarFacing) {
        baseFixture = getFixtureFlatbar(fixtureFlatbar, x1, x2, y1, y2);
      }

      if (isPegboardFacing) {
        baseFixture = getFixturePegboard(fixturePegboard, x1, x2, y1, y2);
      }

      if (isDefaultFacing) {
        baseFixture = getFixture(fixtureSpaces, x1, x2, y1, y2);
      }

      if (baseFixture) {
        const newFacing: FacingGroupExtended = {
          ...facing,
          id: generateUniqueId(),
          segment: baseFixture?.segment,
          located_fixture_id: baseFixture?.id,
          horizontal_facings: horizontalFacings - 1,
        };

        facings.push(newFacing);
      }
    }

    if (isMiddle) {
      const rectFirst = makeRect(
        facing.x,
        facing.y,
        facing.packaging.width * selectedFacing,
        facingHeight
      );

      let baseFixtureFirst = null;

      if (isCrossbarFacing) {
        baseFixtureFirst = getFixtureCrossbar(fixturesCrossbar, rectFirst.x1, rectFirst.x2, rectFirst.y1, rectFirst.y2);
      }

      if (isFlatbarFacing) {
        baseFixtureFirst = getFixtureFlatbar(fixtureFlatbar, rectFirst.x1, rectFirst.x2, rectFirst.y1, rectFirst.y2);
      }

      if (isPegboardFacing) {
        baseFixtureFirst = getFixturePegboard(fixturePegboard, rectFirst.x1, rectFirst.x2, rectFirst.y1, rectFirst.y2);
      }

      if (isDefaultFacing) {
        baseFixtureFirst = getFixture(fixtureSpaces, rectFirst.x1, rectFirst.x2, rectFirst.y1, rectFirst.y2);
      }

      if (baseFixtureFirst) {
        const newFacingFirst: FacingGroupExtended = {
          ...facing,
          id: generateUniqueId(),
          segment: baseFixtureFirst?.segment,
          located_fixture_id: baseFixtureFirst?.id,
          horizontal_facings: selectedFacing,
        };

        facings.push(newFacingFirst);
      }

      const rectSecond = makeRect(
        facing.x + facing.packaging.width * (selectedFacing + 1),
        facing.y,
        facing.packaging.width * (horizontalFacings - (selectedFacing + 1)),
        facingHeight
      );

      let baseFixtureSecond = null;

      if (isCrossbarFacing) {
        baseFixtureSecond = getFixtureCrossbar(fixturesCrossbar, rectSecond.x1, rectSecond.x2, rectSecond.y1, rectSecond.y2);
      }

      if (isFlatbarFacing) {
        baseFixtureFirst = getFixtureFlatbar(fixtureFlatbar, rectSecond.x1, rectSecond.x2, rectSecond.y1, rectSecond.y2);
      }

      if (isPegboardFacing) {
        baseFixtureSecond = getFixturePegboard(fixturePegboard, rectSecond.x1, rectSecond.x2, rectSecond.y1, rectSecond.y2);
      }

      if (isDefaultFacing) {
        baseFixtureSecond = getFixture(fixtureSpaces, rectSecond.x1, rectSecond.x2, rectSecond.y1, rectSecond.y2);
      }

      if (baseFixtureSecond) {
        const newFacingSecond: FacingGroupExtended = {
          ...facing,
          id: generateUniqueId(),
          segment: baseFixtureSecond?.segment,
          located_fixture_id: baseFixtureSecond?.id,
          horizontal_facings: horizontalFacings - (selectedFacing + 1),
          x: facing.x + facing.packaging.width * (selectedFacing + 1)
        };

        facings.push(newFacingSecond);
      }
    }

    return facings;
  }

  return facings;
};

export const getFixturesMinWhiteSpaceHeight = (
  maxFacingHeightFixturesById: Record<string, number>,
  fixtureSpaces: Array<FixtureSpace>,
  locatedFixturesIds: Array<number>,
  locatedFixtureIdToFacingIds: Record<string, Array<string>>,
  facingGroupsByIds: Record<string, FacingGroup>,
  locatedFixtures: Array<LocatedFixture>
) => {
  let nextFixtureProducts: Array<string> = [];
  const fixturesHeight = locatedFixturesIds.map((id) => {
    const nextFixture = locatedFixtureIdToFacingIds[id + 1] || [];

    if (nextFixture.length) nextFixtureProducts = [...nextFixtureProducts, ...nextFixture];
    const fixtureHeight = maxFacingHeightFixturesById[id];

    if (fixtureHeight === 0) {
      const fixtureSpace = fixtureSpaces.filter(fS => fS.id === id);

      if (fixtureSpace) {
        const { y1, y2 } = fixtureSpace[0];

        return y2 - y1;
      }
    }

    return fixtureHeight;
  });
  const actualFixtureHeight = Math.min(...fixturesHeight);
  const alertSegments = locatedFixturesIds.reduce((acc, id) => {
    const fixture = locatedFixtures.filter(f => f.id === id);

    if (fixture.length) {
      const { segment } = fixture[0];

      if (fixture.length && !acc.includes(segment)) {
        acc.push(segment);
      }
    }

    return acc;
  }, [] as Array<number>);
  const pegboardProducts = alertSegments.reduce((acc, segment) => {
    const pegboardFixture = locatedFixtures.filter(f => f.segment === segment && f.type === 'PEGBOARD');

    if (pegboardFixture.length) {
      const { id } = pegboardFixture[0];

      if (locatedFixtureIdToFacingIds[id]) {
        acc = [...acc, ...locatedFixtureIdToFacingIds[id]];
      }
    }

    return acc;
  }, [] as Array<string>);
  const overlappingPegboardProducts = pegboardProducts.reduce((acc, facingId) => {
    if (facingGroupsByIds[facingId]) {
      const facing = facingGroupsByIds[facingId];
      const fixtureType = facing.fixtureType;

      if (fixtureType === 'PEGBOARD') {
        acc.push(facing.y);
      }
    }

    return acc;
  }, [] as Array<number>);
  const lowerProductYInPegboardFixture = overlappingPegboardProducts.length ? Math.min(...overlappingPegboardProducts) : 0;
  const overlappingProducts = nextFixtureProducts.reduce((acc, facingId) => {
    if (facingGroupsByIds[facingId]) {
      const facing = facingGroupsByIds[facingId];

      acc.push(facing.y);
    }

    return acc;
  }, [] as Array<number>);
  const lowerProductYInFixture = overlappingProducts.length ? Math.min(...overlappingProducts) : 0;

  return {
    height: actualFixtureHeight,
    crossbarOverlappingY: lowerProductYInFixture,
    pegboardOverlappingY: lowerProductYInPegboardFixture
  };
};

export const getIntersection = (
  draggableRect: DraggableRect,
  fixture: FixtureSpace[],
): boolean => {
  const {
    draggableLeft,
    draggableBottom,
    draggableRight,
    draggableTop,
  } = draggableRect;

  const intersection = fixture.some(element => {
    const left = Math.max(draggableLeft, element.x1);
    const bottom = Math.max(draggableBottom, element.y1);
    const right = Math.min(draggableRight, element.x2);
    const top = Math.min(draggableTop, element.y2);

    return right > left && top > bottom;
  });

  return intersection;
};

export const getDropFixtureType = (
  isSelectedPeggable: boolean,
  draggableRect: DraggableRect,
  fixtureCrossbar: FixtureSpace[],
  fixturePegboard: FixtureSpace[],
  fixtureBasket: FixtureSpace[],
  fixtureFlatbar: FixtureSpace[],
  fixtureDefaultShelf: FixtureSpace[],
): FixtureType => {
  const basketIntersaction = getIntersection(draggableRect, fixtureBasket);

  if (basketIntersaction) return FIXTURE_TYPES.BASKET;

  const crossbarIntersaction = isSelectedPeggable && getIntersection(draggableRect, fixtureCrossbar);

  if (crossbarIntersaction) return FIXTURE_TYPES.CROSSBAR;

  const flatbarIntersaction = isSelectedPeggable && getIntersection(draggableRect, fixtureFlatbar);

  if (flatbarIntersaction) return FIXTURE_TYPES.FLATBAR;

  const defaultIntersaction = isSelectedPeggable && getIntersection(draggableRect, fixtureDefaultShelf);

  if (defaultIntersaction) return FIXTURE_TYPES.SHELF;

  const pegboardIntersaction = isSelectedPeggable && getIntersection(draggableRect, fixturePegboard);

  if (pegboardIntersaction) return FIXTURE_TYPES.PEGBOARD;

  return FIXTURE_TYPES.SHELF;
};

export const transformPegholes = (peghole: Peghole, facingWidth: number, scale: number): TransformPeghole => {
  const width = Math.min(3, Math.ceil(facingWidth) - 1);
  const height = width === 3 ? 1.4 : 1;
  const centerX = peghole.peghole_x;
  const centerY = peghole.peghole_y;
  const x = centerX - width / 2;
  const y = centerY - height / 2;

  return {
    x: x * scale,
    y: y * scale,
    width: width * scale,
    height: height * scale,
    centerX: centerX * scale,
    centerY: centerY * scale,
  };
};

export const adjacentHeightToShelfSlopeAngle = (shelfSlope: number, depth: number) => {
  return Math.tan(Math.abs(shelfSlope) * (Math.PI / 180)) * depth;
};

export const yToShelfSlopeOffset = (y: number, shelfSlope: number, depth: number) => {
  return y - adjacentHeightToShelfSlopeAngle(shelfSlope, depth);
};

export const yToOriginShelfSlopeOffset = (y: number, shelfSlope: number, depth: number) => {
  return y + adjacentHeightToShelfSlopeAngle(shelfSlope, depth);
};

export const getDragItemsPack = (
  selectedFacingEntities: Record<string, Array<number>>,
  facingGroupsByIds: Record<string, FacingGroupExtended>,
  draggableItem: FacingPack
) => {
  return Object.keys(selectedFacingEntities).reduce((acc, selectedId) => {
    if (selectedId === draggableItem.id) {
      return acc;
    }

    const {
      id,
      horizontal_facings: h,
      x,
      y,
      vertical_facings: v,
      capped_vertical_facings: cV,
      cap_nested: cN,
      cappedPackaging: {
        height: cH
      },
      packaging: {
        width: rW,
        height: rH,
        nested_height: nH,
        pegholes
      },
    } = facingGroupsByIds[selectedId];
    const height = getFacingHeight(rH, v, cN, nH, cH, cV);
    const facings = selectedFacingEntities[id];
    const width = rW * ((facings.length === 0 || facings.length === h) ? h : selectedFacingEntities[id].length);
    const pegholeY = Array.isArray(pegholes) && pegholes.length > 0 ? pegholes[0].peghole_y : 0;
    const peghole = Array.isArray(pegholes) ? pegholes[0] : undefined;

    acc.push({ id, height, width, x, y, pegholeY, pegOffsetY: 0, pegholes: peghole });

    return acc;
  }, [] as Array<FacingPack>);
};

export const getGroupedFacingsParams = (
  packedFacings: Array<FacingPack>,
  draggableItem: FacingPack,
  singleFacingOffset: number,
  scale: number,
  fixtureType: string | FixtureType = ''
) => {
  const { height, x, pegOffsetY, width, id } = draggableItem;
  const isPegboard = fixtureType === FIXTURE_TYPES.PEGBOARD;
  const draggableIndexInPackedFacings = packedFacings.findIndex(f => f.id === id);
  const { xmax, xmin, hmax, groupedWidth, clipboardOffset } = packedFacings.reduce((acc, val, i) => {
    acc.xmax = val.x < acc.xmax ? acc.xmax : val.x;
    acc.xmin = val.x > acc.xmin ? acc.xmin : val.x;
    acc.hmax = val.height < acc.hmax ? acc.hmax : val.height;
    acc.groupedWidth = val.width + acc.groupedWidth;

    if (i >= draggableIndexInPackedFacings) {
      acc.clipboardOffset = val.width + acc.clipboardOffset + HORIZONTAL_SPACING;
    }

    return acc;
  }, {
    xmax: packedFacings[0].x,
    xmin: packedFacings[0].x,
    hmax: packedFacings[0].height,
    groupedWidth: 0,
    clipboardOffset: 0
  });

  const currentTopOffsetScaled = isPegboard ? 0 : (hmax - height + pegOffsetY) * scale;
  const currentRightOffsetScaled = isPegboard ? 0 : (x - singleFacingOffset - xmax) * scale;
  const currentLeftOffsetScaled = isPegboard ? 0 : (xmin - x + singleFacingOffset) * scale;
  const groupedItemsNumber = packedFacings.length;
  const horizontalSpacing = groupedItemsNumber > 1
    ? groupedItemsNumber * HORIZONTAL_SPACING
    : 0;
  const xmaxScaled = xmax * scale;
  const hmaxScaled = hmax * scale;
  const totalGroupedWidthScaled = scale * (groupedWidth + horizontalSpacing);
  const lastItem = packedFacings[packedFacings.length - 1];
  const lastItemWidthScaled = lastItem.width * scale;
  const clipboardOffsetRight = (clipboardOffset - lastItem.width - (width / 2) - HORIZONTAL_SPACING) * scale;

  const clipboardOffsetTop = (hmax - (height / 2)) * scale;

  return {
    xmax: xmaxScaled,
    hmax: hmaxScaled,
    groupedWidth: totalGroupedWidthScaled,
    draggableHeight: height,
    draggableWidth: width * scale,
    lastItemWidthScaled,
    currentRightOffset: currentRightOffsetScaled,
    currentTopOffset: currentTopOffsetScaled,
    currentLeftOffset: currentLeftOffsetScaled,
    clipboardOffsetRight,
    clipboardOffsetTop,
    clipboardOffset
  };
};

export const processRearangingMultipleItems = (
  packedFacings: Array<FacingPack>,
  draggableItem: FacingPack,
  selectedFixtureType: FixtureType | string = '',
  selectedDistrictType: string = ''
) => {
  const { x, y, pegholeY, height: draggableItemHeight } = draggableItem;

  switch (selectedFixtureType) {
    case FIXTURE_TYPES.PEGBOARD: {
      if (selectedDistrictType === DISTRICT_TYPES.CLIPBOARD) {
        const yPeg = draggableItemHeight + y - pegholeY;
        const yNormalizationFacings = packedFacings.map(f => {
          const yn = f.height + f.y - f.pegholeY;
          const newItem = f.y > y
            ? ({ ...f, y: yPeg - yn })
            : ({ ...f, y: yn - yPeg });

          return newItem;
        });

        const [leftHalfRemainingItems, rightHalfRemainingItems] = partition(
          yNormalizationFacings,
          facing => facing.x < x
        );

        const sortedLeft = orderBy(leftHalfRemainingItems, ['y', 'x'], ['desc', 'desc']);
        const sortedRight = orderBy(rightHalfRemainingItems, ['y', 'x'], ['desc', 'asc']);
        const rearangeLeftHalfItems = sortedLeft.reduce((acc, val, index) => {
          const xPosition = (index === 0 ? x : acc[index - 1].x) - HORIZONTAL_SPACING - val.width;
          // const yBottomPos = yPeg - val.height + val.pegholeY;
          const pegOffsetY = 0;

          acc.push({ ...val, x: xPosition, pegOffsetY });

          return acc;
        }, [] as Array<FacingPack>);

        const rearangeRightHalfItems = sortedRight.reduce((acc, val, index) => {
          const xPosition = (index === 0 ? x + draggableItem.width : acc[index - 1].x + acc[index - 1].width) + HORIZONTAL_SPACING;
          // const yBottomPos = yPeg - val.height + val.pegholeY;
          const pegOffsetY = 0;

          acc.push({ ...val, x: xPosition, pegOffsetY });

          return acc;
        }, [] as Array<FacingPack>);

        const result = [
          ...rearangeLeftHalfItems,
          draggableItem,
          ...rearangeRightHalfItems
        ].sort((a, b) => a.x - b.x);

        return result;
      }

      const rearangedItems = [...packedFacings, draggableItem].reduce((acc, val) => {
        const xPosition = val.x - draggableItem.x;
        const yBottomPos = draggableItem.y + draggableItem.height - val.y - val.height;

        acc.push({ ...val, x: xPosition, y: yBottomPos, pegOffsetY: 0 });

        return acc;
      }, [] as Array<FacingPack>);

      return rearangedItems;
    }

    case FIXTURE_TYPES.CROSSBAR:
    case FIXTURE_TYPES.FLATBAR: {
      const yPeg = draggableItemHeight + y - pegholeY;
      const yNormalizationFacings = packedFacings.map(f => {
        const yn = f.height + f.y - f.pegholeY;
        const newItem = f.y > y
          ? ({ ...f, y: yPeg - yn })
          : ({ ...f, y: yn - yPeg });

        return newItem;
      });

      const [leftHalfRemainingItems, rightHalfRemainingItems] = partition(
        yNormalizationFacings,
        facing => facing.x < x
      );

      const sortedLeft = orderBy(leftHalfRemainingItems, ['y', 'x'], ['desc', 'desc']);
      const sortedRight = orderBy(rightHalfRemainingItems, ['y', 'x'], ['desc', 'asc']);
      const rearangeLeftHalfItems = sortedLeft.reduce((acc, val, index) => {
        const xPosition = (index === 0 ? x : acc[index - 1].x) - HORIZONTAL_SPACING - val.width;
        const yBottomPos = yPeg - val.height + val.pegholeY;
        const pegOffsetY = y - yBottomPos;

        acc.push({ ...val, x: xPosition, pegOffsetY });

        return acc;
      }, [] as Array<FacingPack>);

      const rearangeRightHalfItems = sortedRight.reduce((acc, val, index) => {
        const xPosition = (index === 0 ? x + draggableItem.width : acc[index - 1].x + acc[index - 1].width) + HORIZONTAL_SPACING;
        const yBottomPos = yPeg - val.height + val.pegholeY;
        const pegOffsetY = y - yBottomPos;

        acc.push({ ...val, x: xPosition, pegOffsetY });

        return acc;
      }, [] as Array<FacingPack>);

      const result = [
        ...rearangeLeftHalfItems,
        draggableItem,
        ...rearangeRightHalfItems
      ].sort((a, b) => a.x - b.x);

      return result;
    }

    default: {
      const yNormalizationFacings = packedFacings.map(item => item.y > y
        ? ({ ...item, y: y - item.y })
        : ({ ...item, y: item.y - y })
      );
      const [leftHalfRemainingItems, rightHalfRemainingItems] = partition(
        yNormalizationFacings,
        facing => facing.x < x
      );

      const sortedLeft = orderBy(leftHalfRemainingItems, ['y', 'x'], ['desc', 'desc']);
      const sortedRight = orderBy(rightHalfRemainingItems, ['y', 'x'], ['desc', 'asc']);
      const rearangeLeftHalfItems = sortedLeft.reduce((acc, val, index) => {
        const xPosition = (index === 0 ? x : acc[index - 1].x) - HORIZONTAL_SPACING - val.width;

        acc.push({ ...val, x: xPosition, y });

        return acc;
      }, [] as Array<FacingPack>);

      const rearangeRightHalfItems = sortedRight.reduce((acc, val, index) => {
        const xPos = (index === 0 ? x + draggableItem.width : acc[index - 1].x + acc[index - 1].width) + HORIZONTAL_SPACING;

        acc.push({ ...val, x: xPos, y });

        return acc;
      }, [] as Array<FacingPack>);

      const result = [
        ...rearangeLeftHalfItems,
        draggableItem,
        ...rearangeRightHalfItems
      ].sort((a, b) => a.x - b.x);

      return result;
    }
  }
};

export const processRearangingMultipleClipboardItems = (
  packedFacings: Array<FacingPack>,
  draggableItem: FacingPack,
  clipboardIds: Array<string>,
  selectedFixtureType: FixtureType | string
) => {
  const { x, y, pegholeY, height: draggableItemHeight, id } = draggableItem;

  const arrangedFacings = packedFacings.sort((f1, f2) => clipboardIds.indexOf(f1.id) - clipboardIds.indexOf(f2.id));
  const [leftHalfRemainingItems, rightHalfRemainingItems] = partition(
    arrangedFacings,
    f => clipboardIds.indexOf(f.id) < clipboardIds.indexOf(id)
  );

  switch (selectedFixtureType) {
    case FIXTURE_TYPES.CROSSBAR:
    case FIXTURE_TYPES.PEGBOARD:
    case FIXTURE_TYPES.FLATBAR: {
      const yPeg = draggableItemHeight + y - pegholeY;
      const isPegboard = FIXTURE_TYPES.PEGBOARD === selectedFixtureType;
      const rearangeLeftHalfItems = leftHalfRemainingItems.reverse().reduce((acc, val, index) => {
        const xPosition = (index === 0 ? x : acc[index - 1].x) - HORIZONTAL_SPACING - val.width;
        const yBottomPos = yPeg - val.height + val.pegholeY;
        const pegOffsetY = isPegboard ? 0 : y - yBottomPos;

        acc.push({ ...val, x: xPosition, pegOffsetY });

        return acc;
      }, [] as Array<FacingPack>);

      const rearangeRightHalfItems = rightHalfRemainingItems.reduce((acc, val, index) => {
        const xPosition = (index === 0 ? x + draggableItem.width : acc[index - 1].x + acc[index - 1].width) + HORIZONTAL_SPACING;
        const yBottomPos = yPeg - val.height + val.pegholeY;
        const pegOffsetY = isPegboard ? 0 : y - yBottomPos;

        acc.push({ ...val, x: xPosition, pegOffsetY });

        return acc;
      }, [] as Array<FacingPack>);

      const result = [
        ...rearangeLeftHalfItems,
        draggableItem,
        ...rearangeRightHalfItems
      ].sort((a, b) => a.x - b.x);

      return result;
    }

    default: {
      const rearangeLeftHalfItems = leftHalfRemainingItems.reverse().reduce((acc, val, index) => {
        const xPosition = (index === 0 ? x : acc[index - 1].x) - HORIZONTAL_SPACING - val.width;

        acc.push({ ...val, x: xPosition, y });

        return acc;
      }, [] as Array<FacingPack>);

      const rearangeRightHalfItems = rightHalfRemainingItems.reduce((acc, val, index) => {
        const xPos = (index === 0 ? x + draggableItem.width : acc[index - 1].x + acc[index - 1].width) + HORIZONTAL_SPACING;

        acc.push({ ...val, x: xPos, y });

        return acc;
      }, [] as Array<FacingPack>);

      const result = [
        ...rearangeLeftHalfItems,
        draggableItem,
        ...rearangeRightHalfItems
      ].sort((a, b) => a.x - b.x);

      return result;
    }
  }
};

export const makeDraggableItem = (
  selectedFacingEntities: Record<string, Array<number>>,
  facing: FacingGroupExtended,
  offsetX: number
) => {
  const {
    id,
    x,
    y,
    vertical_facings: v,
    horizontal_facings: h,
    cap_nested: cN,
    capped_vertical_facings: cV,
    packaging: {
      width: rW,
      height: rH,
      nested_height: nH,
      pegholes
    },
    cappedPackaging: {
      height: cH
    }
  } = facing;
  const height = getFacingHeight(rH, v, cN, nH, cH, cV);
  const facings = selectedFacingEntities[id];
  const facingsNumber = facings.length;
  const isGroupSelected = facingsNumber === 0 || facingsNumber === h;
  const pegholeY = Array.isArray(pegholes) && pegholes.length ? pegholes[0].peghole_y : 0;

  return {
    id,
    y,
    height,
    pegholeY,
    pegOffsetY: 0,
    ...(isGroupSelected ? {
      width: rW * h,
      x
    } : {
      width: rW * facingsNumber,
      x: x + offsetX
    })
  };
};

export const getOffset = (
  selectedFacings: Array<number>,
  facing: FacingGroupExtended,
  currentOffset: CoordinatePoint,
  currentCursorPosition: CoordinatePoint,
  viewScale: number
) => {
  const {
    horizontal_facings: h,
    packaging: {
      width,
    }
  } = facing;
  const cursorPosition = currentCursorPosition.x;
  const offset = currentOffset.x;
  let offsetX = 0;
  let draggableSingleFacing = null;

  if (h > 1 && (h !== selectedFacings.length && selectedFacings.length !== 0)) {
    draggableSingleFacing = Math.floor((cursorPosition - offset) / (width * viewScale));
    const indexOfSelectedFixture = selectedFacings.indexOf(draggableSingleFacing);
    const firstHalf = selectedFacings.slice(0, indexOfSelectedFixture);

    offsetX = width * (draggableSingleFacing - firstHalf.length);
  }

  return offsetX;
};

export const getClipboardItemPosition = (
  clientOffset: XYCoord,
  sourceClientOffset: XYCoord,
  currentRightOffset: number = 0,
  currentTopOffset: number = 0,
) => {
  const topClipboardItemShift = clientOffset.y - sourceClientOffset.y + currentTopOffset - CLIPBOARD_TOP_OFFSET;
  const leftClipboardItemShift = clientOffset.x - sourceClientOffset.x + currentRightOffset - CLIPBOARD_HORIZONTAL_OFFSET;
  const leftBoardItemShift = clientOffset.x - sourceClientOffset.x;
  const topBoardItemShift = clientOffset.y - sourceClientOffset.y;

  return { topClipboardItemShift, leftClipboardItemShift, leftBoardItemShift, topBoardItemShift };
};

export const getItemStyles = (currentOffset: CoordinatePoint | null): CSSProperties => {
  if (!currentOffset) {
    return {
      display: 'none',
    };
  }

  const { x, y } = currentOffset;
  const transform = `translate(${x}px, ${y}px)`;

  return {
    transform,
    WebkitTransform: transform
  };
};

export const groupBySingleFacings = (
  facings: number[]
) => facings.reduce((acc, val) => {
  const previousGroup = acc[acc.length - 1];

  if (previousGroup && previousGroup[previousGroup.length - 1] + 1 === val) {
    acc[acc.length - 1].push(val);
  } else {
    acc[acc.length] = [val];
  }

  return acc;
}, [] as Array<Array<number>>);

export const getNewFacingsByGroup = (h: number, selectedFacings: number[]) => {
  const allPossibleFacings = generateFacingsInRange(0, h);
  const newFacings = allPossibleFacings.filter(v => !selectedFacings.includes(v));
  const newGroupedFacings = groupBySingleFacings(newFacings);

  return newGroupedFacings;
};

export const getActiveFacingsFromFacingGroup = (
  activeFacingGroupIds: string[],
  selectedFacingsGroup: Record<string, Array<number>>,
  facingsByIds: Record<string, FacingGroupExtended>,
  draggableLeft: number,
  draggableRight: number,
  isCopy: boolean = false
) => {
  return activeFacingGroupIds.reduce((acc, val) => {
    const {
      x,
      horizontal_facings: h,
      packaging: {
        width
      }
    } = facingsByIds[val];

    if (h > 1) {
      const xStart = x;
      const xEnd = xStart + h * width;

      if (
        draggableLeft < xStart &&
        draggableRight < xEnd
      ) {
        const xOffset = draggableRight - xStart;
        const hEnd = Math.ceil(xOffset / width);
        const touchingFacings = generateFacingsInRange(0, hEnd, 1);

        acc = { ...acc, [val]: touchingFacings };
      } else if (
        draggableLeft >= xStart &&
        draggableRight >= xEnd
      ) {
        const hEnd = Math.ceil((xEnd - draggableLeft) / width);
        const touchingFacings = generateFacingsInRange(h - hEnd, h, 1);

        acc = { ...acc, [val]: touchingFacings };
      } else if (
        draggableLeft >= xStart &&
        draggableRight <= xEnd
      ) {
        const hStart = Math.floor((draggableLeft - xStart) / width);
        const hEnd = Math.ceil((draggableRight - xStart) / width);
        const touchingFacings = generateFacingsInRange(hStart, hEnd, 1);

        acc = { ...acc, [val]: touchingFacings };
      } else if (
        draggableLeft <= xStart &&
        draggableRight >= xEnd
      ) {
        const touchingFacings = generateFacingsInRange(0, h, 1);

        acc = { ...acc, [val]: touchingFacings };
      }
    } else {
      acc = { ...acc, [val]: [] };
    }

    const selectedFacings = isCopy ? [] : selectedFacingsGroup[val];

    if (selectedFacings && selectedFacings.length > 0) {
      const allFacings = Array.from(new Set([...acc[val].concat(selectedFacings)])).sort((b, a) => b - a);

      acc = { ...acc, [val]: allFacings };
    }

    return acc;
  }, {} as Record<string, Array<number>>);
};

export const toggleClipboardStatus = (
  productNums: string[],
  warningsData: WarningsData,
  status: boolean
) => {
  const updatedWarningsData = warningsData?.map((warningData) => {
    if (excludedProductAlertTypes.includes(warningData.warningType)) {
      warningData.data = warningData.data.map((excludedProductWarning: any) => {
        if (productNums.includes(excludedProductWarning.product_num)) {
          excludedProductWarning.in_clipboard = status;
        }

        return excludedProductWarning as ExcludedProductWarnings;
      });
    }

    return warningData;
  });

  return updatedWarningsData;
};

export const filterOccupiedCrossbarFixtures = (locatedFixtures: Array<FixtureSpace> = []) => {
  const groupedBySegments: Record<number, FixtureSpace> = locatedFixtures.reduce((acc: Record<number, FixtureSpace>, lf: FixtureSpace) => ({
    ...acc,
    [lf.segment]: acc[lf.segment] && acc[lf.segment].y2 > lf.y2 ? acc[lf.segment] : lf,
  }), {} as Record<number, FixtureSpace>);

  return Object.values(groupedBySegments);
};

export const convertFacingsToItemCoordinates = (facings: Array<FacingGroup>, eslEnabled: boolean) => {
  const itemCoordinates = facings?.map((item) => {
    const height = getFacingHeight(
      item.packaging.height,
      item.vertical_facings,
        item.cap_nested as Nesting,
        item.packaging.nested_height,
        item.cappedPackaging.height,
        item.capped_vertical_facings
    );
    const isPeggableFixtureType = isPeggableFixture(item.fixtureType);
    const isSpecialty = item.fixtureType === FIXTURE_TYPES.SPECIALTY;
    const peggable = item.packaging.peggable;
    const isPeg =
        peggable &&
        item.packaging.pegholes.length > 0 &&
        item.packaging.peggable &&
        isPeggableFixtureType;
    const isESL = isPeg && eslEnabled;
    const eslHeightOutsideFacing = isESL ? EXTRA_ESL_HEIGHT_OUTSIDE_FACING : 0;

    return {
      id: item.id,
      x1: round(item.x, 2),
      y1: round(item.y, 2),
      x2: round(item.x + item.packaging.width * item.horizontal_facings, 2),
      y2: round(item.y + height + eslHeightOutsideFacing, 2),
      isFacingGroup: item.horizontal_facings > 1,
      isCrossbar: Boolean(item.packaging.pegholes),
      peggable,
      isPeg,
      isSpecialty
    } as ItemCoordinates;
  });

  return itemCoordinates;
};

export const updateFacingsDepth = (
  facings: Array<FacingGroup>,
  locatedFixturesById: Record<string, LocatedFixture>,
  fixturesById: FixtureById,
) => {
  const newFacings = facings.reduce((acc, facing) => {
    const { located_fixture_id, packaging: { depth, peggable }, peg_id } = facing;
    const locatedFixture = locatedFixturesById[located_fixture_id];

    if (!locatedFixture) {
      acc.push(facing);

      return acc;
    }

    const { depth: fixtureDepth, type } = locatedFixture;
    const isPegableFixture = ['FLATBAR', 'CROSSBAR', 'PEGBOARD'].some(t => t === type);
    const isOverhangAllowed = type === FIXTURE_TYPES.SHELF && fixtureDepth < depth;
    let compareDepth = fixtureDepth + (isOverhangAllowed ? 1 : 0);

    if (isPegableFixture && peggable && peg_id) {
      const fixture = fixturesById[peg_id];

      if (fixture) {
        const { fixture_depth } = fixture;

        if (fixture_depth && fixture_depth > 0) {
          compareDepth = fixture_depth;
        }
      }
    }

    const newDepth = Math.trunc(compareDepth / depth);

    acc.push({ ...facing, depth_facings: newDepth });

    return acc;
  }, [] as Array<FacingGroup>);

  return newFacings;
};

export const processingMoveEvent = (
  moveableBoundary: { left: number; right: number; top: number; },
  direction: Direction,
  selectedFacingsGroup: Record<string, Array<number>>,
  scale: number,
  locatedFixturesIdToType: Record<string, FixtureType>,
  fixtureCrossbar: Array<FixtureSpace>,
  fixtureFlatbar: Array<FixtureSpace>,
  fixturePegboard: Array<FixtureSpace>,
  fixtureBasket: Array<FixtureSpace>,
  itemsCoordinates: Array<ItemCoordinates>,
  facingGroupsByIds: Record<string, FacingGroupExtended>,
  fixtureSpaces: Array<FixtureSpace>,
  fixtureSpaceSelectorByTypes: Array<FixturePosition>,
  locatedFixturesById: Record<string, LocatedFixture>,
  locatedFixtures: Array<LocatedFixture>,
  selectedFixtureType: string | FixtureType,
  alerts: WarningsData,
  isEslPog: boolean,
  numberOfSegments: number,
  fixtureHeight: Array<FixtureHeight>,
  isKeyHold: boolean
) => {
  const selectedItems = Object.keys(selectedFacingsGroup);

  if (!selectedItems.length || !direction) {
    return false;
  }

  const newHistoryFacings: Array<FacingGroup> = [];
  const newHistoryRemovedIds: Array<string> = [];
  const reSelectFacings: Array<FacingGroup> = [];
  let newAlerts: WarningsData = alerts;

  const processMoveItem = (
    id: string,
    newHistoryFacings: Array<FacingGroup>,
    newHistoryRemovedIds: Array<string>
  ) => {
    let allowItemMove = true;
    const moveableItem = facingGroupsByIds[id];
    const facings = selectedFacingsGroup[id];
    const {
      x,
      y,
      product_num: product,
      packaging: {
        height,
        width,
        nested_height: nH,
        // peggable: isSelectedPeggable
      },
      fixtureType: sourceFixtureType,
      horizontal_facings: h,
      vertical_facings: v,
      cap_nested: capNested,
      capped_horizontal_facings: cappedHorFacing,
      cap_nested: cN,
      capped_vertical_facings: cV,
      cappedPackaging: {
        height: cH
      },
      orientation_code: oC,
      located_fixture_id: sourceFixtureId
    } = moveableItem;
    const {
      left: containerLeftPosition,
      right: containerRightPosition,
      top: containerTopPosition
    } = moveableBoundary;
    const containerRight = containerRightPosition / scale;
    const containerLeft = containerLeftPosition / scale;
    const containerTop = containerTopPosition / scale;
    const isYCapped = capNested === Nesting.YCapped;
    const isFacingGroupSelected = facings.length === 0 || facings.length === h;
    const newHorizontalFacings = isFacingGroupSelected ? h : facings.length;
    const facingHeight = getFacingHeight(height, v, cN, nH, cH, cV);
    const facingWidth = width * newHorizontalFacings;
    const rightBoundary = numberOfSegments * SEGMENT_WIDTH;
    const moveableItemRect = makeDraggableRectShift(x, y, facingWidth, facingHeight, rightBoundary);
    const { draggableTop } = moveableItemRect;
    const allowFixturesToMoveItem = [FIXTURE_TYPES.BASKET, FIXTURE_TYPES.SHELF, FIXTURE_TYPES.PEGBOARD];
    const isAllowFixturesToMoveItem = allowFixturesToMoveItem.includes(sourceFixtureType as FIXTURE_TYPES);
    const defaultDirections = ['left', 'right'] as Array<Direction>;
    const pegboardDirections = ['left', 'right', 'up', 'down'] as Array<Direction>;
    const isMovingHorizonal = ['left', 'right'].includes(direction);
    const isAllowDirectionForDefaultFixtureItem = (sourceFixtureType === FIXTURE_TYPES.BASKET || sourceFixtureType === FIXTURE_TYPES.SHELF) &&
      defaultDirections.includes(direction);
    const isAllowDirectionForPegboardFixtureItem = sourceFixtureType === FIXTURE_TYPES.PEGBOARD && pegboardDirections.includes(direction);

    if (!isAllowFixturesToMoveItem) allowItemMove = false;
    if (!isAllowDirectionForDefaultFixtureItem && !isAllowDirectionForPegboardFixtureItem) allowItemMove = false;

    const getMaxShiftValue = (d: Direction, isPegboard: boolean) => {
      const isShiftRight = d === 'right';
      const isShiftLeft = d === 'left';
      const isShiftUp = d === 'up';
      const isShiftDown = d === 'down';
      let selectedFacingWidth = facingWidth;
      let selectedFacingHeight = facingHeight;
      const pegboardGap = 1.25;
      const eslExtraHeight = isEslPog && isPegboard ? EXTRA_ESL_HEIGHT_OUTSIDE_FACING : 0;

      // For x1 and x2 position we need to consider the remaining facings as well
      let remainingFacingWidth = 0;

      if (!isFacingGroupSelected && isShiftRight) {
        const facingLeftOnPog = h - facings.length;

        remainingFacingWidth = facingLeftOnPog * width;
      }

      let pogBottomStartingY = 0;
      const baseFixture = locatedFixtures.filter(f => f.is_base_shelf);

      if (baseFixture.length) {
        const { y, height } = baseFixture[0];

        pogBottomStartingY = y + height;
      }

      // Calculate actual fixture item can move
      const {
        x1: actualX1,
        x2: actualX2,
        y1: actualY1,
        y2: actualY2
      } = makeRect(x, y, facingWidth, facingHeight);
      const actualFixture = isShiftRight ?
        getFixture(isPegboard ? fixturePegboard : fixtureSpaces, isPegboard ? actualX2 : actualX2 - 1, actualX2, actualY1, actualY2) :
        getFixture(isPegboard ? fixturePegboard : fixtureSpaces, actualX1, isPegboard ? actualX2 : actualX2 - 1, actualY1, actualY2);
      const allFixtures = isPegboard ?
        fixturePegboard.filter(fixture => {
          return fixture.y1 === actualFixture.y1 &&
            fixture.y2 >= actualFixture.y1 &&
            (isShiftRight ? fixture.x1 >= actualFixture.x2 + pegboardGap : fixture.x2 <= actualFixture.x1 - pegboardGap);
        }) :
        fixtureSpaces.filter(fixture => {
          const cF = locatedFixturesById[fixture.id];

          return cF.type === sourceFixtureType &&
            fixture.y1 === actualFixture.y1 &&
            fixture.y2 >= (actualFixture.y1 + facingHeight) &&
            (isShiftRight ? fixture.x1 >= actualFixture.x2 : fixture.x2 <= actualFixture.x1);
        });
      const allFixturesAllowMove = allFixtures.sort((firstItem, secondItem) => {
        if (isShiftRight) return firstItem.x1 - secondItem.x1;
        if (isShiftLeft) return secondItem.x2 - firstItem.x2;

        return 1;
      });

      const sourcePegboardHeight = locatedFixturesById[sourceFixtureId].y + locatedFixturesById[sourceFixtureId].height;
      let actualPoint = isShiftRight ?
        isPegboard ? actualFixture.x2 + pegboardGap : actualFixture.x2 :
        isPegboard ? actualFixture.x1 - pegboardGap : actualFixture.x1;

      for (const fixture of allFixturesAllowMove) {
        if (isShiftRight && actualPoint === fixture.x1) actualPoint = fixture.x2;
        if (isShiftLeft && actualPoint === fixture.x2) actualPoint = fixture.x1;
      }

      if (isShiftLeft) actualPoint = actualPoint + 1;
      if (isShiftRight) actualPoint = actualPoint - 1;

      if (isShiftRight) selectedFacingWidth = actualPoint - (x + remainingFacingWidth);
      if (isShiftLeft) selectedFacingWidth = (x + facingWidth) - actualPoint;
      if (isShiftUp) selectedFacingHeight = sourcePegboardHeight - y;
      if (isShiftDown) selectedFacingHeight = (y + facingHeight + eslExtraHeight) - pogBottomStartingY;

      const {
        x1: origX1,
        x2: origX2,
        y1: origY1,
        y2: origY2
      } = makeRect(
        isShiftLeft ? actualPoint : isShiftRight ? x + remainingFacingWidth : x,
        isShiftDown ? pogBottomStartingY : y,
        selectedFacingWidth,
        selectedFacingHeight
      );

      const occupiedShiftFixtures = fixtureHeight.filter(fixture => {
        const left = Math.max(origX1, fixture.x1);
        const bottom = Math.max(origY1, fixture.y1);
        const right = Math.min(origX2, fixture.x2);
        const top = Math.min(origY2, fixture.y2);

        return right > left && top > bottom && !fixture.isPegboard;
      }).sort((firstItem, secondItem) => {
        if (isShiftRight) return firstItem.x1 - secondItem.x1;
        if (isShiftLeft) return secondItem.x2 - firstItem.x2;
        if (isShiftUp) return firstItem.y1 - secondItem.y1;
        if (isShiftDown) return secondItem.y2 - firstItem.y2;

        return 1;
      });

      const itemsCoordinatesExcludingSelected = itemsCoordinates.filter(item => item.id !== id);
      const newHistotyFacingsCoordinates = convertFacingsToItemCoordinates(newHistoryFacings, isEslPog);
      const overlappingFacings = itemsCoordinatesExcludingSelected.concat(newHistotyFacingsCoordinates).filter((i) => {
        if (selectedItems.includes(i.id) && newHistoryRemovedIds.includes(i.id)) return false;

        const left = Math.max(origX1, i.x1);
        const bottom = Math.max(origY1, i.y1);
        const right = Math.min(origX2, i.x2);
        const top = Math.min(origY2, i.y2);

        return right > left && top > bottom;
      }).sort((firstItem, secondItem) => {
        if (isShiftRight) return firstItem.x1 - secondItem.x1;
        if (isShiftLeft) return secondItem.x2 - firstItem.x2;
        if (isShiftUp) return firstItem.y1 - secondItem.y1;
        if (isShiftDown) return secondItem.y2 - firstItem.y2;

        return 1;
      });

      const shiftRightValueWithoutOvelapping = isPegboard ?
        (actualPoint - facingWidth) - (x + remainingFacingWidth) :
        (actualPoint - facingWidth) - (x + remainingFacingWidth);
      const shiftLeftValueWithoutOvelapping = isPegboard ?
        (x + remainingFacingWidth) - 1 :
        (x + remainingFacingWidth) - actualPoint;
      const shiftUpValueWithoutOvelapping = (sourcePegboardHeight - (facingHeight + eslExtraHeight + 1)) - y;
      const shiftDownValueWithoutOvelapping = y - (pogBottomStartingY + 1);

      if (overlappingFacings.length || occupiedShiftFixtures.length) {
        let x1ToConsider: number = 0;
        let x2ToConsider: number = 0;
        let y1ToConsider: number = 0;
        let y2ToConsider: number = 0;

        if (overlappingFacings.length && !occupiedShiftFixtures.length) {
          x1ToConsider = overlappingFacings[0].x1;
          x2ToConsider = overlappingFacings[0].x2;
          y1ToConsider = overlappingFacings[0].y1;
          y2ToConsider = overlappingFacings[0].y2;
        }

        if (occupiedShiftFixtures.length && !overlappingFacings.length) {
          x1ToConsider = occupiedShiftFixtures[0].x1;
          x2ToConsider = occupiedShiftFixtures[0].x2;
          y1ToConsider = occupiedShiftFixtures[0].y1;
          y2ToConsider = occupiedShiftFixtures[0].y2;
        }

        if (occupiedShiftFixtures.length && overlappingFacings.length) {
          x1ToConsider = overlappingFacings[0].x1 < occupiedShiftFixtures[0].x1 ? overlappingFacings[0].x1 : occupiedShiftFixtures[0].x1;
          x2ToConsider = overlappingFacings[0].x2 > occupiedShiftFixtures[0].x2 ? overlappingFacings[0].x2 : occupiedShiftFixtures[0].x2;
          y1ToConsider = overlappingFacings[0].y1 < occupiedShiftFixtures[0].y1 ? overlappingFacings[0].y1 : occupiedShiftFixtures[0].y1;
          y2ToConsider = overlappingFacings[0].y2 > occupiedShiftFixtures[0].y2 ? overlappingFacings[0].y2 : occupiedShiftFixtures[0].y2;
        }

        const shiftRightValueWithOvelapping = isPegboard ?
          (x1ToConsider - facingWidth - 1) - (x + remainingFacingWidth) :
          (x1ToConsider - facingWidth - 1) - (x + remainingFacingWidth);
        const shiftLeftValueWithOvelapping = isPegboard ?
          (x + remainingFacingWidth) - x2ToConsider - 1 :
          (x + remainingFacingWidth) - x2ToConsider;
        const shiftUpValueWithOvelapping = ((y1ToConsider - 1) - (facingHeight + eslExtraHeight)) - y;
        const shiftDownValueWithOvelapping = (y + facingHeight + eslExtraHeight) - (y2ToConsider + (facingHeight + eslExtraHeight + 1));

        if (isShiftRight) return shiftRightValueWithOvelapping;
        if (isShiftLeft) return shiftLeftValueWithOvelapping;
        if (isShiftUp) return shiftUpValueWithOvelapping;
        if (isShiftDown) return shiftDownValueWithOvelapping;
      }

      if (isShiftRight) return shiftRightValueWithoutOvelapping;
      if (isShiftLeft) return shiftLeftValueWithoutOvelapping;
      if (isShiftUp) return shiftUpValueWithoutOvelapping;
      if (isShiftDown) return shiftDownValueWithoutOvelapping;

      return 0;
    };

    if (allowItemMove) {
      switch (sourceFixtureType) {
        case FIXTURE_TYPES.BASKET:
        case FIXTURE_TYPES.SHELF: {
          if (allowItemMove) {
            const shiftValue = isKeyHold ? getMaxShiftValue(direction, false) : ARROW_MOVE_VALUE;
            const xPosition = direction === 'left' ? x - shiftValue : x + shiftValue;
            const {
              draggableLeft: posX1,
              draggableRight: posX2,
              draggableBottom: posY1,
              draggableTop: posY2
            } = makeDraggableRect(xPosition, draggableTop + facingHeight, facingWidth, facingHeight);
            const baseFixture = getBaseFixture(fixtureSpaces, scale, posX1, posX2, posY1, posY2);
            const locatedFixture = locatedFixturesById[baseFixture.id];
            const { maxVerticalFacings, maxCappedVerticalFacings } = maxVerticalStack(moveableItem, locatedFixture);

            const newFacingGroup = {
              ...facingGroupsByIds[id],
              id: generateUniqueId(),
              x: xPosition,
              horizontal_facings: newHorizontalFacings,
              located_fixture_id: baseFixture?.id,
              fixtureType: locatedFixturesIdToType[baseFixture.id],
              segment: baseFixture?.segment,
              vertical_facings: maxVerticalFacings,
              capped_vertical_facings: maxCappedVerticalFacings,
              capped_horizontal_facings: isYCapped
                ? newHorizontalFacings
                : cappedHorFacing
            };

            // For x1 and x2 position we need to consider the remaining facings as well
            let remainingFacingWidth = 0;

            if (!isFacingGroupSelected && direction === 'right') {
              const facingLeftOnPog = h - facings.length;

              remainingFacingWidth = facingLeftOnPog * width;
            }

            const newItem = {
              id: newFacingGroup.id,
              x1: newFacingGroup.x + remainingFacingWidth,
              x2: newFacingGroup.x + remainingFacingWidth + facingWidth,
              y1: newFacingGroup.y,
              y2: newFacingGroup.y + facingHeight
            };
            const isItemOutsideMoveableLayer = newItem.x1 < containerLeft || newItem.x2 > containerRight || newItem.y2 > containerTop || newItem.y1 < 0;

            if (isItemOutsideMoveableLayer) allowItemMove = false;

            if (allowItemMove) {
              const {
                overlappingWithOthers
              } = itemsCoordinates.reduce((itemsCollection, i) => {
                const left = Math.max(newItem.x1, i.x1);
                const bottom = Math.max(newItem.y1, i.y1);
                const right = Math.min(newItem.x2, i.x2);
                const top = Math.min(newItem.y2, i.y2);
                const isOverlapping = right > left && top > bottom;

                if (isOverlapping) {
                  if (i.isPeg || i.isSpecialty) {
                    itemsCollection.overlappingWithOthers.push(i.id);
                  }
                }

                return itemsCollection;
              }, {
                overlappingWithOthers: [] as string[]
              });

              if (overlappingWithOthers.length) allowItemMove = false;

              if (allowItemMove) {
                const overlapFixture = fixtureSpaceSelectorByTypes.filter(item => {
                  const left = Math.max(newItem.x1, item.x1);
                  const bottom = Math.max(newItem.y1, item.y1);
                  const top = Math.min(newItem.y2, item.y2);
                  const right = Math.min(newItem.x2, item.x2);

                  return right > left && top > bottom;
                }).sort((a, b) => b.y2 - a.y2)[0];

                if (overlapFixture) allowItemMove = false;

                if (allowItemMove) {
                  const fixtures = fixtureSpaces.filter(({ x1, x2, y1, y2 }) => (
                    newItem.x1 <= x2 && newItem.x2 >= x1 && newItem.y2 <= y2 && newItem.y1 >= y1
                  )).sort((a, b) => a.y2 - b.y2);
                  const [fixture] = fixtures;
                  const isIdenticalFixtures = fixtures.every(f => f.y1 === fixture.y1 && locatedFixturesById[f.id].type === locatedFixturesById[fixture.id].type);
                  const isDisplayOnly = fixtures.some(item => item.isDisplayOnly);
                  const fixtureIdTypeTarget = locatedFixturesIdToType[fixture?.id];
                  const canBeAcceptedByFixtureType = acceptedFixtureType[fixtureIdTypeTarget]?.includes(selectedFixtureType);

                  if (!isIdenticalFixtures) allowItemMove = false;
                  if (isDisplayOnly) allowItemMove = false;
                  if (!canBeAcceptedByFixtureType) allowItemMove = false;

                  if (allowItemMove) {
                    const itemsCoordinatesWithoutSelected = itemsCoordinates.filter(item => item.id !== id);
                    const overlapFacings = itemsCoordinatesWithoutSelected.filter(item => {
                    // When user is trying to move 2 facings (or more) near to each other then we should allow move all facings
                    // So in this case if the item in itemsCoordinatesWithoutSelected is selected then we should just allow it
                      if (selectedItems.includes(item.id) && newHistoryRemovedIds.includes(item.id)) return false;

                      const left = Math.max(item.x1, newItem.x1);
                      const bottom = Math.max(item.y1, newItem.y1);
                      const right = Math.min(item.x2, newItem.x2);
                      const top = Math.min(item.y2, newItem.y2);

                      return right > left && top > bottom;
                    });

                    if (overlapFacings.length) allowItemMove = false;

                    if (allowItemMove) {
                      const currentSelectedFacingGroup = { [id]: facings } as Record<string, number[]>;
                      const { newFacings, remainingFacings } = processFacingGroupSplitting(
                        facingGroupsByIds,
                        currentSelectedFacingGroup,
                        direction,
                        newFacingGroup,
                        shiftValue
                      );

                      newHistoryRemovedIds.push(id);
                      reSelectFacings.push(...remainingFacings.length ? remainingFacings : newFacings);
                      newHistoryFacings.push(...newFacings);
                      newHistoryFacings.push(...remainingFacings);

                      // Handle alerts
                      const isShelf = sourceFixtureType === FIXTURE_TYPES.SHELF;
                      const isUserAlertExist = isFacingHasAlert(alerts, moveableItem, 'move_basket_to_shelf_warnings');
                      const isUserAlertChangesRequired = isShelf && isUserAlertExist;

                      if (isUserAlertChangesRequired) {
                        newAlerts = processAlertsForMove(newAlerts, newFacings.concat(remainingFacings), product);
                      }

                      const isFdDwoAlertExist = isFacingHasAlert(alerts, moveableItem, 'fd_dwo_bincap_warnings');

                      if (isFdDwoAlertExist) {
                        newAlerts = processAlertsForMove(newAlerts, newFacings.concat(remainingFacings), product);
                      }
                    }
                  }
                }
              }
            }
          }

          break;
        }

        case FIXTURE_TYPES.PEGBOARD:
          if (allowItemMove) {
            const shiftValue = isKeyHold ? getMaxShiftValue(direction, true) : ARROW_MOVE_VALUE_PEGBOARD;
            const eslExtraHeight = isEslPog ? EXTRA_ESL_HEIGHT_OUTSIDE_FACING : 0;
            const isDefaultOrientationCode = PEGGABLE_CODES_TO_DROP.some(code => oC === code);

            if (!isDefaultOrientationCode) allowItemMove = false;

            if (allowItemMove) {
              let xPosition = x;
              let yPosition = y;

              if (direction === 'left') {
                xPosition = x - shiftValue;
                yPosition = y;
              } else if (direction === 'right') {
                xPosition = x + shiftValue;
                yPosition = y;
              } else if (direction === 'up') {
                xPosition = x;
                yPosition = y + shiftValue;
              } else if (direction === 'down') {
                xPosition = x;
                yPosition = y - shiftValue;
              }

              const {
                x1: posX1,
                x2: posX2,
                y1: posY1,
                y2: posY2
              } = makeRect(
                xPosition,
                yPosition,
                facingWidth,
                facingHeight
              );

              const occupiedFixtures = fixtureHeight.filter(fixture => {
                const left = Math.max(posX1, fixture.x1);
                const bottom = Math.max(posY1, fixture.y1);
                const right = Math.min(posX2, fixture.x2);
                const top = Math.min(posY2, fixture.y2);

                return right > left && top > bottom;
              });

              const allOccupiedFixturesPegboard = occupiedFixtures.every(f => f.isPegboard);

              if (!occupiedFixtures.length || !allOccupiedFixturesPegboard) allowItemMove = false;

              if (allowItemMove) {
                const isEqualSourceDestionationFixture = sourceFixtureType === locatedFixturesById[occupiedFixtures[0]?.id].type;

                if (!isEqualSourceDestionationFixture) allowItemMove = false;

                if (allowItemMove) {
                  const newFacingX = posX1;
                  const newFacingY = posY1;
                  const cord = isMovingHorizonal ? { x: newFacingX } : { y: newFacingY };

                  const newFacingGroup = {
                    ...facingGroupsByIds[id],
                    id: generateUniqueId(),
                    horizontal_facings: newHorizontalFacings,
                    located_fixture_id: occupiedFixtures[0]?.id,
                    fixtureType: locatedFixturesIdToType[occupiedFixtures[0]?.id],
                    segment: occupiedFixtures[0]?.segment,
                    ...cord
                  };

                  const newItem = {
                    x1: newFacingGroup.x,
                    x2: newFacingGroup.x + newFacingGroup.packaging.width * newFacingGroup.horizontal_facings,
                    y1: newFacingGroup.y,
                    y2: newFacingGroup.y + newFacingGroup.packaging.height + eslExtraHeight
                  };

                  const isBelowBaseFixture = newItem.y1 < PEGBOARD_TEMPLATE.y;

                  if (isBelowBaseFixture) allowItemMove = false;

                  const isItemOutsideMoveableLayer = newItem.x1 < containerLeft || newItem.x2 > containerRight || newItem.y2 > containerTop || newItem.y1 < 0;

                  if (isItemOutsideMoveableLayer) allowItemMove = false;

                  if (allowItemMove) {
                    const {
                    // overlappingWithCrossbars,
                      overlappingWithOthers
                    } = itemsCoordinates.reduce((itemsCollection, i) => {
                      const left = Math.max(newItem.x1, i.x1);
                      const bottom = Math.max(newItem.y1, i.y1);
                      const right = Math.min(newItem.x2, i.x2);
                      const top = Math.min(newItem.y2, i.y2);
                      const isOverlapping = right > left && top > bottom;

                      if (isOverlapping) {
                        if (i.isPeg) {
                          itemsCollection.overlappingWithCrossbars.push(i.id);
                        } else {
                          itemsCollection.overlappingWithOthers.push(i.id);
                        }
                      }

                      return itemsCollection;
                    }, {
                      overlappingWithCrossbars: [] as string[],
                      overlappingWithOthers: [] as string[]
                    });
                    const overlappingWithShelfFixture = isFacingsOverlapWithFixture([newItem], locatedFixtures);

                    if (overlappingWithOthers.length) allowItemMove = false;
                    if (overlappingWithShelfFixture.length) allowItemMove = false;

                    if (allowItemMove) {
                      const itemsCoordinatesWithoutSelf = itemsCoordinates.filter(item => item.id !== id);
                      const overlapShiftItems = itemsCoordinatesWithoutSelf.filter(item => {
                        if (selectedItems.includes(item.id) && newHistoryRemovedIds.includes(item.id)) return false;

                        const left = Math.max(newItem.x1, item.x1);
                        const bottom = Math.max(newItem.y1, item.y1);
                        const right = Math.min(newItem.x2, item.x2);
                        const top = Math.min(newItem.y2, item.y2);

                        return right > left && top > bottom;
                      });
                      const occupiedShiftFixtures = fixtureHeight.filter(fixture => {
                        const left = Math.max(newItem.x1, fixture.x1);
                        const bottom = Math.max(newItem.y1, fixture.y1);
                        const right = Math.min(newItem.x2, fixture.x2);
                        const top = Math.min(newItem.y2, fixture.y2);

                        return right > left && top > bottom && !fixture.isPegboard;
                      });
                      const isItemOutsideDropLayer =
                        newItem.x1 < containerLeft ||
                        newItem.x2 > containerRight ||
                        newItem.y2 > containerTop ||
                        newItem.y1 < 0;

                      if (overlapShiftItems.length) allowItemMove = false;
                      if (occupiedShiftFixtures.length) allowItemMove = false;
                      if (isItemOutsideDropLayer) allowItemMove = false;

                      if (allowItemMove) {
                        newHistoryRemovedIds.push(id);
                        reSelectFacings.push(newFacingGroup);
                        newHistoryFacings.push(newFacingGroup);

                        // Handle alerts
                        const isShelf = sourceFixtureType === FIXTURE_TYPES.PEGBOARD;
                        const isUserAlertExist = isFacingHasAlert(alerts, moveableItem, 'move_crossbar_to_peg_warnings');
                        const isUserAlertChangesRequired = isShelf && isUserAlertExist;

                        if (isUserAlertChangesRequired) {
                          newAlerts = processAlertsForMove(newAlerts, [newFacingGroup], product);
                        }

                        const isFdDwoAlertExist = isFacingHasAlert(alerts, moveableItem, 'fd_dwo_bincap_warnings');

                        if (isFdDwoAlertExist) {
                          newAlerts = processAlertsForMove(newAlerts, [newFacingGroup], product);
                        }
                      }
                    }
                  }
                }
              }
            }
          }

          break;

        default:
          break;
      }
    }
  };

  const sortedItem = selectedItems.sort((a, b) => {
    const firstFacingGroup = facingGroupsByIds[a];
    const secondFacingGroup = facingGroupsByIds[b];
    const isMovingHorizonal = ['left', 'right'].includes(direction);

    if (isMovingHorizonal) {
      return direction === 'left' ? firstFacingGroup.x - secondFacingGroup.x : secondFacingGroup.x - firstFacingGroup.x;
    }

    return direction === 'down' ? firstFacingGroup.y - secondFacingGroup.y : secondFacingGroup.y - firstFacingGroup.y;
  });

  for (const item of sortedItem) {
    processMoveItem(item, newHistoryFacings, newHistoryRemovedIds);
  }

  const anyFacingMoved = newHistoryFacings.length > 0 || newHistoryRemovedIds.length > 0;
  const newHistoryItem = {
    ...historyStepItem,
    newFacings: newHistoryFacings,
    removedId: newHistoryRemovedIds,
    alerts: newAlerts,
    reSelectFacings
  };

  return anyFacingMoved ? newHistoryItem : false;
};

export const processingDropEvent = (
  processedMousePositions: ReturnType<typeof getProcessedPositionsFromMonitor>,
  monitor: DropTargetMonitor,
  selectedFacingsGroup: Record<string, Array<number>>,
  scale: number,
  locatedFixturesIdToType: Record<string, FixtureType>,
  fixtureCrossbar: FixtureSpace[],
  fixtureFlatbar: FixtureSpace[],
  fixturePegboard: FixtureSpace[],
  fixtureBasket: FixtureSpace[],
  fixtureDefaultShelf: FixtureSpace[],
  itemsCoordinates: ItemCoordinates[],
  facingGroupsByIds: Record<string, FacingGroupExtended>,
  uniquePlanogramFacings: Record<string, number>,
  clipboardSkuToFacingGroups: Record<string, FacingGroupExtended>,
  fixturesPeg: Fixture[],
  fixtureSpaces: FixtureSpace[],
  fixtureSpaceSelectorByTypes: Array<FixturePosition>,
  fixtureHeight: FixtureHeight[],
  locatedFixturesById: Record<string, LocatedFixture>,
  locatedFixtures: Array<LocatedFixture>,
  selectedFixtureType: string | FixtureType,
  pegboardConfig: PegboardStartHoles,
  splicerCoordinates: Array<SplicerPosition>,
  alerts: WarningsData,
  isEslPog: boolean,
  distinctType: string = '',
  numberOfSegments: number,
) => {
  const type = monitor.getItemType() as string;
  const dropEffect = getDropEffectFromStorage();
  const isCopy = dropEffect === DROP_EFFECTS.COPY;

  if (!processedMousePositions || !type) {
    return false;
  }

  const {
    itemRootPosition: { x, y },
    correctedClipboardItem,
    containerBoundary: {
      left: containerLeftPosition,
      right: containerRightPosition,
      top: containerTopPosition
    }
  } = processedMousePositions;

  const draggableItem = monitor.getItem() as ClipboardDraggableObject;

  if (!draggableItem) return false;
  const {
    rearangedItems: items,
    facingsIds: activeItems,
    groupedWidth: itemsWidth,
    hmax,
    facingsIds,
    clipboardOffsetTop = 0,
    clipboardOffsetRight = 0,
    lastItemWidthScaled = 0,
    id: currentDraggableId,
    draggableHeight,
    fixtureType,
  } = draggableItem;
  const width = itemsWidth / scale;
  const horizontalSpacing = facingsIds?.length > 1 ? HORIZONTAL_SPACING * scale : 0;
  const xOffset = correctedClipboardItem.x + clipboardOffsetRight - itemsWidth + lastItemWidthScaled + horizontalSpacing;
  const clipboardXOffset = getClipboardXOffset(xOffset, type);
  const clipboardYOffset = Object.values(DraggableSkuClipboardItems).includes(type)
    ? clipboardOffsetTop - correctedClipboardItem.y
    : 0;
  const isClipboardType = isClipboardItem(type);
  const isWorkbenchType = isWorkbenchItem(type);
  const x1 = (x + clipboardXOffset) / scale;
  const y1 = (y + clipboardYOffset) / scale;
  const height = hmax / scale;
  const containerRight = containerRightPosition / scale;
  const containerLeft = containerLeftPosition / scale;
  const containerTop = containerTopPosition / scale;
  const rightBoundary = numberOfSegments * SEGMENT_WIDTH;
  const draggableItemRect = makeDraggableRectShift(x1, y1, width, height, rightBoundary);
  let { draggableLeft, draggableRight, draggableBottom, draggableTop } = draggableItemRect;
  const isSelectedPeggable = !!facingsIds?.every(id => facingGroupsByIds[id]?.packaging?.peggable);
  const dropFixtureType = getDropFixtureType(
    isSelectedPeggable,
    draggableItemRect,
    fixtureCrossbar,
    fixturePegboard,
    fixtureBasket,
    fixtureFlatbar,
    fixtureDefaultShelf
  );

  const isShelf = selectedFixtureType === FIXTURE_TYPES.SHELF;
  const isPegboard = selectedFixtureType === FIXTURE_TYPES.PEGBOARD;
  const isPlanogram = distinctType === DISTRICT_TYPES.PLANOGRAM || distinctType === DISTRICT_TYPES.WORKBENCH;
  const isPegOnPlanogram = isPlanogram && isPegboard;
  const isMultiple = items?.length > 1;
  const { startHoleX, startHoleY } = pegboardConfig;

  let removedId: Array<string> = [];
  let facings: Array<FacingGroupExtended> = [];
  let removedClipboardId: Array<string> = [];
  let newClipboardFacings: Array<FacingGroupExtended> = [];
  let newClipboardFixturesFacings: Array<FacingGroupExtended> = [];
  let removedClipboardFixturesFacings: Array<string> = [];
  let removedWorkbenchFacingId: Array<string> = [];
  let newWorkbenchFacings: Array<FacingGroupExtended> = [];
  let newAlerts = alerts;
  let openAlert = false;
  let selectAlert: WarningData | undefined = undefined;
  const eslExtraHeight = isEslPog ? EXTRA_ESL_HEIGHT_OUTSIDE_FACING : 0;

  const createAlertEvent = (dropResult: {
    warningType: 'move_basket_to_shelf_warnings' | 'move_crossbar_to_peg_warnings';
    newClipboardFacings: Array<FacingGroupExtended>;
    recalculatedPositions: Array<FacingGroupExtended>;
    newRemainingFacings?: Array<FacingGroupExtended>;
    newFacings?: Array<FacingGroupExtended>;
    removedClipboardId?: Array<string>;
    alerts: WarningsData;
  }) => {
    const {
      warningType,
      newClipboardFacings,
      recalculatedPositions,
      newRemainingFacings,
      newFacings,
      removedClipboardId,
    } = dropResult;
    let uniqueProducts: Array<string> = [];

    if (recalculatedPositions && recalculatedPositions.length && isClipboardType) {
      const allDraggableProducts = recalculatedPositions.map(fG => fG.product_num);

      uniqueProducts = Array.from(new Set(allDraggableProducts));
    }

    if (removedClipboardId && removedClipboardId.length) {
      const clipboardDraggableProducts = removedClipboardId.map((id) => facingGroupsByIds[id].product_num);
      const combineProducts = [...uniqueProducts, ...clipboardDraggableProducts];

      uniqueProducts = Array.from(new Set(combineProducts));
    }

    const isFromTo = (from: FIXTURE_TYPES, to: FIXTURE_TYPES) => {
      const activeItemsFG = activeItems.map((id) => facingGroupsByIds[id]);

      if (!activeItemsFG.length || !recalculatedPositions.length) {
        return false;
      }

      return activeItemsFG[0].fixtureType === from && recalculatedPositions[0].fixtureType === to;
    };

    const isMovedPogHasAlertFixtureType =
      isFromTo(FIXTURE_TYPES.BASKET, FIXTURE_TYPES.SHELF) ||
      isFromTo(FIXTURE_TYPES.CROSSBAR, FIXTURE_TYPES.PEGBOARD) ||
      isFromTo(FIXTURE_TYPES.FLATBAR, FIXTURE_TYPES.PEGBOARD);

    const getSelectedFacingHavingAlert = () => {
      const selectedItem = activeItems.map((id) => facingGroupsByIds[id]);

      return selectedItem.filter((f) => isFacingHasAlert(alerts, f, warningType, false));
    };

    let openAlert = isMovedPogHasAlertFixtureType;
    const alertExistIndex = alerts?.findIndex((a) => a.warningType === warningType);
    const selectedItem = activeItems.map((id) => facingGroupsByIds[id]);
    let allFacings = newFacings ? newFacings.filter((f) => isFacingHasAlert(alerts, f, warningType, false)) : [];

    if (recalculatedPositions && recalculatedPositions.length) {
      if (isMovedPogHasAlertFixtureType) {
        allFacings = allFacings.concat(recalculatedPositions);
      } else {
        const facingWithAlert = getSelectedFacingHavingAlert();
        const prodNums = facingWithAlert.length ? facingWithAlert.map((a) => a.product_num) : [];

        allFacings = allFacings.concat(
          recalculatedPositions.filter((nrf) => prodNums.includes(nrf.product_num)).map((nf) => nf)
        );
      }
    }

    let newAlerts: Partial<WarningData> = {};
    const alertsToAdd = allFacings.map((f) => generateAlertObj(f, true));

    if (alertExistIndex > -1) {
      newAlerts = alerts[alertExistIndex];
      let data = (newAlerts.data as MoveBasketToSelfWarnings[] | MoveCrossbarToPegWarnings[]) || [];

      data = data.filter(
        (a: MoveBasketToSelfWarnings | MoveCrossbarToPegWarnings) =>
          !selectedItem.find((cF) => cF.x === a.x && a.y === cF.y)
      );

      if (removedClipboardId && removedClipboardId.length) {
        removedClipboardId.forEach((id) => {
          const facing = facingGroupsByIds[id];

          if (isFacingHasAlert(alerts, facing, warningType, true)) {
            openAlert = true;
          }
        });
      }

      if (newRemainingFacings && newRemainingFacings.length) {
        const facingWithAlert = getSelectedFacingHavingAlert();
        const prodNums = facingWithAlert.length ? facingWithAlert.map((a) => a.product_num) : [];

        data = data.concat(
          newRemainingFacings
            .filter((nrf) => prodNums.includes(nrf.product_num))
            .map((nf) => generateAlertObj(nf, true))
        );
      }

      if (newClipboardFacings && newClipboardFacings.length) {
        newClipboardFacings.forEach((cF) => {
          const index = data.findIndex((a) => cF.x === a.x && a.y === cF.y);
          const objFound = data[index];

          if (index > -1) {
            data[index] = { ...objFound, to_fixture_type: '' };
          }
        });
      }

      newAlerts = { ...newAlerts, data: [...data, ...alertsToAdd] } as WarningData;
      const alertsWithUserAction = newAlerts.data?.length
        ? ([
          ...alerts.slice(alertExistIndex, 0),
            newAlerts as WarningData,
            ...alerts.slice(alertExistIndex + 1),
        ] as WarningsData)
        : ([...alerts.slice(alertExistIndex, 0), ...alerts.slice(alertExistIndex + 1)] as WarningsData);

      return {
        alerts: uniqueProducts.length > 0
          ? toggleClipboardStatus(uniqueProducts, alertsWithUserAction, false)
          : alertsWithUserAction,
        selectAlert: openAlert ? newAlerts : undefined,
        openAlert,
      };
    } else {
      newAlerts = { selected: true, warningType, data: alertsToAdd } as WarningData;
      // clear selected alerts if we need to select new one
      const existingAlerts = openAlert ? alerts.map(a => {
        return { ...a, selected: false };
      }) : alerts;
      const alertsWithUserAction = newAlerts.data?.length
        ? ([newAlerts, ...existingAlerts] as WarningsData)
        : (alerts );

      return {
        alerts: uniqueProducts.length > 0
          ? toggleClipboardStatus(uniqueProducts, alertsWithUserAction, false)
          : alertsWithUserAction,
        selectAlert: openAlert ? newAlerts : undefined,
        openAlert,
      };
    }
  };

  switch (dropFixtureType) {
    case FIXTURE_TYPES.CROSSBAR:
    case FIXTURE_TYPES.FLATBAR: {
      const moreThenOneHorisontalyVerticaly = items.some(item => {
        const facing = facingGroupsByIds[item.id];

        if (facing) {
          const { vertical_facings } = facing;

          if (vertical_facings > 1) {
            return true;
          }
        }

        return false;
      });

      if (moreThenOneHorisontalyVerticaly) {
        return false;
      }

      // check orientation
      const isDefaultOrientationCode = items.every(item => {
        const facing = facingGroupsByIds[item.id];

        if (facing) {
          const { orientation_code } = facing;

          if (PEGGABLE_CODES_TO_DROP.some(code => orientation_code === code)) {
            return true;
          }
        }

        return false;
      });

      if (!isDefaultOrientationCode) {
        return false;
      }

      const locatedFixtureFrom = locatedFixturesById?.[facingGroupsByIds[facingsIds[0]]?.located_fixture_id];
      const z = locatedFixtureFrom?.z || 0;
      let occupiedFixtures: Array<FixtureSpace> = [];

      if (dropFixtureType === FIXTURE_TYPES.CROSSBAR) {
        occupiedFixtures = fixtureCrossbar.filter(fixture => {
          const left = Math.max(draggableLeft, fixture.x1);
          const bottom = Math.max(draggableBottom, fixture.y1);
          const right = Math.min(draggableRight, fixture.x2);
          const top = Math.min(draggableTop, fixture.y2);

          return right > left && top > bottom;
        });
      }

      if (dropFixtureType === FIXTURE_TYPES.FLATBAR) {
        occupiedFixtures = fixtureFlatbar.filter(fixture => {
          const left = Math.max(draggableLeft, fixture.x1);
          const bottom = Math.max(draggableBottom, fixture.y1);
          const right = Math.min(draggableRight, fixture.x2);
          const top = Math.min(draggableTop, fixture.y2);

          return right > left && top > bottom;
        });
      }

      occupiedFixtures = filterOccupiedCrossbarFixtures(occupiedFixtures);

      if (!occupiedFixtures.length) {
        return false;
      }

      const [occupiedFixture] = occupiedFixtures.sort((a, b) => Math.abs(a.y2 - draggableTop) - Math.abs(b.y2 - draggableTop));

      if (
        (locatedFixtureFrom?.type === 'CROSSBAR' || locatedFixtureFrom?.type === 'FLATBAR') &&
        occupiedFixture.z > 0 &&
        z !== occupiedFixture.z
      ) {
        return false;
      }

      if (z > 0) {
        occupiedFixtures = occupiedFixtures.filter(of => of.z === z);
      }

      if (!occupiedFixtures.length) return false;

      if (occupiedFixtures.length > 1) {
        const sameLevelFixture = occupiedFixtures.every(
          f => f.y1 === occupiedFixture.y1 &&
          f.y2 === occupiedFixture.y2 &&
          f.z > 0 ? f.z === z : true
        );

        if (!sameLevelFixture) {
          return false;
        }
      }

      const locatedFixtureHeightWithoutPeggableFixtures = fixtureHeight.filter(locatedFixture =>
        !locatedFixture.isPegboard &&
        !locatedFixture.isCrossbar &&
        !locatedFixture.isFlatbar
      );
      const overlapFixtureWithoutCrossbar = locatedFixtureHeightWithoutPeggableFixtures.some(fixture => {
        const left = Math.max(draggableLeft, fixture.x1);
        const bottom = Math.max(draggableBottom, fixture.y1);
        const right = Math.min(draggableRight, fixture.x2);
        const top = Math.min(draggableTop, fixture.y2);

        return right > left && top > bottom;
      });

      if (overlapFixtureWithoutCrossbar) {
        return false;
      }

      // each peggable facing needs its own peg, so multi facing groups are placed one facing at a time
      const crossbarFacings = splitPegboardFacingsGroup(items, facingGroupsByIds, selectedFacingsGroup);
      const productsWithoutCompatiblePeg = new Set<string>();

      let recalculatedPositions: Array<FacingGroupExtended> = crossbarFacings.reduce((acc, val) => {
        const draggableItem = facingGroupsByIds[val.id];
        const sourceFixture = locatedFixturesById[draggableItem.located_fixture_id];
        const {
          vertical_facings: v,
          packaging: {
            height,
            nested_height: nH,
            width,
            depth,
            weight,
            pegholes
          },
          cap_nested: cN,
          capped_vertical_facings: cV,
          cappedPackaging: {
            height: cH
          },
        } = draggableItem;
        // check peg_id
        let { peg_id, depth_facings } = draggableItem;

        const newHorizontalFacings = 1;
        const facingHeight = getFacingHeight(height, v, cN, nH, cH, cV);
        const facingWidth = width * newHorizontalFacings;
        const previousFacing = acc[acc.length - 1];
        const xPosition = !previousFacing
          ? draggableLeft
          : previousFacing.x +
            (previousFacing.packaging.width * previousFacing.horizontal_facings) +
            HORIZONTAL_SPACING;
        const {
          draggableLeft: posX1,
          draggableRight: posX2,
        } = makeDraggableRect(xPosition, y1, facingWidth, facingHeight);
        const baseFixture = occupiedFixtures.find(f => (f.x1 <= posX1 && f.x2 >= posX2) || (f.x1 <= posX1 && f.x2 >= posX1)) || occupiedFixture;
        const existingPeg = peg_id ? fixturesPeg.find(f => f.fixture_id === peg_id) : undefined;
        const canReuseClipboardPeg = isClipboardType &&
          fixtureType === dropFixtureType &&
          String(sourceFixture?.fixture_id) === String(baseFixture.fixtureId) &&
          existingPeg?.fixture_attachment === dropFixtureType &&
          existingPeg.fixture_depth !== null &&
          existingPeg.fixture_depth >= depth &&
          (existingPeg.weight_capacity === null || existingPeg.weight_capacity >= weight);

        if (fixtureType !== dropFixtureType || (isClipboardType && !canReuseClipboardPeg) || isWorkbenchType) {
          const [fixturePeg] = fixturesPeg.filter(f =>
            f.fixture_attachment === dropFixtureType &&
            f.fixture_depth !== null &&
            f.weight_capacity !== null &&
            f.fixture_depth >= depth &&
            f.weight_capacity >= weight
          ).sort((a, b) => {
            const countOfWeigthA = a.weight_capacity ? Math.trunc(a.weight_capacity / weight) : 0;
            const countOfDepthA = a.fixture_depth ? Math.trunc(a.fixture_depth / depth) : 0;
            const countMinA = Math.min(countOfWeigthA, countOfDepthA);
            const countOfWeigthB = b.weight_capacity ? Math.trunc(b.weight_capacity / weight) : 0;
            const countOfDepthB = b.fixture_depth ? Math.trunc(b.fixture_depth / depth) : 0;
            const countMinB = Math.min(countOfWeigthB, countOfDepthB);

            return countMinB - countMinA;
          });

          if (!fixturePeg) {
            productsWithoutCompatiblePeg.add(draggableItem.product_num);

            return acc;
          }

          const countOfWeight = fixturePeg.weight_capacity ? Math.trunc(fixturePeg.weight_capacity / weight) : 0;
          const countOfDepth = fixturePeg.fixture_depth ? Math.trunc(fixturePeg.fixture_depth / depth) : 0;

          depth_facings = Math.min(countOfWeight, countOfDepth);
          peg_id = fixturePeg.fixture_id;
        }

        const pegholesPerInch = pegholes.map(peghole => ({
          peghole_x: peghole.peghole_x,
          peghole_y: peghole.peghole_y,
          peghole_width: peghole.peghole_width,
        }));

        const pegholePerInch = pegholesPerInch[0];
        const absolutePegholePerInch = {
          peghole_x: val.x + pegholePerInch.peghole_x,
          peghole_y: val.y + val.height - pegholePerInch.peghole_y,
          peghole_width: pegholePerInch.peghole_width,
        };
        const deltaFixturePeghole = absolutePegholePerInch.peghole_y - occupiedFixture.y2;
        const shiftDraggableRect = {
          draggableRight,
          draggableLeft,
          draggableTop: val.y + val.height - deltaFixturePeghole,
          draggableBottom: val.y - deltaFixturePeghole,
        };

        const newFacingX = xPosition;
        const newFacingY = shiftDraggableRect.draggableBottom;

        acc.push({
          ...facingGroupsByIds[val.id],
          peg_id,
          depth_facings,
          id: generateUniqueId(),
          x: newFacingX,
          y: newFacingY,
          horizontal_facings: newHorizontalFacings,
          located_fixture_id: baseFixture.id,
          fixtureType: locatedFixturesIdToType[baseFixture.id],
          segment: baseFixture.segment
        });

        return acc;
      }, [] as Array<FacingGroupExtended>);

      if (recalculatedPositions.length !== crossbarFacings.length) {
        if (productsWithoutCompatiblePeg.size) {
          return {
            ...historyStepItem,
            incompatiblePegSkus: Array.from(productsWithoutCompatiblePeg).map(sku => ({
              sku,
              source: isClipboardType ? 'clipboard' as const : 'planogram' as const,
            })),
            openAlert: false,
            selectAlert: undefined,
          };
        }

        return false;
      }

      let newItems = recalculatedPositions.map(item => {
        return {
          id: item.id,
          x1: item.x,
          x2: item.x + item.packaging.width * item.horizontal_facings,
          y1: item.y,
          y2: item.y + item.packaging.height + eslExtraHeight
        };
      });

      const isBelowBaseFixture = newItems.some(item => item.y1 < PEGBOARD_TEMPLATE.y);

      if (isBelowBaseFixture) {
        return false;
      }

      // shift
      const itemsCoordinatesWithoutSelected = itemsCoordinates.filter(item => !facingsIds.includes(item.id));
      const overlapFacings = itemsCoordinatesWithoutSelected.filter(item => newItems.some(newItem => {
        const left = Math.max(item.x1, newItem.x1);
        const bottom = Math.max(item.y1, newItem.y1);
        const right = Math.min(item.x2, newItem.x2);
        const top = Math.min(item.y2, newItem.y2);

        return right > left && top > bottom;
      }));

      if (overlapFacings.length) {
        overlapFacings.sort((a, b) => {
          const facingGroupA = facingGroupsByIds[a.id];
          const facingGroupB = facingGroupsByIds[b.id];

          return facingGroupA.x - facingGroupB.x;
        });
        const firstOverlapFacing = overlapFacings[0];
        const firstNewItem = newItems[0];
        const lastOverlapFacing = overlapFacings[overlapFacings.length - 1];
        const lastNewItem = newItems[newItems.length - 1];
        let diff = 0;

        // check orientation overlap
        if (firstOverlapFacing.x1 < firstNewItem.x1) {
          diff = Math.abs(firstOverlapFacing.x2 - firstNewItem.x1);
        }

        if (lastOverlapFacing.x2 > lastNewItem.x2) {
          diff = -(Math.abs(firstOverlapFacing.x1 - lastNewItem.x2));
        }

        if (firstOverlapFacing.x1 < firstNewItem.x1 && lastOverlapFacing.x2 > lastNewItem.x2) {
          diff = 0;
        }

        if (diff !== 0) {
        // set new shifted position
          const newItemsShift = newItems.map(item => ({
            ...item,
            x1: item.x1 + diff,
            x2: item.x2 + diff,
          }));

          // check is out not of boundary
          const firstNewItemShift = newItemsShift[0];
          const lastNewItemShift = newItemsShift[newItems.length - 1];

          if (firstNewItemShift.x1 >= 0 && lastNewItemShift.x2 <= rightBoundary) {
          // check is overlap with Facings
            const overlapFacingsShift = itemsCoordinatesWithoutSelected.some(item => newItemsShift.some(newItem => {
              const left = Math.max(item.x1, newItem.x1);
              const bottom = Math.max(item.y1, newItem.y1);
              const right = Math.min(item.x2, newItem.x2);
              const top = Math.min(item.y2, newItem.y2);

              return right > left && top > bottom;
            }));

            if (!overlapFacingsShift) {
            // check is overlap with Fixtures
              const overlapFixtureShift = fixtureSpaceSelectorByTypes
                .filter(item => locatedFixturesById[item.id].type !== 'CROSSBAR')
                .some(item => newItemsShift.some(newItem => {
                  const left = Math.max(newItem.x1, item.x1);
                  const bottom = Math.max(newItem.y1, item.y1);
                  const right = Math.min(newItem.x2, item.x2);
                  const top = Math.min(newItem.y2, item.y2 );

                  return right > left && top > bottom;
                }));

              if (!overlapFixtureShift) {
                newItems = [...newItemsShift];
                recalculatedPositions = recalculatedPositions.map(item => ({
                  ...item,
                  x: item.x + diff
                }));
              }
            }
          }
        }
      }
      // shift

      const { crossbarOverlappingIds, othersOverlappingsIds } = newItems.reduce((acc, item) => {
        const {
          overlappingWithCrossbars,
          overlappingWithOthers
        } = itemsCoordinatesWithoutSelected.reduce((itemsCollection, i) => {
          const lf = locatedFixturesById[facingGroupsByIds[i.id].located_fixture_id];

          if (z > 0 && i.isCrossbar && lf.z !== z) {
            return itemsCollection;
          }

          const left = Math.max(item.x1, i.x1);
          const bottom = Math.max(item.y1, i.y1);
          const right = Math.min(item.x2, i.x2);
          const top = Math.min(item.y2, i.y2);
          const isOverlapping = right > left && top > bottom;

          if (isOverlapping) {
            if (i.isPeg) {
              itemsCollection.overlappingWithCrossbars.push(i.id);
            } else {
              itemsCollection.overlappingWithOthers.push(i.id);
            }
          }

          return itemsCollection;
        }, {
          overlappingWithCrossbars: [] as string[],
          overlappingWithOthers: [] as string[]
        });

        acc.crossbarOverlappingIds.push(...overlappingWithCrossbars);
        acc.othersOverlappingsIds.push(...overlappingWithOthers);

        return acc;
      }, {
        crossbarOverlappingIds: [] as string[],
        othersOverlappingsIds: [] as string[]
      });

      if (othersOverlappingsIds.length) {
        return false;
      }

      const extendedIds = getActiveFacingsFromFacingGroup(
        crossbarOverlappingIds,
        selectedFacingsGroup,
        facingGroupsByIds,
        draggableLeft,
        draggableRight,
        isCopy
      );

      const extendedIdsCollection = Object.keys(extendedIds);
      const { newFacings, newClipboardFacings: clipboardFacings } = processRemovingFacingWIP1(
        extendedIds,
        facingGroupsByIds,
        fixtureCrossbar,
        fixtureFlatbar,
        fixturePegboard,
        fixtureSpaces,
        locatedFixturesIdToType,
        selectedFixtureType,
        selectedFacingsGroup
      );

      const newRemainingFacings = isCopy ? [] : Object.keys(selectedFacingsGroup).reduce((acc, val) => {
        if (extendedIdsCollection.includes(val)) {
          return acc;
        }

        const facingGroup = facingGroupsByIds[val];
        const { horizontal_facings: h } = facingGroup;
        const facings = selectedFacingsGroup[val];
        const facingsNumber = facings.length;

        if (facingsNumber === 0 || facingsNumber === h) {
          return acc;
        }

        const newFacingsCollection = processCreatingBoardFacings(
          facingGroup,
          facings,
          fixtureSpaces,
          locatedFixturesIdToType,
          selectedFixtureType,
          fixtureCrossbar,
          fixtureFlatbar,
          fixturePegboard
        );

        acc.push(...newFacingsCollection);

        return acc;
      }, [] as Array<any>);

      facings = [
        ...recalculatedPositions,
        ...newFacings,
        ...(isClipboardType || isWorkbenchType ? [] : newRemainingFacings)
      ];

      if (!facings.length) {
        return false;
      }

      removedId = isCopy ? crossbarOverlappingIds : activeItems.concat(crossbarOverlappingIds);
      newClipboardFacings = filterByUniqueSku(clipboardFacings, uniquePlanogramFacings);

      if (isWorkbenchType) {
        removedWorkbenchFacingId = isCopy ? [] : facingsIds;
        newWorkbenchFacings = newRemainingFacings;
      }

      if (isClipboardType) {
        const {
          removedClipboardItemIds, newClipboardItems
        } = processRemovingClipbordItems(selectedFacingsGroup, facingGroupsByIds);

        switch (type) {
          case DraggableClipboardFixtureSkuItems.FixtureDefauldItem:
          case DraggableClipboardFixtureSkuItems.FixtureYCapped:
          case DraggableClipboardFixtureSkuItems.FixtureYNested:
          case DraggableClipboardFixtureSkuItems.FixtureZNested: {
            const connectedSkus = processRemovingSkusConnectedToFixture(
              facings,
              clipboardSkuToFacingGroups
            );

            newClipboardFixturesFacings = removedClipboardItemIds.reduce((acc, id) => {
              const facing = facingGroupsByIds[id];
              const selectedFacings = selectedFacingsGroup[id].length;

              if (selectedFacings === facing.horizontal_facings || selectedFacings === 0) return acc;

              const h = facing.horizontal_facings - selectedFacings;

              return acc.concat({
                ...facing,
                id: generateUniqueId(),
                horizontal_facings: h
              });
            }, [] as Array<FacingGroupExtended>);

            removedClipboardId = removedClipboardId.concat(connectedSkus.removedClipboardIds);
            removedClipboardFixturesFacings = removedClipboardItemIds;
            newClipboardFacings = newClipboardFacings.concat(connectedSkus.newClipboardFacings);
          }

            break;

          default: {
            removedClipboardId = removedClipboardItemIds;
            newClipboardFacings = newClipboardFacings.concat(newClipboardItems);
          }
        }
      }

      // PEGBOARD -> CROSSBAR
      if (isPegboard) {
        const alertsData = createAlertEvent({
          warningType: 'move_crossbar_to_peg_warnings',
          newClipboardFacings,
          recalculatedPositions: recalculatedPositions.filter(fg => fg.fixtureType === FIXTURE_TYPES.PEGBOARD),
          newRemainingFacings,
          newFacings,
          removedClipboardId,
          alerts
        });

        newAlerts = alertsData.alerts;
      }
    }

      break;

    case FIXTURE_TYPES.PEGBOARD: {
      const moreThenOneHorisontalyVerticaly = items.some(item => {
        const facing = facingGroupsByIds[item.id];

        if (facing) {
          const { vertical_facings } = facing;

          if (vertical_facings > 1) {
            return true;
          }
        }

        return false;
      });

      if (moreThenOneHorisontalyVerticaly) {
        return false;
      }

      // check orientation
      const isDefaultOrientationCode = items.every(item => {
        const facing = facingGroupsByIds[item.id];

        if (facing) {
          const { orientation_code } = facing;

          if (PEGGABLE_CODES_TO_DROP.some(code => orientation_code === code)) {
            return true;
          }
        }

        return false;
      });

      if (!isDefaultOrientationCode) {
        return false;
      }

      const pegboardFacings = splitPegboardFacingsGroup(items, facingGroupsByIds, selectedFacingsGroup);

      const recalculatedPositions: Array<FacingGroup> = pegboardFacings.reduce((acc, val, index) => {
        const currentDraggableItem = facingGroupsByIds[currentDraggableId];
        const draggableItem = facingGroupsByIds[val.id];
        const {
          vertical_facings: v,
          packaging: {
            height,
            nested_height: nH,
            width,
            pegholes
          },
          cap_nested: cN,
          capped_vertical_facings: cV,
          cappedPackaging: {
            height: cH
          },
          x: originalLeftPos,
          y: originalBottomPos
        } = draggableItem;

        // check peg_id
        let { peg_id, depth_facings } = draggableItem;

        if (
          fixtureType !== 'PEGBOARD' ||
          isClipboardType ||
          isWorkbenchType
        ) {
          const [fixturePeg] = fixturesPeg.filter(f =>
            f.fixture_attachment === 'PEGBOARD' &&
            f.fixture_depth !== null &&
            f.weight_capacity !== null &&
            f.fixture_depth >= draggableItem.packaging.depth &&
            f.weight_capacity * draggableItem.packaging.pegholes.length >= draggableItem.packaging.weight
          ).sort((a, b) => {
            const countOfWeigthA = a.weight_capacity ? Math.trunc((a.weight_capacity * draggableItem.packaging.pegholes.length) / draggableItem.packaging.weight) : 0;
            const countOfDepthA = a.fixture_depth ? Math.trunc(a.fixture_depth / draggableItem.packaging.depth) : 0;
            const countMinA = Math.min(countOfWeigthA, countOfDepthA);
            const countOfWeigthB = b.weight_capacity ? Math.trunc((b.weight_capacity * draggableItem.packaging.pegholes.length) / draggableItem.packaging.weight) : 0;
            const countOfDepthB = b.fixture_depth ? Math.trunc(b.fixture_depth / draggableItem.packaging.depth) : 0;
            const countMinB = Math.min(countOfWeigthB, countOfDepthB);

            return countMinB - countMinA;
          });

          if (!fixturePeg) return acc;

          const countOfWeight = fixturePeg.weight_capacity ? Math.trunc((fixturePeg.weight_capacity * draggableItem.packaging.pegholes.length) / draggableItem.packaging.weight) : 0;
          const countOfDepth = fixturePeg.fixture_depth ? Math.trunc(fixturePeg.fixture_depth / draggableItem.packaging.depth) : 0;

          depth_facings = Math.min(countOfWeight, countOfDepth);
          peg_id = fixturePeg.fixture_id;
        }

        const newHorizontalFacings = 1;
        const facingHeight = getFacingHeight(height, v, cN, nH, cH, cV);
        const facingWidth = width * newHorizontalFacings;
        let xPosition = 0;
        let yPosition = 0;

        if (isPegOnPlanogram) {
          xPosition = draggableLeft + originalLeftPos - currentDraggableItem.x;
          yPosition = y1 - (currentDraggableItem.y - originalBottomPos + (currentDraggableId === val.id ? 0 : draggableHeight - facingHeight));
        } else {
          xPosition = index === 0
            ? draggableLeft
            : acc[index - 1]?.x + (acc[index - 1]?.packaging?.width * acc[index - 1]?.horizontal_facings) + 2 * HORIZONTAL_SPACING;
          yPosition = y1;
        }

        let { draggableLeft: posX1, draggableRight: posX2, draggableBottom: posY1, draggableTop: posY2 } = makeDraggableRect(xPosition, yPosition, facingWidth, facingHeight);
        const isItemOutsideDropLayer = posX1 < containerLeft || posX2 > containerRight || posY2 > containerTop || posY1 < 0;

        if (isItemOutsideDropLayer) {
          return acc;
        }

        const occupiedFixtures = fixtureHeight.filter(fixture => {
          const left = Math.max(posX1, fixture.x1);
          const bottom = Math.max(posY1, fixture.y1);
          const right = Math.min(posX2, fixture.x2);
          const top = Math.min(posY2, fixture.y2);

          return right > left && top > bottom;
        });

        const allOccupiedFixturesPegboard = occupiedFixtures.every(f => f.isPegboard);

        if (!occupiedFixtures.length || !allOccupiedFixturesPegboard) {
          return acc;
        }

        if (!isMultiple) {
          const itemsCoordinatesWithoutSelf = itemsCoordinates.filter(item => item.id !== val.id);
          const itemsCoordinatesPeg = itemsCoordinatesWithoutSelf.filter(item => item.isPeg);
          const overlapItemsCoordinatesPeg = itemsCoordinatesPeg.filter(item => {
            const left = Math.max(posX1, item.x1);
            const bottom = Math.max(posY1, item.y1);
            const right = Math.min(posX2, item.x2);
            const top = Math.min(posY2, item.y2);

            return right > left && top > bottom;
          });

          if (overlapItemsCoordinatesPeg.length === 1) {
            const overlapItem = overlapItemsCoordinatesPeg[0];
            const shiftItem = {
              posX1,
              posX2,
              posY1,
              posY2,
            };
            const leftTop =
              shiftItem.posX1 < overlapItem.x1 &&
              shiftItem.posX1 < overlapItem.x2 &&
              shiftItem.posX2 > overlapItem.x1 &&
              shiftItem.posX2 < overlapItem.x2 &&
              shiftItem.posY1 > overlapItem.y1 &&
              shiftItem.posY1 < overlapItem.y2 &&
              shiftItem.posY2 > overlapItem.y1 &&
              shiftItem.posY2 > overlapItem.y2;
            const leftBottom =
              shiftItem.posX1 < overlapItem.x1 &&
              shiftItem.posX1 < overlapItem.x2 &&
              shiftItem.posX2 > overlapItem.x1 &&
              shiftItem.posX2 < overlapItem.x2 &&
              shiftItem.posY1 < overlapItem.y1 &&
              shiftItem.posY1 < overlapItem.y2 &&
              shiftItem.posY2 > overlapItem.y1 &&
              shiftItem.posY2 < overlapItem.y2;
            const rightTop =
              shiftItem.posX1 > overlapItem.x1 &&
              shiftItem.posX1 < overlapItem.x2 &&
              shiftItem.posX2 > overlapItem.x1 &&
              shiftItem.posX2 > overlapItem.x2 &&
              shiftItem.posY1 > overlapItem.y1 &&
              shiftItem.posY1 < overlapItem.y2 &&
              shiftItem.posY2 > overlapItem.y1 &&
              shiftItem.posY2 > overlapItem.y2;
            const rightBottom =
              shiftItem.posX1 > overlapItem.x1 &&
              shiftItem.posX1 < overlapItem.x2 &&
              shiftItem.posX2 > overlapItem.x1 &&
              shiftItem.posX2 > overlapItem.x2 &&
              shiftItem.posY1 < overlapItem.y1 &&
              shiftItem.posY1 < overlapItem.y2 &&
              shiftItem.posY2 > overlapItem.y1 &&
              shiftItem.posY2 < overlapItem.y2;

            const leftFull =
              shiftItem.posX1 < overlapItem.x1 &&
              shiftItem.posX1 < overlapItem.x2 &&
              shiftItem.posX2 > overlapItem.x1 &&
              shiftItem.posX2 < overlapItem.x2 &&
              shiftItem.posY1 < overlapItem.y1 &&
              shiftItem.posY1 < overlapItem.y2 &&
              shiftItem.posY2 > overlapItem.y1 &&
              shiftItem.posY2 > overlapItem.y2;
            const topFull =
              shiftItem.posX1 < overlapItem.x1 &&
              shiftItem.posX1 < overlapItem.x2 &&
              shiftItem.posX2 > overlapItem.x1 &&
              shiftItem.posX2 > overlapItem.x2 &&
              shiftItem.posY1 > overlapItem.y1 &&
              shiftItem.posY1 < overlapItem.y2 &&
              shiftItem.posY2 > overlapItem.y1 &&
              shiftItem.posY2 > overlapItem.y2;
            const rightFull =
              shiftItem.posX1 > overlapItem.x1 &&
              shiftItem.posX1 < overlapItem.x2 &&
              shiftItem.posX2 > overlapItem.x1 &&
              shiftItem.posX2 > overlapItem.x2 &&
              shiftItem.posY1 < overlapItem.y1 &&
              shiftItem.posY1 < overlapItem.y2 &&
              shiftItem.posY2 > overlapItem.y1 &&
              shiftItem.posY2 > overlapItem.y2;
            const downFull =
              shiftItem.posX1 < overlapItem.x1 &&
              shiftItem.posX1 < overlapItem.x2 &&
              shiftItem.posX2 > overlapItem.x1 &&
              shiftItem.posX2 > overlapItem.x2 &&
              shiftItem.posY1 < overlapItem.y1 &&
              shiftItem.posY1 < overlapItem.y2 &&
              shiftItem.posY2 > overlapItem.y1 &&
              shiftItem.posY2 < overlapItem.y2;

            const leftInside =
              shiftItem.posX1 < overlapItem.x1 &&
              shiftItem.posX1 < overlapItem.x2 &&
              shiftItem.posX2 > overlapItem.x1 &&
              shiftItem.posX2 < overlapItem.x2 &&
              shiftItem.posY1 > overlapItem.y1 &&
              shiftItem.posY1 < overlapItem.y2 &&
              shiftItem.posY2 > overlapItem.y1 &&
              shiftItem.posY2 < overlapItem.y2;
            const topInside =
              shiftItem.posX1 > overlapItem.x1 &&
              shiftItem.posX1 < overlapItem.x2 &&
              shiftItem.posX2 > overlapItem.x1 &&
              shiftItem.posX2 < overlapItem.x2 &&
              shiftItem.posY1 > overlapItem.y1 &&
              shiftItem.posY1 < overlapItem.y2 &&
              shiftItem.posY2 > overlapItem.y1 &&
              shiftItem.posY2 > overlapItem.y2;
            const rightInside =
              shiftItem.posX1 > overlapItem.x1 &&
              shiftItem.posX1 < overlapItem.x2 &&
              shiftItem.posX2 > overlapItem.x1 &&
              shiftItem.posX2 > overlapItem.x2 &&
              shiftItem.posY1 > overlapItem.y1 &&
              shiftItem.posY1 < overlapItem.y2 &&
              shiftItem.posY2 > overlapItem.y1 &&
              shiftItem.posY2 < overlapItem.y2;
            const downInside =
              shiftItem.posX1 > overlapItem.x1 &&
              shiftItem.posX1 < overlapItem.x2 &&
              shiftItem.posX2 > overlapItem.x1 &&
              shiftItem.posX2 < overlapItem.x2 &&
              shiftItem.posY1 < overlapItem.y1 &&
              shiftItem.posY1 < overlapItem.y2 &&
              shiftItem.posY2 > overlapItem.y1 &&
              shiftItem.posY2 < overlapItem.y2;

            if (
              leftTop ||
              leftBottom ||
              leftFull ||
              leftInside
            ) {
              shiftItem.posX1 = overlapItem.x1 - startHoleX - facingWidth;
              shiftItem.posY1 = overlapItem.y2 - facingHeight;
              shiftItem.posX2 = shiftItem.posX1 + facingWidth;
              shiftItem.posY2 = shiftItem.posY1 + facingHeight;
            }

            if (
              rightTop ||
              rightBottom ||
              rightFull ||
              rightInside
            ) {
              shiftItem.posX1 = overlapItem.x2 + startHoleX;
              shiftItem.posY1 = overlapItem.y2 - facingHeight;
              shiftItem.posX2 = shiftItem.posX1 + facingWidth;
              shiftItem.posY2 = shiftItem.posY1 + facingHeight;
            }

            if (
              topFull ||
              topInside
            ) {
              shiftItem.posY1 = overlapItem.y2 + PEGBOARD_Y_OFFSET;
              shiftItem.posY2 = shiftItem.posY1 + facingHeight;
            }

            if (
              downFull ||
              downInside
            ) {
              shiftItem.posY1 = overlapItem.y1 - PEGBOARD_Y_OFFSET - facingHeight;
              shiftItem.posY2 = shiftItem.posY1 + facingHeight;
            }

            // check if out of the container
            const isShiftItemOutsideDropLayer =
              shiftItem.posX1 < containerLeft ||
              shiftItem.posX2 > containerRight ||
              shiftItem.posY2 > containerTop ||
              shiftItem.posY1 < 0;

            if (!isShiftItemOutsideDropLayer) {
            // check overlap with facings
              const overlapShiftItems = itemsCoordinatesWithoutSelf.filter(item => {
                const left = Math.max(shiftItem.posX1, item.x1);
                const bottom = Math.max(shiftItem.posY1, item.y1);
                const right = Math.min(shiftItem.posX2, item.x2);
                const top = Math.min(shiftItem.posY2, item.y2);

                return right > left && top > bottom;
              });

              if (overlapShiftItems.length === 0) {
              // check overlap with fixtures
                const occupiedShiftFixtures = fixtureHeight.filter(fixture => {
                  const left = Math.max(shiftItem.posX1, fixture.x1);
                  const bottom = Math.max(shiftItem.posY1, fixture.y1);
                  const right = Math.min(shiftItem.posX2, fixture.x2);
                  const top = Math.min(shiftItem.posY2, fixture.y2);

                  return right > left && top > bottom && !fixture.isPegboard;
                });

                if (occupiedShiftFixtures.length === 0) {
                  posX1 = shiftItem.posX1;
                  posX2 = shiftItem.posX2;
                  posY1 = shiftItem.posY1;
                  posY2 = shiftItem.posY2;
                }
              }
            }
          }
        }

        const peghole = pegholes[0];
        const occupiedFixture = occupiedFixtures[0];
        const absolutePegholeX = posX1 + peghole.peghole_x;
        const absolutePegholeY = posY2 - peghole.peghole_y;

        const shiftPegholeX = absolutePegholeX - occupiedFixture.x1 - startHoleX;
        const shiftPegholeY = absolutePegholeY - occupiedFixture.y1 - startHoleY;

        const extremumPegholeX = occupiedFixture.x2 - occupiedFixture.x1 - startHoleX;
        const extremumPegholeY = occupiedFixture.y2 - occupiedFixture.y1 - startHoleY;

        const correctionPegholeX1 = Math.floor(Math.min(shiftPegholeX, extremumPegholeX));
        const correctionPegholeY1 = Math.floor(Math.min(shiftPegholeY, extremumPegholeY));

        const correctionPegholeX2 = Math.ceil(Math.min(shiftPegholeX, extremumPegholeX));
        const correctionPegholeY2 = Math.ceil(Math.min(shiftPegholeY, extremumPegholeY));

        const correctionLeftBottom: Point = {
          x: correctionPegholeX1,
          y: correctionPegholeY1,
        };

        const correctionLeftTop: Point = {
          x: correctionPegholeX1,
          y: correctionPegholeY2,
        };

        const correctionRightBottom: Point = {
          x: correctionPegholeX2,
          y: correctionPegholeY1,
        };

        const correctionRightTop: Point = {
          x: correctionPegholeX2,
          y: correctionPegholeY2,
        };

        const distanceLeftBottom = Math.sqrt(Math.pow(correctionLeftBottom.x - shiftPegholeX, 2) + Math.pow(correctionLeftBottom.y - shiftPegholeY, 2));
        const distanceLeftTop = Math.sqrt(Math.pow(correctionLeftTop.x - shiftPegholeX, 2) + Math.pow(correctionLeftTop.y - shiftPegholeY, 2));
        const distanceRightBottom = Math.sqrt(Math.pow(correctionRightBottom.x - shiftPegholeX, 2) + Math.pow(correctionRightBottom.y - shiftPegholeY, 2));
        const distanceRightTop = Math.sqrt(Math.pow(correctionRightTop.x - shiftPegholeX, 2) + Math.pow(correctionRightTop.y - shiftPegholeY, 2));

        const foundMinDistance = Math.min(distanceLeftBottom, distanceLeftTop, distanceRightBottom, distanceRightTop);

        let minPeghole: Point | null = null;

        switch (foundMinDistance) {
          case distanceLeftBottom:
            minPeghole = correctionLeftBottom;
            break;
          case distanceLeftTop:
            minPeghole = correctionLeftTop;
            break;
          case distanceRightBottom:
            minPeghole = correctionRightBottom;
            break;
          case distanceRightTop:
            minPeghole = correctionRightTop;
            break;
        }

        if (!minPeghole) return acc;

        const shift: Point = {
          x: minPeghole.x - shiftPegholeX,
          y: minPeghole.y - shiftPegholeY,
        };

        const newFacingX = posX1 + shift.x;
        let newFacingY = posY1 + shift.y;

        const pegholeArea = transformPegholes(peghole, width, 1);
        const pegholeY = newFacingY + facingHeight - pegholeArea.centerY;
        const splicer = splicerCoordinates.find(item =>
          round(item.y1, 2) <= round(pegholeY, 2) &&
          round(item.y2, 2) >= round(pegholeY, 2)
        );

        if (splicer) {
          const { y1, y2 } = splicer;
          const roundedY1 = Math.round(y1);
          const roundedY2 = Math.round(y2);
          const roundedCurrentPegholeY = Math.round(pegholeY);
          const isShiftUp = roundedY2 === roundedCurrentPegholeY;
          const isShiftDown = roundedY1 === roundedCurrentPegholeY;

          newFacingY = isShiftUp ? newFacingY + 1 : isShiftDown ? newFacingY - 1 : newFacingY;
        }

        acc.push({
          ...facingGroupsByIds[val.id],
          depth_facings,
          peg_id,
          id: generateUniqueId(),
          x: newFacingX,
          y: newFacingY,
          horizontal_facings: newHorizontalFacings,
          located_fixture_id: occupiedFixtures[0]?.id,
          fixtureType: locatedFixturesIdToType[occupiedFixtures[0]?.id],
          segment: occupiedFixtures[0]?.segment
        });

        return acc;
      }, [] as Array<FacingGroup>);

      if (pegboardFacings.length !== recalculatedPositions.length) {
        return false;
      }

      const newItems = recalculatedPositions.map(item => {
        return {
          x1: item.x,
          x2: item.x + item.packaging.width * item.horizontal_facings,
          y1: item.y,
          y2: item.y + item.packaging.height + eslExtraHeight
        };
      });

      const isBelowBaseFixture = newItems.some(item => item.y1 < PEGBOARD_TEMPLATE.y);

      if (isBelowBaseFixture) {
        return false;
      }

      const { pegboardOverlappingIds, othersOverlappingsIds } = newItems.reduce((acc, item) => {
        const {
          overlappingWithCrossbars,
          overlappingWithOthers
        } = itemsCoordinates.reduce((itemsCollection, i) => {
          const left = Math.max(item.x1, i.x1);
          const bottom = Math.max(item.y1, i.y1);
          const right = Math.min(item.x2, i.x2);
          const top = Math.min(item.y2, i.y2);
          const isOverlapping = right > left && top > bottom;

          if (isOverlapping) {
            if (i.isPeg) {
              itemsCollection.overlappingWithCrossbars.push(i.id);
            } else {
              itemsCollection.overlappingWithOthers.push(i.id);
            }
          }

          return itemsCollection;
        }, {
          overlappingWithCrossbars: [] as string[],
          overlappingWithOthers: [] as string[]
        });

        acc.pegboardOverlappingIds.push(...overlappingWithCrossbars);
        acc.othersOverlappingsIds.push(...overlappingWithOthers);

        return acc;
      }, {
        pegboardOverlappingIds: [] as string[],
        othersOverlappingsIds: [] as string[]
      });

      if (othersOverlappingsIds.length) {
        return false;
      }

      const overlappingWithShelfFixture = isFacingsOverlapWithFixture(newItems, locatedFixtures);

      if (overlappingWithShelfFixture.length) {
        return false;
      }

      const extendedIds = getActiveFacingsFromFacingGroup(
        pegboardOverlappingIds,
        selectedFacingsGroup,
        facingGroupsByIds,
        draggableLeft,
        draggableRight
      );

      const { newFacings, newClipboardFacings: clipboardFacings } = processRemovingFacingWIP1(
        extendedIds,
        facingGroupsByIds,
        fixtureCrossbar,
        fixtureFlatbar,
        fixturePegboard,
        fixtureSpaces,
        locatedFixturesIdToType,
        selectedFixtureType,
        selectedFacingsGroup
      );

      const extendedIdsCollection = Object.keys(extendedIds);

      const newRemainingFacings = isCopy ? [] : Object.keys(selectedFacingsGroup).reduce((acc, val) => {
        if (extendedIdsCollection.includes(val)) {
          return acc;
        }

        const facingGroup = facingGroupsByIds[val];
        const { horizontal_facings: h } = facingGroup;
        const facings = selectedFacingsGroup[val];
        const facingsNumber = facings.length;

        if (facingsNumber === 0 || facingsNumber === h) {
          return acc;
        }

        const newFacingsCollection = processCreatingBoardFacings(
          facingGroup,
          facings,
          fixtureSpaces,
          locatedFixturesIdToType,
          selectedFixtureType,
          fixtureCrossbar,
          fixtureFlatbar,
          fixturePegboard
        );

        acc.push(...newFacingsCollection);

        return acc;
      }, [] as Array<any>);

      facings = [
        ...recalculatedPositions,
        ...newFacings,
        ...(isClipboardType || isWorkbenchType ? [] : newRemainingFacings)
      ];

      removedId = isCopy ? pegboardOverlappingIds : activeItems.concat(pegboardOverlappingIds);
      newClipboardFacings = clipboardFacings.filter(item => uniquePlanogramFacings[item.product_num] === item.horizontal_facings);

      if (isWorkbenchType) {
        removedWorkbenchFacingId = isCopy ? [] : facingsIds;
      }

      if (isClipboardType) {
        const {
          removedClipboardItemIds, newClipboardItems
        } = processRemovingClipbordItems(selectedFacingsGroup, facingGroupsByIds);

        switch (type) {
          case DraggableClipboardFixtureSkuItems.FixtureDefauldItem:
          case DraggableClipboardFixtureSkuItems.FixtureYCapped:
          case DraggableClipboardFixtureSkuItems.FixtureYNested:
          case DraggableClipboardFixtureSkuItems.FixtureZNested: {
            const connectedSkus = processRemovingSkusConnectedToFixture(
              facings,
              clipboardSkuToFacingGroups
            );

            newClipboardFixturesFacings = removedClipboardItemIds.reduce((acc, id) => {
              const facing = facingGroupsByIds[id];
              const selectedFacings = selectedFacingsGroup[id].length;

              if (selectedFacings === facing.horizontal_facings || selectedFacings === 0) return acc;

              const h = facing.horizontal_facings - selectedFacings;

              return acc.concat({
                ...facing,
                id: generateUniqueId(),
                horizontal_facings: h
              });
            }, [] as Array<FacingGroupExtended>);

            removedClipboardId = removedClipboardId.concat(connectedSkus.removedClipboardIds);
            removedClipboardFixturesFacings = removedClipboardItemIds;
            newClipboardFacings = newClipboardFacings.concat(connectedSkus.newClipboardFacings);
          }

            break;

          default: {
            removedClipboardId = removedClipboardItemIds;
            newClipboardFacings = newClipboardFacings.concat(newClipboardItems);
          }
        }
      }

      const alertsData = createAlertEvent({
        warningType: 'move_crossbar_to_peg_warnings',
        newClipboardFacings,
        recalculatedPositions: isPegboard
          ? recalculatedPositions.filter((fg) => fg.fixtureType === FIXTURE_TYPES.PEGBOARD)
          : recalculatedPositions,
        removedClipboardId,
        alerts,
      });

      newAlerts = alertsData.alerts;
      selectAlert = alertsData.selectAlert as WarningData;
      openAlert = alertsData.openAlert;
    }

      break;

    default: {
      if (hasFacingWiderThanSegment(items, facingGroupsByIds, numberOfSegments * SEGMENT_WIDTH)) {
        return false;
      }

      const overlapFixture = fixtureSpaceSelectorByTypes.filter(item => {
        const left = Math.max(draggableLeft, item.x1);
        const bottom = Math.max(draggableBottom, item.y1);
        const top = Math.min(draggableTop, item.y2);
        const right = Math.min(draggableRight, item.x2);

        return right > left && top > bottom;
      }).sort((a, b) => b.y2 - a.y2)[0];

      if (overlapFixture) {
        const diff = Math.abs(overlapFixture.y2 - draggableBottom);

        draggableBottom = draggableBottom + diff;
        draggableTop = draggableTop + diff;
      }

      const fixtures = fixtureSpaces.filter(({ x1, x2, y1, y2 }) => (
        draggableLeft <= x2 && draggableRight >= x1 && draggableTop <= y2 && draggableBottom >= y1
      )).sort((a, b) => a.y2 - b.y2);
      const [fixture] = fixtures;
      const isIdenticalFixtures = fixtures.every(f =>
        f.y1 === fixture.y1 &&
        locatedFixturesById[f.id].type === locatedFixturesById[fixture.id].type &&
        locatedFixturesById[f.id].shelf_slope === locatedFixturesById[fixture.id].shelf_slope
      );

      if (!isIdenticalFixtures) {
        return false;
      }

      const isDisplayOnly = fixtures.some(item => item.isDisplayOnly);

      if (isDisplayOnly) {
        return false;
      }

      const fixtureIdTypeTarget = locatedFixturesIdToType[fixture?.id];
      const canBeAcceptedByFixtureType = acceptedFixtureType[fixtureIdTypeTarget]?.includes(selectedFixtureType);

      if (!canBeAcceptedByFixtureType) {
        return false;
      }

      let recalculatedPositions: Array<FacingGroupExtended> = items.reduce((acc, val, index) => {
        const draggableItem = facingGroupsByIds[val.id];
        const facings = selectedFacingsGroup[val.id];
        const {
          horizontal_facings: h,
          vertical_facings: v,
          cap_nested: capNested,
          capped_horizontal_facings: cappedHorFacing,
          packaging: {
            height,
            nested_height: nH,
            width
          },
          cap_nested: cN,
          capped_vertical_facings: cV,
          cappedPackaging: {
            height: cH
          },
        } = draggableItem;
        const isYCapped = capNested === Nesting.YCapped;
        const isFacingGroupSelected = facings.length === 0 || facings.length === h;
        const newHorizontalFacings = isFacingGroupSelected ? h : facings.length;
        const facingHeight = getFacingHeight(height, v, cN, nH, cH, cV);
        const facingWidth = width * newHorizontalFacings;

        const xPosition = index === 0
          ? draggableLeft
          : acc[index - 1].x + (acc[index - 1].packaging.width * acc[index - 1].horizontal_facings) + HORIZONTAL_SPACING;
        const {
          draggableLeft: posX1,
          draggableRight: posX2,
          draggableBottom: posY1,
          draggableTop: posY2
        } = makeDraggableRect(xPosition, draggableTop, facingWidth, facingHeight);

        const baseFixture = getBaseFixture(fixtureSpaces, 1, posX1, posX2, posY1, posY2);

        const locatedFixture = locatedFixturesById[baseFixture.id];
        const { maxVerticalFacings, maxCappedVerticalFacings } = maxVerticalStack(draggableItem, locatedFixture);

        const yPosition = baseFixture.y1;

        acc.push({
          ...facingGroupsByIds[val.id],
          peg_id: null,
          id: generateUniqueId(),
          x: xPosition,
          y: yPosition,
          horizontal_facings: newHorizontalFacings,
          located_fixture_id: baseFixture?.id,
          fixtureType: locatedFixturesIdToType[baseFixture.id],
          segment: baseFixture?.segment,
          vertical_facings: maxVerticalFacings,
          capped_vertical_facings: maxCappedVerticalFacings,
          capped_horizontal_facings: isYCapped
            ? newHorizontalFacings
            : cappedHorFacing
        });

        return acc;
      }, [] as Array<FacingGroupExtended>);

      let newItems = recalculatedPositions.map(item => {
        const {
          horizontal_facings: h,
          vertical_facings: v,
          packaging: {
            height,
            nested_height: nH,
            width
          },
          cap_nested: cN,
          capped_vertical_facings: cV,
          cappedPackaging: {
            height: cH
          }
        } = item;
        const facingHeight = getFacingHeight(height, v, cN, nH, cH, cV);
        const facingWidth = width * h;

        return {
          id: item.id,
          product_num: item.product_num,
          x1: item.x,
          x2: item.x + facingWidth,
          y1: item.y,
          y2: item.y + facingHeight + eslExtraHeight
        };
      });

      // shift start
      const itemsCoordinatesWithoutSelected = itemsCoordinates.filter(item => !facingsIds.includes(item.id));
      const overlapFacings = itemsCoordinatesWithoutSelected.filter(item => newItems.some(newItem => {
        const facingGroup = facingGroupsByIds[item.id];

        if (facingGroup) {
          const sameProductNum = facingGroup.product_num === newItem.product_num;
          let space = HORIZONTAL_SPACING;

          if (sameProductNum) {
            space = 0;
          }

          const left = Math.max(item.x1 - space, newItem.x1);
          const bottom = Math.max(item.y1, newItem.y1);
          const right = Math.min(item.x2 + space, newItem.x2);
          const top = Math.min(item.y2, newItem.y2);

          return right > left && top > bottom;
        }

        return false;
      }));

      if (overlapFacings.length) {
        overlapFacings.sort((a, b) => {
          const facingGroupA = facingGroupsByIds[a.id];
          const facingGroupB = facingGroupsByIds[b.id];

          return facingGroupA.x - facingGroupB.x;
        });
        const firstOverlapFacing = overlapFacings[0];
        const firstNewItem = newItems[0];
        const lastOverlapFacing = overlapFacings[overlapFacings.length - 1];
        const lastNewItem = newItems[newItems.length - 1];
        let diff = 0;

        // check orientation overlap
        if (firstOverlapFacing.x1 < firstNewItem.x1) {
          let space = HORIZONTAL_SPACING;
          const firstOverlapFacingGroup = facingGroupsByIds[firstOverlapFacing.id];
          const sameProductNum = firstOverlapFacingGroup.product_num === firstNewItem.product_num;

          if (sameProductNum) {
            space = 0;
          }

          diff = Math.abs(firstOverlapFacing.x2 - firstNewItem.x1) + space;
        }

        if (lastOverlapFacing.x2 > lastNewItem.x2) {
          let space = HORIZONTAL_SPACING;
          const lastOverlapFacingGroup = facingGroupsByIds[lastOverlapFacing.id];
          const sameProductNum = lastOverlapFacingGroup.product_num === lastNewItem.product_num;

          if (sameProductNum) {
            space = 0;
          }

          diff = -(Math.abs(firstOverlapFacing.x1 - lastNewItem.x2)) - space;
        }

        if (firstOverlapFacing.x1 < firstNewItem.x1 && lastOverlapFacing.x2 > lastNewItem.x2) {
          diff = 0;
        }

        if (diff !== 0) {
        // set new shifted position
          const newItemsShift = newItems.map(item => ({
            ...item,
            x1: item.x1 + diff,
            x2: item.x2 + diff,
          }));

          // check is out not of boundary
          const firstNewItemShift = newItemsShift[0];
          const lastNewItemShift = newItemsShift[newItems.length - 1];

          if (firstNewItemShift.x1 >= 0 && lastNewItemShift.x2 <= rightBoundary) {
          // check hanging product
            const overlapingFixturesNewItems = fixtureSpaces.filter(({ x1, x2, y1, y2 }) => (
              newItemsShift.some(item => {
                const left = Math.max(item.x1, x1);
                const bottom = Math.max(item.y1, y1);
                const right = Math.min(item.x2, x2);
                const top = Math.min(item.y2, y2);
                const isOverlapping = right > left && top > bottom;

                return isOverlapping;
              })
            ));
            const isIdenticalY = new Set(overlapingFixturesNewItems.map(f => f.y1)).size === 1;
            const isIdenticalType = new Set(overlapingFixturesNewItems.map(f => locatedFixturesById[f.id].type)).size === 1;

            if (isIdenticalY && isIdenticalType) {
            // check is overlap with Facings
              const overlapFacingsShift = itemsCoordinatesWithoutSelected.some(item => newItemsShift.some(newItem => {
                const left = Math.max(item.x1, newItem.x1);
                const bottom = Math.max(item.y1, newItem.y1);
                const right = Math.min(item.x2, newItem.x2);
                const top = Math.min(item.y2, newItem.y2);

                return right > left && top > bottom;
              }));

              if (!overlapFacingsShift) {
              // check is overlap with Fixtures
                const overlapFixtureShift = fixtureSpaceSelectorByTypes.some(item => newItemsShift.some(newItem => {
                  const left = Math.max(newItem.x1, item.x1);
                  const bottom = Math.max(newItem.y1, item.y1);
                  const right = Math.min(newItem.x2, item.x2);
                  const top = Math.min(newItem.y2, item.y2);

                  return right > left && top > bottom;
                }));

                if (!overlapFixtureShift) {
                  newItems = [...newItemsShift];
                  recalculatedPositions = recalculatedPositions.map(item => ({
                    ...item,
                    x: item.x + diff,
                  }));
                }
              }
            }
          }
        }
      }
      // shift end

      const { activeOverlappingIds, othersOverlappingsIds } = newItems.reduce((acc, item) => {
        const {
          activeOverlappingIds,
          overlappingWithOthers
        } = itemsCoordinates.reduce((itemsCollection, i) => {
        // if (facingsIds.includes(i.id)) {
        //   return itemsCollection
        // }
          const left = Math.max(item.x1, i.x1);
          const bottom = Math.max(item.y1, i.y1);
          const right = Math.min(item.x2, i.x2);
          const top = Math.min(item.y2, i.y2);
          const isOverlapping = right > left && top > bottom;

          if (isOverlapping) {
            if (i.isPeg || i.isSpecialty) {
              itemsCollection.overlappingWithOthers.push(i.id);
            } else {
              itemsCollection.activeOverlappingIds.push(i.id);
            }
          }

          return itemsCollection;
        }, {
          activeOverlappingIds: [] as string[],
          overlappingWithOthers: [] as string[]
        });

        acc.activeOverlappingIds.push(...activeOverlappingIds);
        acc.othersOverlappingsIds.push(...overlappingWithOthers);

        return acc;
      }, {
        activeOverlappingIds: [] as string[],
        othersOverlappingsIds: [] as string[]
      });

      if (othersOverlappingsIds.length) {
        return false;
      }

      const extendedIds = getActiveFacingsFromFacingGroup(
        activeOverlappingIds,
        selectedFacingsGroup,
        facingGroupsByIds,
        draggableLeft,
        draggableRight,
        isCopy
      );

      const extendedIdsCollection = Object.keys(extendedIds);

      const { newFacings, newClipboardFacings: clipboardFacings } = processRemovingFacingWIP1(
        extendedIds,
        facingGroupsByIds,
        fixtureCrossbar,
        fixtureFlatbar,
        fixturePegboard,
        fixtureSpaces,
        locatedFixturesIdToType,
        selectedFixtureType,
        selectedFacingsGroup
      );

      const newRemainingFacings = isCopy ? [] : Object.keys(selectedFacingsGroup).reduce((acc, val) => {
        if (extendedIdsCollection.includes(val)) {
          return acc;
        }

        const facingGroup = facingGroupsByIds[val];
        const { horizontal_facings: h } = facingGroup;
        const facings = selectedFacingsGroup[val];
        const facingsNumber = facings.length;

        if (facingsNumber === 0 || facingsNumber === h) {
          return acc;
        }

        const newFacingsCollection = processCreatingBoardFacings(
          facingGroup,
          facings,
          fixtureSpaces,
          locatedFixturesIdToType,
          selectedFixtureType,
          fixtureCrossbar,
          fixtureFlatbar,
          fixturePegboard
        );

        acc.push(...newFacingsCollection);

        return acc;
      }, [] as Array<FacingGroupExtended>);

      facings = [
        ...recalculatedPositions,
        ...newFacings,
        ...(isClipboardType || isWorkbenchType ? [] : newRemainingFacings),
      ];

      // check locatedfixture depth
      const facingOutOfLocatedFixtureDepth = facings.some(f => {
        const locatedFixture = locatedFixturesById[f.located_fixture_id];

        if (locatedFixture) {
          const gapDepthShelf = locatedFixture.type === FIXTURE_TYPES.SHELF ? 1 : 0;

          return f.packaging.depth > locatedFixture.depth + gapDepthShelf;
        }

        return true;
      });

      if (facingOutOfLocatedFixtureDepth) {
        return false;
      }

      removedId = isCopy ? activeOverlappingIds : (isClipboardType || isWorkbenchType ? [] : activeItems).concat(activeOverlappingIds);
      newClipboardFacings = filterByUniqueSku(clipboardFacings, uniquePlanogramFacings);

      if (isWorkbenchType) {
        removedWorkbenchFacingId = isCopy ? [] : facingsIds;
        newWorkbenchFacings = newRemainingFacings;
      }

      if (isClipboardType) {
        const {
          removedClipboardItemIds, newClipboardItems
        } = processRemovingClipbordItems(selectedFacingsGroup, facingGroupsByIds);

        switch (type) {
          case DraggableClipboardFixtureSkuItems.FixtureDefauldItem:
          case DraggableClipboardFixtureSkuItems.FixtureYCapped:
          case DraggableClipboardFixtureSkuItems.FixtureYNested:
          case DraggableClipboardFixtureSkuItems.FixtureZNested: {

            const connectedSkus = processRemovingSkusConnectedToFixture(
              facings,
              clipboardSkuToFacingGroups
            );

            newClipboardFixturesFacings = removedClipboardItemIds.reduce((acc, id) => {
              const facing = facingGroupsByIds[id];
              const selectedFacings = selectedFacingsGroup[id].length;

              if (selectedFacings === facing.horizontal_facings || selectedFacings === 0) return acc;

              const h = facing.horizontal_facings - selectedFacings;
              const isYCapped = facing.cap_nested === Nesting.YCapped;

              return acc.concat({
                ...facing,
                id: generateUniqueId(),
                horizontal_facings: h,
                capped_horizontal_facings: isYCapped
                  ? h
                  : facing.capped_horizontal_facings
              });
            }, [] as Array<FacingGroupExtended>);

            removedClipboardId = removedClipboardId.concat(connectedSkus.removedClipboardIds);
            removedClipboardFixturesFacings = removedClipboardItemIds;
            newClipboardFacings = newClipboardFacings.concat(connectedSkus.newClipboardFacings);
          }

            break;

          default: {
            removedClipboardId = removedClipboardItemIds;
            newClipboardFacings = newClipboardFacings.concat(newClipboardItems);
          }
        }
      }

      const alertsData = createAlertEvent({
        warningType: isPegboard
          ? 'move_crossbar_to_peg_warnings'
          : 'move_basket_to_shelf_warnings',
        newClipboardFacings,
        recalculatedPositions: isShelf
          ? recalculatedPositions.filter((fg) => fg.fixtureType === FIXTURE_TYPES.SHELF)
          : recalculatedPositions,
        newRemainingFacings,
        newFacings,
        removedClipboardId,
        alerts,
      });

      newAlerts = alertsData.alerts;
      selectAlert = alertsData.selectAlert as WarningData;
      openAlert = alertsData.openAlert;
    }
  }

  return {
    ...historyStepItem,
    newFacings: facings,
    removedId,
    removedClipboardId,
    newClipboardFacings,
    alerts: newAlerts,
    openAlert,
    selectAlert,
    removedClipboardFixturesFacings,
    newClipboardFixturesFacings,
    removedWorkbenchFacingId,
    newWorkbenchFacings,
    incompatiblePegSkus: [],
  };
};

export const processDroppingClipboardItems = (
  collectionIds: Record<string, Array<number>>,
  facingGroupsByIds: Record<string, FacingGroupExtended>,
  uniquePlanogramFacings: Record<string, number>,
  locatedFixturesIdToType: Record<string, FixtureType>,
  fixturesCrossbar: FixtureSpace[],
  fixturesFlatbar: FixtureSpace[],
  fixturesPegboard: FixtureSpace[],
  fixtureSpaces: FixtureSpace[],
  selectedFixtureType: FixtureType | string,
) => {
  const facings = processRemovingFacingWIP(
    collectionIds,
    facingGroupsByIds,
    fixturesCrossbar,
    fixturesFlatbar,
    fixturesPegboard,
    fixtureSpaces,
    locatedFixturesIdToType,
    selectedFixtureType,
    uniquePlanogramFacings
  );

  return facings;
};

export const getAllPositionInSequence = (
  dataSet: Record<string, number[]>,
  direction: Direction
): Record<string, number[]> => {
  const result: Record<string, number[]> = {};

  for (const key in dataSet) {
    // eslint-disable-next-line
    if (dataSet.hasOwnProperty(key)) {
      const values = dataSet[key];
      const sequence: Array<number> = [];

      // If left pick all indexes sequence from left
      if (direction === 'left') {
        let prevIndex: null | number = null;

        for (const index of values) {
          if (prevIndex === null) {
            sequence.push(index);
            prevIndex = index;
          } else if (prevIndex + 1 === index) {
            sequence.push(index);
            prevIndex = index;
          } else {
            break;
          }
        }
      }

      // If right pick all indexes sequence from right
      if (direction === 'right') {
        let prevIndex: null | number = null;

        for (const index of values.reverse()) {
          if (prevIndex === null) {
            sequence.push(index);
            prevIndex = index;
          } else if (prevIndex - 1 === index) {
            sequence.push(index);
            prevIndex = index;
          } else {
            break;
          }
        }
      }

      result[key] = direction === 'left' ? sequence : sequence.reverse();
    }
  }

  return result;
};

export const processFacingGroupSplitting = (
  facingsByIds: Record<string, FacingGroupExtended>,
  selectedFacingEntities: Record<string, Array<number>>,
  direction: Direction,
  newFacing: FacingGroup,
  shiftValue: number
) => {

  const {
    id,
    located_fixture_id,
    fixtureType,
    segment,
    vertical_facings,
    capped_vertical_facings,
    capped_horizontal_facings
  } = newFacing;
  const moveableSelectedFacingEntities = getAllPositionInSequence(selectedFacingEntities, direction);
  const facings = Object.keys(moveableSelectedFacingEntities).reduce((acc, val) => {
    const facingGroup = {
      ...facingsByIds[val],
      id,
      located_fixture_id,
      fixtureType,
      segment,
      vertical_facings,
      capped_vertical_facings,
      capped_horizontal_facings
    };
    const horizontalFacings = facingGroup.horizontal_facings;
    const facings = new Array(horizontalFacings).fill(0).map((_v: number, i: number) => i);

    if (!moveableSelectedFacingEntities[val]) {
      return acc;
    }

    const selectedFacings = moveableSelectedFacingEntities[val];

    // If all facings selected for a facing group just return that we new x position
    if (selectedFacings.length === 0 || horizontalFacings === selectedFacings.length) {
      const newX = direction === 'left' ? facingGroup.x - shiftValue : facingGroup.x + shiftValue;

      acc.newFacings.push(...[{ ...facingGroup, x: newX }]);

      return acc;
    }

    const uniqueNumber = facings.filter(u => selectedFacings.indexOf(u) === -1).length;

    if (!uniqueNumber) {
      return acc;
    }

    // New Facings which stay in the POG
    const newFacingGroup = processCreatingFacingItem(
      facingGroup,
      uniqueNumber
    );

    // Facings which user trying to move
    const remainingFacings = processCreatingFacingItem(
      facingGroup,
      horizontalFacings - uniqueNumber
    );

    if (direction === 'right') {
      remainingFacings.x = newFacingGroup.x + (newFacingGroup.packaging.width * newFacingGroup.horizontal_facings) + shiftValue;
    } else {
      remainingFacings.x = newFacingGroup.x - shiftValue;
      newFacingGroup.x = newFacingGroup.x + (remainingFacings.packaging.width * remainingFacings.horizontal_facings);
    }

    acc.newFacings.push(...[newFacingGroup]);
    acc.remainingFacings.push(...[remainingFacings]);

    return acc;
  }, {
    newFacings: [] as Array<FacingGroupExtended>,
    remainingFacings: [] as Array<FacingGroupExtended>
  });

  return facings;
};

export const processRemovingFacingWIP1 = (
  collectionIds: Record<string, Array<number>>,
  facingsByIds: Record<string, FacingGroupExtended>,
  fixturesCrossbar: FixtureSpace[],
  fixturesFlatbar: FixtureSpace[],
  fixturesPegboard: FixtureSpace[],
  fixtureSpaces: FixtureSpace[],
  locatedFixturesIdToType: Record<string, FixtureType>,
  selectedFixtureType: FixtureType | string,
  selectedFacingEntities: Record<string, Array<number>>,
) => {
  const facings = Object.keys(collectionIds).reduce((acc, val) => {
    const facingGroup = facingsByIds[val];
    const facings = collectionIds[val];
    const facingsNumber = facings.length;
    const horizontalFacings = facingGroup.horizontal_facings;
    const selectedFacings = selectedFacingEntities[val];

    if (selectedFacingEntities[val]) {
      if (selectedFacings.length === 0 || horizontalFacings === selectedFacings.length) {
        return acc;
      }

      const uniqueNumber = facings.filter(u => selectedFacings.indexOf(u) === -1).length;

      if (uniqueNumber) {
        const newClipboardItem = processCreatingClipboardItem(facingGroup, uniqueNumber);

        acc.newClipboardFacings.push(newClipboardItem);
      }

      const newFacingItem = processCreatingBoardFacings(
        facingGroup,
        facings,
        fixtureSpaces,
        locatedFixturesIdToType,
        selectedFixtureType,
        fixturesCrossbar,
        fixturesFlatbar,
        fixturesPegboard
      );

      acc.newFacings.push(...newFacingItem);

      return acc;
    }

    if (facingsNumber === 0 || horizontalFacings === facingsNumber) {
      const newClipboardItem = processCreatingClipboardItem(facingGroup, horizontalFacings);

      acc.newClipboardFacings.push(newClipboardItem);

      return acc;
    }

    const newFacingItem = processCreatingBoardFacings(
      facingGroup,
      facings,
      fixtureSpaces,
      locatedFixturesIdToType,
      selectedFixtureType,
      fixturesCrossbar,
      fixturesFlatbar,
      fixturesPegboard
    );

    const newClipboardItem = processCreatingClipboardItem(facingGroup, facingsNumber);

    acc.newClipboardFacings.push(newClipboardItem);
    acc.newFacings.push(...newFacingItem);

    return acc;
  }, {
    newFacings: [] as Array<FacingGroupExtended>,
    newClipboardFacings: [] as Array<FacingGroupExtended>
  });

  return facings;
};

export const groupClipboardFacings = (
  facings: Array<FacingGroupExtended>,
  clipboardFacings: Array<FacingGroupExtended>,
  removedClipboard: Array<string>,
) => {
  const groupClipboard = facings.reduce<Record<string, FacingGroupExtended>>((acc, facing) => {
    return {
      ...acc,
      [facing.product_num]: {
        ...facing,
        id: generateUniqueId(),
        'vertical_facings': acc[facing.product_num]
          ? Math.min(acc[facing.product_num].vertical_facings, facing.vertical_facings)
          : facing.vertical_facings,
        'horizontal_facings': acc[facing.product_num]
          ? acc[facing.product_num]?.horizontal_facings + facing.horizontal_facings
          : facing.horizontal_facings,
        'depth_facings': acc[facing.product_num]
          ? Math.min(acc[facing.product_num].depth_facings, facing.depth_facings)
          : facing.depth_facings,
        'capped_horizontal_facings': acc[facing.product_num]
          ? acc[facing.product_num]?.horizontal_facings + facing.horizontal_facings
          : facing.horizontal_facings,
        'capped_vertical_facings': acc[facing.product_num]
          ? Math.min(acc[facing.product_num].capped_vertical_facings, facing.capped_vertical_facings)
          : facing.capped_vertical_facings,
        'capped_depth_facings': acc[facing.product_num]
          ? Math.min(acc[facing.product_num].capped_depth_facings, facing.capped_depth_facings)
          : facing.capped_depth_facings,
      }
    };
  }, {});

  const groupClipboardArray: Array<FacingGroupExtended> = Object.values(groupClipboard);
  const clipboardFacingsWithoutRemoved = clipboardFacings.filter(facing => !removedClipboard.includes(facing.id));

  const { newClipboardFacings, removedClipboardId } = groupClipboardArray

    .reduce((acc, facing) => {
      const findClipboardFacing = clipboardFacingsWithoutRemoved.find(
        clipboardFecing => clipboardFecing.product_num === facing.product_num
      );

      if (findClipboardFacing) {
        acc.removedClipboardId.push(findClipboardFacing.id);
        acc.newClipboardFacings.push({
          ...findClipboardFacing,
          id: generateUniqueId(),
          'vertical_facings': Math.min(findClipboardFacing.vertical_facings, facing.vertical_facings),
          'horizontal_facings': findClipboardFacing.horizontal_facings + facing.horizontal_facings,
          'depth_facings': Math.min(findClipboardFacing.depth_facings, facing.depth_facings),
          'capped_horizontal_facings': findClipboardFacing.horizontal_facings + facing.horizontal_facings,
          'capped_vertical_facings': Math.min(findClipboardFacing.capped_vertical_facings, facing.capped_vertical_facings),
          'capped_depth_facings': Math.min(findClipboardFacing.capped_depth_facings, facing.capped_depth_facings),
        });
      } else {
        acc.newClipboardFacings.push(facing);
      }

      return acc;
    }, {
      newClipboardFacings: [] as Array<FacingGroupExtended>,
      removedClipboardId: [] as Array<string>,
    }
    );

  return {
    newClipboardFacings,
    removedClipboardId
  };
};

export const processRemovingFacingWIP = (
  collectionIds: Record<string, Array<number>>,
  facingsByIds: Record<string, FacingGroupExtended>,
  fixturesCrossbar: FixtureSpace[],
  fixturesFlatbar: FixtureSpace[],
  fixturesPegboard: FixtureSpace[],
  fixtureSpaces: FixtureSpace[],
  locatedFixturesIdToType: Record<string, FixtureType>,
  selectedFixtureType: FixtureType | string,
  uniquePlanogramFacings: Record<string, number>
) => {
  const { newFacings, newClipboardFacings } = Object.keys(collectionIds).reduce((acc, val) => {
    const facingGroup = facingsByIds[val];
    const facings = collectionIds[val];
    const facingsNumber = facings.length;
    const horizontalFacings = facingGroup.horizontal_facings;

    if (facingsNumber === 0 || horizontalFacings === facingsNumber) {
      const newClipboardItem = processCreatingClipboardItem(
        facingGroup,
        horizontalFacings
      );

      acc.newClipboardFacings.push(newClipboardItem);
    } else {
      const newFacingItem = processCreatingBoardFacings(
        facingGroup,
        facings,
        fixtureSpaces,
        locatedFixturesIdToType,
        selectedFixtureType,
        fixturesCrossbar,
        fixturesFlatbar,
        fixturesPegboard
      );
      const newClipboardItem = processCreatingClipboardItem(
        facingGroup,
        facingsNumber
      );

      acc.newFacings.push(...newFacingItem);
      acc.newClipboardFacings.push(newClipboardItem);
    }

    return acc;
  }, {
    newFacings: [] as Array<FacingGroupExtended>,
    newClipboardFacings: [] as Array<FacingGroupExtended>,
  });

  const clipboardFacings = filterByUniqueSku(newClipboardFacings, uniquePlanogramFacings);

  return {
    newFacings,
    newClipboardFacings: clipboardFacings
  };
};

export const processCreatingClipboardItem = (
  facingGroup: FacingGroupExtended,
  numberOfSelectedFacings: number
) => {
  const isYCapped = facingGroup.cap_nested === Nesting.YCapped;
  const clipboardFacing: FacingGroupExtended = {
    ...facingGroup,
    id: generateUniqueId(),
    horizontal_facings: numberOfSelectedFacings,
    capped_horizontal_facings: isYCapped
      ? numberOfSelectedFacings
      : facingGroup.capped_horizontal_facings
  };

  return clipboardFacing;
};

export const processCreatingFacingItem = (
  facingGroup: FacingGroupExtended,
  numberOfSelectedFacings: number
) => {
  const isYCapped = facingGroup.cap_nested === Nesting.YCapped;
  const facgingGroup: FacingGroupExtended = {
    ...facingGroup,
    id: generateUniqueId(),
    horizontal_facings: numberOfSelectedFacings,
    capped_horizontal_facings: isYCapped
      ? numberOfSelectedFacings
      : facingGroup.capped_horizontal_facings
  };

  return facgingGroup;
};

export const processCreatingBoardFacings = (
  facingGroup: FacingGroupExtended,
  selectedFacing: Array<number>,
  fixtureSpaces: FixtureSpace[],
  locatedFixturesIdToType: Record<string, FixtureType>,
  selectedFixtureType: FixtureType | string,
  fixturesCrossbar: FixtureSpace[],
  fixturesFlatbar: FixtureSpace[],
  fixturesPegboard: FixtureSpace[]
) => {
  const {
    x,
    y,
    vertical_facings: v,
    horizontal_facings: h,
    packaging: {
      height,
      nested_height: nestedHeight,
      width
    },
    cap_nested: capNested,
    capped_vertical_facings: cappedVertical,
    capped_horizontal_facings: cappedHorFacing,
    cappedPackaging: {
      height: cappedHeight
    },
  } = facingGroup;
  const newFacings = getNewFacingsByGroup(h, selectedFacing);
  const newFacingsCollection = newFacings.reduce((collection, facingsGroup) => {
    const startPosition = facingsGroup[0];
    const newHorizontalFacings = facingsGroup.length;
    const isYCapped = facingGroup.cap_nested === Nesting.YCapped;
    const facingHeight = getFacingHeight(
      height,
      v,
      capNested,
      nestedHeight,
      cappedHeight,
      cappedVertical
    );
    const facingWidth = width * newHorizontalFacings;
    const xPosition = x + width * startPosition;
    const { x1, x2, y1, y2 } = makeRect(xPosition, y, facingWidth, facingHeight);

    const isCrossbarFacing = FIXTURE_TYPES.CROSSBAR === selectedFixtureType;
    const isFlatbarFacing = FIXTURE_TYPES.FLATBAR === selectedFixtureType;
    const isPegboardFacing = FIXTURE_TYPES.PEGBOARD === selectedFixtureType;

    let baseFixture = null;

    if (isCrossbarFacing) {
      baseFixture = getFixtureCrossbar(fixturesCrossbar, x1, x2, y1, y2);
    } else if (isFlatbarFacing) {
      baseFixture = getFixtureFlatbar(fixturesFlatbar, x1, x2, y1, y2);
    } else if (isPegboardFacing) {
      baseFixture = getFixturePegboard(fixturesPegboard, x1, x2, y1, y2);
    } else {
      baseFixture = getFixture(fixtureSpaces, x1, x2, y1, y2);
    }

    collection.push({
      ...facingGroup,
      id: generateUniqueId(),
      horizontal_facings: newHorizontalFacings,
      capped_horizontal_facings: isYCapped
        ? newHorizontalFacings
        : cappedHorFacing,
      x: xPosition,
      located_fixture_id: baseFixture?.id,
      fixtureType: locatedFixturesIdToType?.[baseFixture?.id],
      segment: baseFixture?.segment
    });

    return collection;
  }, [] as Array<FacingGroupExtended>);

  return newFacingsCollection;
};

export const detectSelectionByContextMenu = (
  selectedIds: Record<string, Array<number>>,
  contextId: string,
  h: number
) => {
  const selectedIdsCollection = Object.keys(selectedIds);
  const isContextIdInSelection = selectedIds[contextId];
  let isMultipleSelected = false;
  let isFacingGroupSelected = false;
  let isMultipleInFacingGroupSelected = false;
  let horizontalFacings = 1;
  let leftOffsetByFacings = 0;

  if (selectedIdsCollection.length === 1 && isContextIdInSelection) {
    const facings = selectedIds[contextId];
    const isSingleSelected = facings.length === 1;

    isFacingGroupSelected = facings.length === h || facings.length === 0;
    isMultipleInFacingGroupSelected = !isFacingGroupSelected && facings.length > 1;
    leftOffsetByFacings = isSingleSelected ? facings[0] : 0;
    horizontalFacings = isSingleSelected ? 1 : h;
  } else if (selectedIdsCollection.length > 1 && isContextIdInSelection) {
    isMultipleSelected = true;
  } else {
    isFacingGroupSelected = true;
    horizontalFacings = h;
  }

  return {
    isMultipleSelected: isMultipleSelected || isMultipleInFacingGroupSelected,
    isFacingGroupSelected,
    leftOffsetByFacings,
    horizontalFacings
  };
};

export const getContextMenuType = (
  selectedFacingEntities: Record<string, Array<number>>,
  contextId: string,
  h: number
) => {
  const facingsSelected = selectedFacingEntities[contextId];
  const selectedFacings = Object.keys(selectedFacingEntities);
  const isFacingsSelected = selectedFacings.length === 0;
  let isExtendedMenu = false;
  let isMultipleSelectedMenu = false;
  let isFacingGroupTarget = false;

  if (isFacingsSelected) {
    isExtendedMenu = true;
    isFacingGroupTarget = true;
  } else {
    isExtendedMenu = selectedFacings.length === 1 && (
      facingsSelected.length === 0 || facingsSelected.length === h
    );
    isMultipleSelectedMenu = selectedFacings.length > 1 || (
      selectedFacings.length === 1 && facingsSelected.length > 1
    );
    isFacingGroupTarget = true;
  }

  return {
    isExtendedMenu,
    isMultipleSelectedMenu,
    isFacingGroupTarget
  };
};

export const getDOMRefs = () => {
  const boardDraggingItem = document.getElementById('custom-dragged-board-item-1');
  const clipboardDraggingItem = document.getElementById('custom-dragged-clipboard-item-1');
  const boardDraggingItem2 = document.getElementById('custom-dragged-board-item-2');
  const clipboardDraggingItem2 = document.getElementById('custom-dragged-clipboard-item-2');

  return {
    boardDraggingItem,
    clipboardDraggingItem,
    boardDraggingItem2,
    clipboardDraggingItem2
  };
};

export const processRemovingClipbordItems = (
  selectedFacingsGroup: Record<string, Array<number>>,
  facingGroupsByIds: Record<string, FacingGroupExtended>
) => Object.keys(selectedFacingsGroup).reduce((acc, id) => {
  const facing = facingGroupsByIds[id];
  const { horizontal_facings: h } = facing;
  const selectedFacings = selectedFacingsGroup[id].length;
  const isAllFacingGroupSelected = selectedFacings === 0 || selectedFacings === h;

  acc.removedClipboardItemIds.push(id);

  if (!isAllFacingGroupSelected) {
    const isYCapped = facing.cap_nested === Nesting.YCapped;
    const newHorFacings = h - selectedFacings;

    acc.newClipboardItems.push({
      ...facing,
      id: generateUniqueId(),
      horizontal_facings: newHorFacings,
      capped_horizontal_facings: isYCapped
        ? newHorFacings
        : facing.capped_horizontal_facings
    });
  }

  return acc;
}, {
  removedClipboardItemIds: [] as string[],
  newClipboardItems: [] as Array<FacingGroupExtended>
});

export const isItemsOverlap = (
  item: Pick<ItemCoordinates, 'x1' | 'y1' | 'x2' | 'y2'>,
  draggableItem: DraggableItemCoordinates
) => {
  const {
    draggableLeft, draggableBottom, draggableRight, draggableTop
  } = draggableItem;
  const left = Math.max(item.x1, draggableLeft);
  const bottom = Math.max(item.y1, draggableBottom);
  const right = Math.min(item.x2, draggableRight);
  const top = Math.min(item.y2, draggableTop);

  return right > left && top > bottom;
};

export const getOccupiedFixtures = (
  fixturesSpaces: FixtureSpace[],
  draggableItem: DraggableItemCoordinates
) => {
  const {
    draggableLeft, draggableBottom, draggableRight, draggableTop
  } = draggableItem;
  const occupiedFixtures = fixturesSpaces.filter(({ x1, x2, y1, y2 }) => (
    draggableLeft <= x2 && draggableRight >= x1 && draggableTop <= y2 && draggableBottom >= y1
  ));

  return occupiedFixtures;
};

export const getSegmentSpaces = (
  segmentSpaces: SegmentSpaces[],
  draggableItem: DraggableItemCoordinates,
  scale: number = 1
) => {
  const { draggableLeft, draggableRight } = draggableItem;

  return segmentSpaces.filter(s => draggableLeft * scale <= s.x2 && draggableRight * scale >= s.x1);
};

export const isFixturesOnOneLevel = (fixturesSpaces: FixtureSpace[]) => fixturesSpaces.every(f =>
  f.y1 === fixturesSpaces[0].y1 && f.fixtureId === fixturesSpaces[0].fixtureId
);

export const hasFacingWiderThanSegment = (
  items: Array<{ id: string }>,
  facingGroupsByIds: Record<string, FacingGroupExtended>,
  segmentWidth: number = SEGMENT_WIDTH,
) => items.some(item => (facingGroupsByIds[item.id]?.packaging?.width || 0) > segmentWidth);

export const getLeadInFontSize = (
  height: number,
  initialFontSize: number = 14,
  threshold: number = 2
): CSSProperties => {
  const fontSize = height > initialFontSize + threshold
    ? initialFontSize
    : height - 2 * threshold;

  return { fontSize };
};

enum OperationType {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
  UPDATE = 'UPDATE',
}

const isFacingHasAlert = (
  alerts: WarningData[],
  f: FacingGroupExtended,
  warningType: 'move_basket_to_shelf_warnings' | 'move_crossbar_to_peg_warnings' | 'fd_dwo_bincap_warnings',
  isClipboardFacing?: boolean
) => {
  if (!alert) {
    return false;
  }

  const alertExistIndex = alerts?.findIndex((a) => a.warningType === warningType);

  if (alertExistIndex === -1) {
    return false;
  }

  if (alertExistIndex > -1) {
    if (warningType === 'fd_dwo_bincap_warnings') {
      const alertExist = alerts[alertExistIndex];
      const alertExistData = (alertExist.data as FdDwoBincapWarnings[]) || [];
      const alertsFound = alertExistData.filter((a: FdDwoBincapWarnings) => {
        return f.x === a.x && a.y === f.y;
      });

      return alertsFound.length ? true : false;
    }

    const alertExist = alerts[alertExistIndex];
    const alertExistData = (alertExist.data as MoveBasketToSelfWarnings[] | MoveCrossbarToPegWarnings[]) || [];
    const alertsFound = alertExistData.filter((a: MoveBasketToSelfWarnings | MoveCrossbarToPegWarnings) => {
      return isClipboardFacing ? f.x === a.x && a.y === f.y && a.to_fixture_type === '' : f.x === a.x && a.y === f.y;
    });

    return alertsFound.length ? true : false;
  }

  return false;
};

const generateAlertObj = (f: FacingGroupExtended, isClipboard: boolean) => {
  const fromFixtureType = f.fixtureType === FIXTURE_TYPES.SHELF ? FIXTURE_TYPES.BASKET : FIXTURE_TYPES.CROSSBAR;
  const toFixtureType = f.fixtureType === FIXTURE_TYPES.SHELF ? FIXTURE_TYPES.SHELF : FIXTURE_TYPES.PEGBOARD;

  return {
    product_num: f.product_num,
    x: f.x,
    y: f.y,
    from_fixture_type: fromFixtureType,
    to_fixture_type: isClipboard ? toFixtureType : '',
  };
};

const processAlerts = (
  alerts: WarningsData,
  type: 'move_basket_to_shelf_warnings' | 'move_crossbar_to_peg_warnings',
  facing: MoveBasketToSelfWarnings | MoveCrossbarToPegWarnings,
  operation: OperationType
) => {
  const alertExistIndex = alerts?.findIndex((a) => a.warningType === type);
  let newAlert = {
    selected: false,
    warningType: type,
    data: [],
  } as WarningData;

  const isFacingAlreadyExist = (
    data: Array<MoveBasketToSelfWarnings | MoveCrossbarToPegWarnings>,
    facing: MoveBasketToSelfWarnings | MoveCrossbarToPegWarnings
  ) => {
    const exist = data.findIndex(
      (d) =>
        d.x === facing.x &&
        d.y === facing.y &&
        d.product_num === facing.product_num &&
        d.from_fixture_type === facing.from_fixture_type &&
        d.to_fixture_type === facing.to_fixture_type
    );

    return exist > -1;
  };

  const insertItemInArray = (
    arr: Array<MoveBasketToSelfWarnings | MoveCrossbarToPegWarnings>,
    index: number,
    item: MoveBasketToSelfWarnings | MoveCrossbarToPegWarnings
  ): Array<MoveBasketToSelfWarnings | MoveCrossbarToPegWarnings> => {
    const newAlerts: Partial<Array<MoveBasketToSelfWarnings | MoveCrossbarToPegWarnings>> = [];

    arr.forEach((a, i) => {
      newAlerts.push(i === index ? item : a);
    });

    return newAlerts as Array<MoveBasketToSelfWarnings | MoveCrossbarToPegWarnings>;
  };

  if (alertExistIndex > -1) {
    newAlert = alerts[alertExistIndex];
  }

  let data = (newAlert.data as Array<MoveBasketToSelfWarnings | MoveCrossbarToPegWarnings>) || [];

  if (operation === OperationType.ADD && !isFacingAlreadyExist(data, facing)) {
    data = [facing, ...data];
  }

  if (operation === OperationType.REMOVE) {
    const alertDataIndex = data.findIndex(
      (d) => d.x === facing.x && d.y === facing.y && d.product_num === facing.product_num
    );

    if (alertDataIndex > -1) {
      data = data.splice(alertDataIndex, 1, facing);
    }
  }

  if (operation === OperationType.UPDATE) {
    const alertDataIndex = data.findIndex(
      (d) => d.x === facing.x && d.y === facing.y && d.product_num === facing.product_num
    );

    if (!isFacingAlreadyExist(data, facing)) {
      if (alertDataIndex > -1) {
        data = insertItemInArray(data, alertDataIndex, facing);
      } else {
        data = [facing, ...data];
      }
    }
  }

  if (alertExistIndex > -1) {
    alerts = alerts.splice(alertExistIndex, 1, { ...newAlert, data });
  } else {
    alerts = [{ ...newAlert, data }, ...alerts] as WarningsData;
  }
};

export const processItemsToClipboard = (actionResult: {
  newFacings: FacingGroupExtended[];
  newClipboardFacings: FacingGroupExtended[];
  alerts: WarningsData;
}) => {
  const { newFacings, newClipboardFacings, alerts } = actionResult;
  let uniqueProducts: Array<string> = [];

  if (newClipboardFacings && newClipboardFacings.length) {
    const allProducts = newClipboardFacings.map(fG => fG.product_num);

    uniqueProducts = Array.from(new Set(allProducts));
  }

  const newAlerts = uniqueProducts.length > 0
    ? toggleClipboardStatus(uniqueProducts, alerts, true)
    : alerts;
  const productsHavingAlert: Array<string> = [];
  let facingWarningType: 'move_basket_to_shelf_warnings' | 'move_crossbar_to_peg_warnings' | '' = '';

  if (newClipboardFacings.length) {
    newClipboardFacings.forEach((f) => {
      const { fixtureType } = f;

      if ([FIXTURE_TYPES.SHELF, FIXTURE_TYPES.PEGBOARD].includes(fixtureType as FIXTURE_TYPES)) {
        facingWarningType =
          fixtureType === FIXTURE_TYPES.SHELF ? 'move_basket_to_shelf_warnings' : 'move_crossbar_to_peg_warnings';

        if (isFacingHasAlert(alerts, f, facingWarningType)) {
          productsHavingAlert.push(f.product_num);
          processAlerts(newAlerts, facingWarningType, generateAlertObj(f, false), OperationType.UPDATE);
        }
      }
    });
  }

  if (newFacings.length) {
    newFacings.forEach((f) => {
      const { fixtureType } = f;

      facingWarningType =
        fixtureType === FIXTURE_TYPES.SHELF ? 'move_basket_to_shelf_warnings' : 'move_crossbar_to_peg_warnings';

      if (productsHavingAlert.includes(f.product_num)) {
        processAlerts(newAlerts, facingWarningType, generateAlertObj(f, true), OperationType.ADD);
      }
    });
  }

  return newAlerts;
};

export const processAlertsToDisplay = (alerts: WarningsData) => {
  const alertsToDisplay: Partial<WarningsData> = [];

  for (const alert of alerts) {
    if (userActionAlertTypes.includes(alert.warningType)) {
      const data = alert.data as Array<MoveBasketToSelfWarnings | MoveCrossbarToPegWarnings>;
      const alertForUIOnly = data.filter((d) => d.to_fixture_type !== '');

      if (alertForUIOnly.length > 0) {
        alertsToDisplay.push({ ...alert, data: alertForUIOnly });
      }
    } else if (excludedProductAlertTypes.includes(alert.warningType)) {
      const data = alert.data as Array<ExcludedProductWarnings>;
      const alertForUIOnly = data.filter((d) => d.in_clipboard);

      if (alertForUIOnly.length > 0) {
        alertsToDisplay.push({ ...alert, data: alertForUIOnly });
      }
    } else {
      alertsToDisplay.push(alert);
    }
  }

  return alertsToDisplay as WarningsData;
};

export const isFixtureHasCurrentAlert = (x: number, newFacings: Array<FacingGroup>) => {
  const points = newFacings.map((facing) => facing.x);

  return points.includes(x);
};

export const processAlertsForBasketSwitching = (actionResult: {
  newFacings: Array<FacingGroup>;
  alerts: WarningsData;
}) => {
  const { newFacings, alerts } = actionResult;
  const alertToUpdate = alerts.filter((alert) => alert.warningType === 'move_basket_to_shelf_warnings');
  const otherAlerts = alerts.filter((alert) => alert.warningType !== 'move_basket_to_shelf_warnings');

  if (alertToUpdate.length && newFacings.length) {
    const alert = alertToUpdate[0];
    const alertData = alert.data as Array<MoveBasketToSelfWarnings>;
    const newAlerts = alertData.filter((a) => !isFixtureHasCurrentAlert(a.x, newFacings));

    if (newAlerts.length) {
      return [...otherAlerts, { ...alert, data: [...newAlerts] }];
    }

    return otherAlerts;
  }

  return alerts;
};

export const processAlertsForMove = (
  alerts: WarningsData,
  newFacings: Array<FacingGroup>,
  product: string
) => {
  const alertToUpdate = alerts.filter((alert) => alert.warningType === 'move_basket_to_shelf_warnings');
  const otherAlerts = alerts.filter((alert) => alert.warningType !== 'move_basket_to_shelf_warnings');

  if (alertToUpdate.length && newFacings.length) {
    const alert = alertToUpdate[0];
    const alertData = alert.data as Array<MoveBasketToSelfWarnings>;
    const alertDataForCurrentProduct = alertData.filter(d => d.product_num === product);
    const alertDataForOtherProduct = alertData.filter(d => d.product_num !== product);

    return [
      ...otherAlerts,
      {
        ...alert,
        data: [
          ...alertDataForOtherProduct,
          ...newFacings.map(f => {
            return { ...alertDataForCurrentProduct[0], x: f.x };
          })
        ]
      }
    ];
  }

  return alerts;
};

export const processAlertsForFixtureRemoving = (actionResult: {
  newClipboardFixturesFacings: Array<FacingGroup>;
  alerts: WarningsData;
}) => {
  const { newClipboardFixturesFacings, alerts } = actionResult;

  if (newClipboardFixturesFacings.length) {
    const points = newClipboardFixturesFacings.map((facing) => facing.x);
    const products = newClipboardFixturesFacings.map((facing) => facing.product_num);
    const newAlerts = alerts.reduce((acc, alert) => {
      if (userActionAlertTypes.includes(alert.warningType)) {
        const data = alert.data as Array<MoveBasketToSelfWarnings> | Array<MoveCrossbarToPegWarnings>;
        const newData = data.reduce((a, d) => {
          a.push(points.includes(d.x) ? { ...d, to_fixture_type: '' } : d);

          return a;
        }, [] as Array<MoveBasketToSelfWarnings> | Array<MoveCrossbarToPegWarnings>);

        acc.push({ ...alert, data: newData });

        return acc;
      }

      if (excludedProductAlertTypes.includes(alert.warningType)) {
        const data = alert.data as Array<ExcludedProductWarnings>;
        const newData = data.reduce((a, d) => {
          a.push(products.includes(d.product_num) ? { ...d, in_clipboard: true } : d);

          return a;
        }, [] as Array<ExcludedProductWarnings>);

        acc.push({ ...alert, data: newData });

        return acc;
      }

      acc.push(alert);

      return acc;
    }, [] as WarningsData);

    return newAlerts;
  }

  return alerts;
};

export const processAlertsForFixtureDropping = (actionResult: {
  draggedFacings: Array<FacingGroup>,
  newFacings: Array<FacingGroup>;
  alerts: WarningsData;
  newClipboardFixturesFacings?: Array<FacingGroup>;
}) => {
  const { draggedFacings, newFacings, alerts, newClipboardFixturesFacings } = actionResult;

  if (newFacings.length && draggedFacings.length) {
    const points = draggedFacings.map((facing) => facing.x);
    const products = draggedFacings.map((facing) => facing.product_num);
    const newAlerts = alerts.reduce((acc, alert) => {
      if (userActionAlertTypes.includes(alert.warningType)) {
        const data = alert.data as Array<MoveBasketToSelfWarnings> | Array<MoveCrossbarToPegWarnings>;
        const newData = data.reduce((a, d) => {
          if (points.includes(d.x)) {
            const index = points.indexOf(d.x);

            a.push({
              ...d,
              x: newFacings[index].x,
              y: newFacings[index].y,
              to_fixture_type: d.from_fixture_type === 'BASKET' ? 'SHELF' : 'PEGBOARD'
            });
          } else {
            a.push(d);
          }

          return a;
        }, [] as Array<MoveBasketToSelfWarnings> | Array<MoveCrossbarToPegWarnings>);

        acc.push({ ...alert, data: newData });

        return acc;
      }

      if (excludedProductAlertTypes.includes(alert.warningType)) {
        const data = alert.data as Array<ExcludedProductWarnings>;
        const newData = data.reduce((a, d) => {
          a.push(products.includes(d.product_num) ? { ...d, in_clipboard: false } : d);

          return a;
        }, [] as Array<ExcludedProductWarnings>);

        acc.push({ ...alert, data: newData });

        return acc;
      }

      acc.push(alert);

      return acc;
    }, [] as WarningsData);

    return newClipboardFixturesFacings ?
      processAlertsForFixtureRemoving({ newClipboardFixturesFacings, alerts: newAlerts }) :
      alerts;
  }

  return newClipboardFixturesFacings ?
    processAlertsForFixtureRemoving({ newClipboardFixturesFacings, alerts }) :
    alerts;
};

export const processingFixtureDropEvent = (
  processedMousePositions: ReturnType<typeof getProcessedPositionsFromMonitor>,
  monitor: DropTargetMonitor,
  scale: number,
  currentFixtureNumberId: number,
  itemsCoordinates: ItemCoordinates[],
  facingGroupsByIds: Record<string, FacingGroupExtended>,
  locatedFixturesById: Record<string, LocatedFixture>,
  maxProductHeightOnFixtures: Record<string, number>,
  draggableFixtureParams: Record<string, DraggableFixtureParams>,
  fixtureIdToFacingIds: Record<string, Array<string>>,
  facingIdToFixtureIds: Record<string, Array<number>>,
  uniquePlanogramFacings: Record<string, number>,
  selectedFixtureIds: Array<number>,
  fixtureItemSpace: Array<FixturePosition>,
  clipboardSkuToFacingGroups: Record<string, FacingGroup>,
  locatedFixtures: Array<LocatedFixture>,
  isEslPog: boolean,
  alerts: Array<WarningData>,
  lowFixtures: Array<number>,
  fixturesNotchesHolesFixtureSystem: Array<number> | null,
) => {
  const item = monitor.getItem();

  if (!processedMousePositions || !item) {
    return false;
  }

  const {
    itemRootPosition: { y },
    correctedMousePosition: { x: mouseX, y: mouseY },
    correctedClipboardItem,
    containerBoundary,
  } = processedMousePositions;

  const itemType = item.type as string;

  const {
    id,
    fixtureIds,
    fixtureHeight,
    clipboardOffsetTop = 0,
    fixtures: clipboardFixtures,
    fixtureType
  } = item as FixtureDraggableObject;

  const isClipboard = isClipboardItem(itemType);
  const isBasket = fixtureType === FIXTURE_TYPES.BASKET;
  const isCrossbar = fixtureType === FIXTURE_TYPES.CROSSBAR;
  const correctedCrossbarTop = isCrossbar ? draggableFixtureParams[id].offsetDownFromFixture * scale : 0;

  const clipboardYOffset = isClipboard
    ? clipboardOffsetTop - correctedClipboardItem.y + correctedCrossbarTop
    : 0;
  const yPossition = (y + clipboardYOffset - (isClipboard ? 0 : fixtureHeight)) / scale;
  const y1 = fixturesNotchesHolesFixtureSystem
    ? fixturesNotchesHolesFixtureSystem.sort((a, b) => Math.abs(a - yPossition) - Math.abs(b - yPossition))[0]
    : yPossition;
  const cursorX = mouseX / scale;
  const cursorY = mouseY / scale;

  const containerLeftPosition = Math.ceil(containerBoundary.left / scale);
  const containerRightPosition = Math.ceil(containerBoundary.right / scale);
  const containerTopPosition = Math.floor(containerBoundary.top / scale);
  const isOutsideLeftBoundary = cursorX < containerLeftPosition;
  const isOutsideRightBoundary = cursorX > containerRightPosition;
  const isOutsideTopBoundary = cursorY > containerTopPosition;
  const isOutside = isOutsideLeftBoundary || isOutsideRightBoundary || isOutsideTopBoundary;
  const isMultiple = fixtureIds.length > 1;

  if (isOutside) return false;

  const currentFixture = locatedFixturesById[id];
  const originFixtureSegment = currentFixture.segment;
  const currentFixtureSegment = Math.trunc(cursorX / SEGMENT_WIDTH) + 1;
  const segmentShift = currentFixtureSegment - originFixtureSegment;

  let removedId: Array<string> = [];
  let newFacings: Array<FacingGroupExtended> = [];
  let removedClipboardId: Array<string> = [];
  let newClipboardFacings: Array<FacingGroupExtended> = [];
  let newFixtures: Array<LocatedFixture> = [];
  // let newClipboardFixturesFacings: Array<FacingGroupExtended> = [];
  let removedClipboardFixturesFacings: Array<string> = [];
  let removedClipboardFixtures: Array<number> = [];
  let removedFixtureIds: Array<number> = [];

  switch (itemType) {
    case DraggableFixtureItems.PEGBOARD:
    case DraggableClipboardFixtureItems.FixturePegboardItem: {
      const locatedFixtureId = currentFixtureNumberId + 1;
      const pegboardShift = currentFixture.x % SEGMENT_WIDTH;
      const fixtureStartPosition = (currentFixtureSegment - 1) * SEGMENT_WIDTH + pegboardShift;
      const isPegboardOverlapping = locatedFixtures.filter(item => item.segment === currentFixtureSegment).some(item => item.type === FIXTURE_TYPES.PEGBOARD);

      if (isPegboardOverlapping) {
        return false;
      }

      const newFixture = {
        ...currentFixture,
        segment: currentFixtureSegment,
        x: fixtureStartPosition,
        id: locatedFixtureId
      };

      newFixtures = [newFixture];
      const currentFixtureFacingGroupIds = fixtureIdToFacingIds[id] || [];

      const { newFacingsGroups, draggedFacings } = currentFixtureFacingGroupIds.reduce((acc, val) => {
        const facing = facingGroupsByIds[val];
        const x1 = facing.x - currentFixture.x + fixtureStartPosition;
        const newfacing = {
          ...facing,
          id: generateUniqueId(),
          segment: newFixture.segment,
          located_fixture_id: newFixture.id,
          x: x1
        };

        const x2 = x1 + facing.packaging.width * facing.horizontal_facings;

        if (x1 < 0 || x2 > containerRightPosition) {
          acc.hangingFacings.push(newfacing);

          return acc;
        }

        acc.newFacingsGroups.push(newfacing);
        acc.draggedFacings.push(facing);

        return acc;
      }, {
        newFacingsGroups: [] as Array<FacingGroupExtended>,
        hangingFacings: [] as Array<FacingGroupExtended>,
        draggedFacings: [] as Array<FacingGroup>
      });

      newFacings = newFacingsGroups;

      const selectedFacingsIds = fixtureIds.reduce((acc, fixtureId) => {
        const ids = fixtureIdToFacingIds[fixtureId] || [];

        return acc.concat(...ids);
      }, [] as Array<string>);

      const facingsWithoutSelected = itemsCoordinates.filter(facing => !selectedFacingsIds.includes(facing.id));

      const newItems = newFacings.map(item => {
        const {
          horizontal_facings: h,
          vertical_facings: v,
          packaging: {
            height,
            nested_height: nH,
            width
          },
          cap_nested: cN,
          capped_vertical_facings: cV,
          cappedPackaging: {
            height: cH
          }
        } = item;
        const facingHeight = getFacingHeight(height, v, cN, nH, cH, cV);
        const facingWidth = width * h;

        return {
          x1: item.x,
          x2: item.x + facingWidth,
          y1: item.y,
          y2: item.y + facingHeight
        };
      });

      const { pegboardOverlappingIds, othersOverlappingsIds } = newItems.reduce((acc, item) => {
        const {
          overlappingWithCrossbars,
          overlappingWithOthers
        } = facingsWithoutSelected.reduce((itemsCollection, i) => {
          const left = Math.max(item.x1, i.x1);
          const bottom = Math.max(item.y1, i.y1);
          const right = Math.min(item.x2, i.x2);
          const top = Math.min(item.y2, i.y2);
          const isOverlapping = right > left && top > bottom;

          if (isOverlapping) {
            if (i.isPeg) {
              itemsCollection.overlappingWithCrossbars.push(i.id);
            } else {
              itemsCollection.overlappingWithOthers.push(i.id);
            }
          }

          return itemsCollection;
        }, {
          overlappingWithCrossbars: [] as string[],
          overlappingWithOthers: [] as string[]
        });

        acc.pegboardOverlappingIds.push(...overlappingWithCrossbars);
        acc.othersOverlappingsIds.push(...overlappingWithOthers);

        return acc;
      }, {
        pegboardOverlappingIds: [] as string[],
        othersOverlappingsIds: [] as string[]
      });

      if (pegboardOverlappingIds.length || othersOverlappingsIds.length) {
        return false;
      }

      const isFacingOverlapWithFixtures = isNewFacingsOverlapWithFixtures(newItems, locatedFixtures);

      if (isFacingOverlapWithFixtures.length) {
        return false;
      }

      removedFixtureIds = [id];
      removedId = currentFixtureFacingGroupIds;

      if (isClipboard) {
        removedClipboardFixtures = fixtureIds;
        removedClipboardFixturesFacings = currentFixtureFacingGroupIds;
        removedId = [];
        removedFixtureIds = [];

        const connectedSkus = processRemovingSkusConnectedToFixture(
          newFacings,
          clipboardSkuToFacingGroups
        );

        newClipboardFacings = newClipboardFacings.concat(connectedSkus.newClipboardFacings);
        removedClipboardId = removedClipboardId.concat(connectedSkus.removedClipboardIds);
      }

      const alertFacings = draggedFacings.length ? draggedFacings : removedClipboardFixturesFacings.map(id => facingGroupsByIds[id]);
      const processedAlerts = processAlertsForFixtureDropping({ draggedFacings: alertFacings, newFacings, alerts, newClipboardFixturesFacings: newClipboardFacings });

      return {
        ...historyStepItem,
        removedClipboardFixturesFacings,
        removedClipboardFixtures,
        newClipboardFacings,
        removedClipboardId,
        removedFixtureIds,
        newFixtures,
        newFacings,
        removedId,
        alerts: processedAlerts
      };
    }

    case DraggableFixtureItems.CROSSBAR:
    case DraggableClipboardFixtureItems.FixtureCrossbarItem: {
      const sortedFixtures = fixtureIds.sort((a, b) => locatedFixturesById[a].x - locatedFixturesById[b].x);
      const newFixturesCollection = isClipboard
        ? getNewFixturesFromClipboardItems(
          clipboardFixtures,
          locatedFixturesById,
          currentFixtureNumberId,
          currentFixtureSegment,
          y1
        )
        : getNewFixtureFromPogItems(
          locatedFixturesById,
          currentFixture,
          sortedFixtures,
          segmentShift,
          currentFixtureNumberId,
          currentFixtureSegment,
          id,
          y1
        );
      const {
        fixtures,
        facingGroups,
        removedFacingsId,
        clipboardFacings,
        draggedFacings
      } = fixtureIds.reduce((acc, fixtureId) => {
        const fixture = locatedFixturesById[fixtureId];
        const newFixture = newFixturesCollection[fixtureId];

        // autocorrection Y
        if (!isMultiple) {
          const ghostNewFixture = {
            y1: newFixture.y,
            y2: newFixture.y + newFixture.height + newFixture.grille_height,
          };
          let correctionFixtureY = newFixture.y;
          const [crossoverlocatedFixture] = locatedFixtures.filter(locatedFixture => {
            const isSelf = locatedFixture.id === fixtureId;

            if (isSelf) return false;

            if (
              locatedFixture.type === FIXTURE_TYPES.PEGBOARD ||
              locatedFixture.type === FIXTURE_TYPES.SHELF ||
              locatedFixture.type === FIXTURE_TYPES.BASKET ||
              locatedFixture.is_display_only ||
              locatedFixture.is_base_shelf
            ) {
              return false;
            }

            const isNear = Math.abs(locatedFixture.segment - newFixture.segment) === 1;

            if (!isNear) return false;

            const fixtureRect = {
              y1: locatedFixture.y,
              y2: locatedFixture.y + locatedFixture.height + locatedFixture.grille_height,
            };
            const bottom = Math.max(ghostNewFixture.y1, fixtureRect.y1);
            const top = Math.min(ghostNewFixture.y2, fixtureRect.y2);

            return top >= bottom;
          }).sort((fixtureA, fixtureB) => {
            const diffA = fixtureA.y - newFixture.y;
            const diffB = fixtureB.y - newFixture.y;

            return diffA - diffB;
          });

          if (crossoverlocatedFixture) {
            const top = crossoverlocatedFixture.y + crossoverlocatedFixture.height;

            correctionFixtureY = top - newFixture.height;
            newFixture.y = correctionFixtureY;
          }
        }
        // end autocorrection Y

        const ids = fixtureIdToFacingIds[fixtureId] || [];
        const offsetUpFromFixture = draggableFixtureParams[fixtureId]?.offsetUpFromFixture || 0;

        const fixtureStartPosition = newFixture.x;
        const shelfProductsTopPosition = newFixture.y + offsetUpFromFixture;
        const fixtureEndPosition = fixtureStartPosition + fixture.width;

        if (
          fixtureStartPosition < containerLeftPosition ||
          fixtureEndPosition > containerRightPosition ||
          shelfProductsTopPosition > containerTopPosition
        ) return acc;

        const { hangingFacingIds = [], newFacings = [], clipboardFacings = [], draggedFacings = [] } = ids.reduce((collection, facingId) => {
          const facing = facingGroupsByIds[facingId];
          const x1 = facing.x - fixture.x + fixtureStartPosition;

          const occupiedFixtures = facingIdToFixtureIds[facing.id] || [];
          const isMultipleFixturesFacing = occupiedFixtures.length > 1;
          const isAllSelfFixturesSelected = occupiedFixtures.every(f => selectedFixtureIds.includes(f));
          const pegholesPerInch = facing.packaging.pegholes.map(peghole => ({
            peghole_x: peghole.peghole_x,
            peghole_y: peghole.peghole_y,
            peghole_width: peghole.peghole_width,
          }));

          const pegholePerInch = pegholesPerInch[0];
          const absolutePegholePerInch = {
            peghole_x: facing.x + pegholePerInch.peghole_x,
            peghole_y: facing.y + facing.packaging.height - pegholePerInch.peghole_y,
            peghole_width: pegholePerInch.peghole_width,
          };
          const deltaFixturePeghole = (facing.packaging.height + facing.y) - absolutePegholePerInch.peghole_y;

          const yPosition = newFixture.y + newFixture.height - facing.packaging.height + deltaFixturePeghole;

          if (isClipboard) {
            const newY = yPosition;
            const { newFacing, newClipboardFacing } = processCreatingFacingsFromFixtureClipboard(
              newFixture,
              fixture,
              facing,
              locatedFixturesById,
              x1,
              newY,
              isEslPog
            );

            if (newClipboardFacing) collection.clipboardFacings.push(newClipboardFacing);
            if (newFacing) collection.newFacings.push(newFacing);

            return collection;
          }

          if (acc.hangingFacingIds.includes(facingId)) {
            return collection;
          }

          if (isMultipleFixturesFacing && !isAllSelfFixturesSelected) {
            const { newFacings, newClipboardFacing } = processCreatingFacingsInPog(
              occupiedFixtures,
              selectedFixtureIds,
              locatedFixturesById,
              facing,
              newFixturesCollection,
              yPosition,
              isEslPog
            );

            collection.clipboardFacings.push(newClipboardFacing);
            collection.newFacings.push(...newFacings);
            collection.hangingFacingIds.push(facingId);

            return collection;
          }

          const newFacingY = isClipboard ? 0 : yPosition;

          const newfacing = {
            ...facing,
            id: generateUniqueId(),
            segment: newFixture.segment,
            located_fixture_id: newFixture.id,
            y: newFacingY,
            x: x1
          };

          if (isMultipleFixturesFacing && isAllSelfFixturesSelected) {
            collection.hangingFacingIds.push(facingId);
          }

          collection.newFacings.push(newfacing);
          collection.draggedFacings.push(facing);

          return collection;
        }, {
          hangingFacingIds: [] as Array<string>,
          newFacings: [] as Array<FacingGroupExtended>,
          clipboardFacings: [] as Array<FacingGroupExtended>,
          draggedFacings: [] as Array<FacingGroup>
        });

        acc.fixtures.push(newFixture);
        acc.facingGroups.push(...newFacings);
        acc.removedFacingsId.push(...ids);
        acc.hangingFacingIds.push(...hangingFacingIds);
        acc.clipboardFacings.push(...clipboardFacings);
        acc.draggedFacings.push(...draggedFacings);

        return acc;
      }, {
        fixtures: [] as Array<LocatedFixture>,
        facingGroups: [] as Array<FacingGroupExtended>,
        removedFacingsId: [] as Array<string>,
        hangingFacingIds: [] as Array<string>,
        clipboardFacings: [] as Array<FacingGroupExtended>,
        draggedFacings: [] as Array<FacingGroup>
      });

      if (fixtures.length !== fixtureIds.length) {
        return false;
      }

      const newItems = facingGroups.map(item => {
        const {
          horizontal_facings: h,
          vertical_facings: v,
          packaging: {
            height,
            nested_height: nH,
            width
          },
          cap_nested: cN,
          capped_vertical_facings: cV,
          cappedPackaging: {
            height: cH
          }
        } = item;
        const facingHeight = getFacingHeight(height, v, cN, nH, cH, cV);
        const facingWidth = width * h;

        return {
          x1: item.x,
          x2: item.x + facingWidth,
          y1: item.y,
          y2: item.y + facingHeight
        };
      });

      const { activeOverlappingIds, activeOverlappingFixturesId } = newItems.reduce((acc, item) => {
        const { activeOverlappingIds } = itemsCoordinates.reduce((itemsCollection, i) => {
          if (removedFacingsId.includes(i.id)) {
            return itemsCollection;
          }

          const left = Math.max(item.x1, i.x1);
          const bottom = Math.max(item.y1, i.y1);
          const right = Math.min(item.x2, i.x2);
          const top = Math.min(item.y2, i.y2);
          const isOverlapping = right > left && top > bottom;

          if (isOverlapping) {
            itemsCollection.activeOverlappingIds.push(i.id);
          }

          return itemsCollection;
        }, {
          activeOverlappingIds: [] as string[]
        });

        const { activeOverlappingFixturesId } = fixtureItemSpace.reduce((itemsCollection, i) => {
          if (selectedFixtureIds.includes(i.id)) {
            return itemsCollection;
          }

          const isOverlapping = item.x1 > i.x1 && item.x2 < i.x2 && item.y1 < i.y1 && item.y2 > i.y2;

          if (isOverlapping) {
            itemsCollection.activeOverlappingFixturesId.push(i.id);
          }

          return itemsCollection;
        }, {
          activeOverlappingFixturesId: [] as number[],
        });

        acc.activeOverlappingIds.push(...activeOverlappingIds);
        acc.activeOverlappingFixturesId.push(...activeOverlappingFixturesId);

        return acc;
      }, {
        activeOverlappingIds: [] as Array<string>,
        activeOverlappingFixturesId: [] as Array<number>
      });

      if (activeOverlappingIds.length || activeOverlappingFixturesId.length) {
        return false;
      }

      const selectedFacingsIds = fixtureIds.reduce((acc, fixtureId) => {
        const ids = fixtureIdToFacingIds[fixtureId] || [];

        return acc.concat(...ids);
      }, [] as Array<string>);

      const facingsWithoutSelected = itemsCoordinates.filter(facing => !selectedFacingsIds.includes(facing.id));
      const occupiedFacings = isFixturesOverlapWithFacings(newFixturesCollection, facingsWithoutSelected, fixtureIds);

      if (occupiedFacings.length) {
        return false;
      }

      const fixtureOverlappings = isFixturesOverlap(fixtures, locatedFixtures, fixtureIds);

      if (fixtureOverlappings.length) {
        return false;
      }

      removedId = removedFacingsId;
      newFacings = facingGroups;
      newClipboardFacings = filterByUniqueSku(clipboardFacings, uniquePlanogramFacings);
      newFixtures = fixtures;
      removedFixtureIds = fixtureIds;

      if (isClipboard) {
        removedClipboardFixtures = fixtureIds;
        removedClipboardFixturesFacings = removedFacingsId;
        removedId = [];
        removedFixtureIds = [];

        const connectedSkus = processRemovingSkusConnectedToFixture(
          newFacings,
          clipboardSkuToFacingGroups
        );

        newClipboardFacings = newClipboardFacings.concat(connectedSkus.newClipboardFacings);
        removedClipboardId = removedClipboardId.concat(connectedSkus.removedClipboardIds);
      }

      const alertFacings = draggedFacings.length ? draggedFacings : removedClipboardFixturesFacings.map(id => facingGroupsByIds[id]);
      const processedAlerts = processAlertsForFixtureDropping({ draggedFacings: alertFacings, newFacings, alerts, newClipboardFixturesFacings: newClipboardFacings });

      return {
        ...historyStepItem,
        newFacings,
        removedId,
        removedClipboardId,
        newClipboardFacings,
        removedFixtureIds,
        newFixtures,
        removedClipboardFixturesFacings,
        removedClipboardFixtures,
        alerts: processedAlerts
      };
    }

    default: {
      const sortedFixtures = fixtureIds.sort((a, b) => locatedFixturesById[a].x - locatedFixturesById[b].x);
      const newFixturesCollection = isClipboard
        ? getNewFixturesFromClipboardItems(
          clipboardFixtures,
          locatedFixturesById,
          currentFixtureNumberId,
          currentFixtureSegment,
          y1
        )
        : getNewFixtureFromPogItems(
          locatedFixturesById,
          currentFixture,
          sortedFixtures,
          segmentShift,
          currentFixtureNumberId,
          currentFixtureSegment,
          id,
          y1
        );
      const {
        fixtures,
        facingGroups,
        removedFacingsId,
        clipboardFacings,
        draggedFacings
      } = fixtureIds.reduce((acc, fixtureId) => {
        const fixture = locatedFixturesById[fixtureId];
        const newFixture = newFixturesCollection[fixtureId];

        // autocorrection Y
        if (!isMultiple) {
          const ghostNewFixture = {
            y1: newFixture.y,
            y2: newFixture.y + newFixture.height + newFixture.grille_height,
          };
          let correctionFixtureY = newFixture.y;
          const [crossoverlocatedFixture] = locatedFixtures.filter(locatedFixture => {
            const isSelf = locatedFixture.id === fixtureId;

            if (isSelf) return false;

            if (
              locatedFixture.type === 'PEGBOARD' ||
              locatedFixture.type === 'CROSSBAR' ||
              locatedFixture.is_display_only ||
              locatedFixture.is_base_shelf
            ) {
              return false;
            }

            const isNear = Math.abs(locatedFixture.segment - newFixture.segment) === 1;

            if (!isNear) return false;

            const fixtureRect = {
              y1: locatedFixture.y,
              y2: locatedFixture.y + locatedFixture.height + locatedFixture.grille_height,
            };
            const bottom = Math.max(ghostNewFixture.y1, fixtureRect.y1);
            const top = Math.min(ghostNewFixture.y2, fixtureRect.y2);

            return top >= bottom;
          }).sort((fixtureA, fixtureB) => {
            const diffA = fixtureA.y - newFixture.y;
            const diffB = fixtureB.y - newFixture.y;

            return diffA - diffB;
          });

          if (crossoverlocatedFixture) {
            const top = crossoverlocatedFixture.y + crossoverlocatedFixture.height;

            correctionFixtureY = top - newFixture.height;
            newFixture.y = correctionFixtureY;
          }
        }
        // end autocorrection Y

        const ids = fixtureIdToFacingIds[fixtureId] || [];
        const maxFacingHeight = maxProductHeightOnFixtures[fixtureId] || 0;
        const isSlopedBasket = newFixture.type === 'BASKET' && newFixture.shelf_slope !== 0;
        const basketSlopeHeight = adjacentHeightToShelfSlopeAngle(newFixture.shelf_slope, newFixture.depth);
        const slopedFacingHeight = isSlopedBasket
          ? basketSlopeHeight + maxFacingHeight * Math.cos(Math.abs(newFixture.shelf_slope) * (Math.PI / 180))
          : 0;
        const basketHeight = Math.max(maxFacingHeight, fixture.grille_height, slopedFacingHeight);
        const isDisplayOnly = fixture.is_display_only;
        const totalShelfHeight = fixture.height + (isDisplayOnly ? DISPLAY_HEIGHT : maxFacingHeight);
        const height = isBasket
          ? basketHeight
          : totalShelfHeight;

        const fixtureStartPosition = newFixture.x;
        const shelfProductsTopPosition = newFixture.y + height;
        const fixtureEndPosition = fixtureStartPosition + fixture.width;

        if (
          fixtureStartPosition < containerLeftPosition ||
          fixtureEndPosition > containerRightPosition ||
          shelfProductsTopPosition > containerTopPosition
        ) return acc;

        const {
          hangingFacingIds = [],
          newFacings = [],
          clipboardFacings = [],
          draggedFacings = []
        } = ids.reduce((collection, facingId) => {
          const facing = facingGroupsByIds[facingId];
          const x1 = facing.x - fixture.x + fixtureStartPosition;

          const occupiedFixtures = facingIdToFixtureIds[facing.id] || [];
          const isMultipleFixturesFacing = occupiedFixtures.length > 1;
          const isAllSelfFixturesSelected = occupiedFixtures.every(f => selectedFixtureIds.includes(f));
          const yPosition = newFixture.y + newFixture.height;

          if (isClipboard) {
            const { newFacing, newClipboardFacing } = processCreatingFacingsFromFixtureClipboard(
              newFixture,
              fixture,
              facing,
              locatedFixturesById,
              x1,
              newFixture.y + newFixture.height
            );

            if (newClipboardFacing) collection.clipboardFacings.push(newClipboardFacing);
            if (newFacing) collection.newFacings.push(newFacing);

            return collection;
          }

          if (acc.hangingFacingIds.includes(facingId)) {
            return collection;
          }

          if (isMultipleFixturesFacing && !isAllSelfFixturesSelected) {
            const { newFacings, newClipboardFacing } = processCreatingFacingsInPog(
              occupiedFixtures,
              selectedFixtureIds,
              locatedFixturesById,
              facing,
              newFixturesCollection,
              yPosition
            );

            collection.clipboardFacings.push(newClipboardFacing);
            collection.newFacings.push(...newFacings);
            collection.hangingFacingIds.push(facingId);

            return collection;
          }

          const newfacing = {
            ...facing,
            id: generateUniqueId(),
            segment: newFixture.segment,
            located_fixture_id: newFixture.id,
            y: isClipboard
              ? 0
              : yPosition,
            x: x1
          };

          if (isMultipleFixturesFacing && isAllSelfFixturesSelected) {
            collection.hangingFacingIds.push(facingId);
          }

          collection.newFacings.push(newfacing);

          collection.draggedFacings.push(facing);

          return collection;
        }, {
          hangingFacingIds: [] as Array<string>,
          newFacings: [] as Array<FacingGroupExtended>,
          clipboardFacings: [] as Array<FacingGroupExtended>,
          draggedFacings: [] as Array<FacingGroup>
        });

        acc.fixtures.push(newFixture);
        acc.facingGroups.push(...newFacings);
        acc.removedFacingsId.push(...ids);
        acc.hangingFacingIds.push(...hangingFacingIds);
        acc.clipboardFacings.push(...clipboardFacings);
        acc.draggedFacings.push(...draggedFacings);

        return acc;
      }, {
        fixtures: [] as Array<LocatedFixture>,
        facingGroups: [] as Array<FacingGroupExtended>,
        removedFacingsId: [] as Array<string>,
        hangingFacingIds: [] as Array<string>,
        clipboardFacings: [] as Array<FacingGroupExtended>,
        draggedFacings: [] as Array<FacingGroup>
      });

      if (fixtures.length !== fixtureIds.length) {
        return false;
      }

      const isOutBaseShelf = fixtures.some(fixture => {
        const lowFixtureSameSegment = lowFixtures.find(id => locatedFixturesById[id]?.segment === fixture.segment);

        if (!lowFixtureSameSegment) {
          return false;
        }

        return fixture.y < locatedFixturesById[lowFixtureSameSegment].y;
      });

      if (isOutBaseShelf) {
        return false;
      }

      const newItems = facingGroups.map(item => {
        const {
          horizontal_facings: h,
          vertical_facings: v,
          packaging: {
            height,
            nested_height: nH,
            width
          },
          cap_nested: cN,
          capped_vertical_facings: cV,
          cappedPackaging: {
            height: cH
          }
        } = item;
        const facingHeight = getFacingHeight(height, v, cN, nH, cH, cV);
        const facingWidth = width * h;

        return {
          x1: item.x,
          x2: item.x + facingWidth,
          y1: item.y - 1.25,
          y2: item.y + facingHeight + 1.25
        };
      });

      const { activeOverlappingIds, activeOverlappingFixturesId } = newItems.reduce((acc, item) => {
        const { activeOverlappingIds } = itemsCoordinates.reduce((itemsCollection, i) => {
          if (removedFacingsId.includes(i.id)) {
            return itemsCollection;
          }

          const left = Math.max(item.x1, i.x1);
          const bottom = Math.max(item.y1, i.y1);
          const right = Math.min(item.x2, i.x2);
          const top = Math.min(item.y2, i.y2);
          const isOverlapping = right > left && top > bottom;

          if (isOverlapping) {
            itemsCollection.activeOverlappingIds.push(i.id);
          }

          return itemsCollection;
        }, {
          activeOverlappingIds: [] as string[]
        });

        const { activeOverlappingFixturesId } = fixtureItemSpace.reduce((itemsCollection, i) => {
          if (selectedFixtureIds.includes(i.id)) {
            return itemsCollection;
          }

          const isOverlapping = item.x1 > i.x1 && item.x2 < i.x2 && item.y1 < i.y1 && item.y2 > i.y2;

          if (isOverlapping) {
            itemsCollection.activeOverlappingFixturesId.push(i.id);
          }

          return itemsCollection;
        }, {
          activeOverlappingFixturesId: [] as number[],
        });

        acc.activeOverlappingIds.push(...activeOverlappingIds);
        acc.activeOverlappingFixturesId.push(...activeOverlappingFixturesId);

        return acc;
      }, {
        activeOverlappingIds: [] as Array<string>,
        activeOverlappingFixturesId: [] as Array<number>
      });

      if (activeOverlappingIds.length || activeOverlappingFixturesId.length) {
        return false;
      }

      const selectedFacingsIds = fixtureIds.reduce((acc, fixtureId) => {
        const ids = fixtureIdToFacingIds[fixtureId] || [];

        return acc.concat(...ids);
      }, [] as Array<string>);

      const facingsWithoutSelected = itemsCoordinates.filter(facing => !selectedFacingsIds.includes(facing.id));
      const occupiedFacings = isFixturesOverlapWithFacings(newFixturesCollection, facingsWithoutSelected, fixtureIds);

      if (occupiedFacings.length) {
        return false;
      }

      const fixtureOverlappings = isFixturesOverlap(fixtures, locatedFixtures, fixtureIds);

      if (fixtureOverlappings.length) {
        return false;
      }

      removedId = removedFacingsId;
      newFacings = facingGroups;
      newClipboardFacings = filterByUniqueSku(clipboardFacings, uniquePlanogramFacings);
      newFixtures = fixtures;
      removedFixtureIds = fixtureIds;

      if (isClipboard) {
        removedClipboardFixtures = fixtureIds;
        removedClipboardFixturesFacings = removedFacingsId;
        removedId = [];
        removedFixtureIds = [];

        const connectedSkus = processRemovingSkusConnectedToFixture(
          newFacings,
          clipboardSkuToFacingGroups
        );
        const connectedClipboardSkus = filterByUniqueSku(
          connectedSkus.newClipboardFacings,
          uniquePlanogramFacings
        );

        newClipboardFacings = clipboardFacings.concat(connectedClipboardSkus);
        removedClipboardId = removedClipboardId.concat(connectedSkus.removedClipboardIds);
      }

      const alertFacings = draggedFacings.length ? draggedFacings : removedClipboardFixturesFacings.map(id => facingGroupsByIds[id]);
      const processedAlerts = processAlertsForFixtureDropping({ draggedFacings: alertFacings, newFacings, alerts, newClipboardFixturesFacings: newClipboardFacings });

      return {
        ...historyStepItem,
        newFacings,
        removedId,
        removedClipboardId,
        newClipboardFacings,
        removedFixtureIds,
        newFixtures,
        removedClipboardFixturesFacings,
        removedClipboardFixtures,
        alerts: processedAlerts
      };
    }
  }
};

export const createNewFacingFromHangingFacing = (
  facing: FacingGroupExtended,
  startingFixtureId: number,
  endingFixtureId: number,
  locatedFixturesById: Record<string, LocatedFixture>,
  fixture: LocatedFixture,
  y: number,
  isEslPog: boolean = false
): FacingGroupExtended => {
  const {
    x: facingStartPosition,
    horizontal_facings: horizontalFacings,
    packaging: {
      width: facingWidth
    }
  } = facing;
  const startingFixture = locatedFixturesById[startingFixtureId];
  const endingFixture = locatedFixturesById[endingFixtureId];

  const fixtureStartPosition = startingFixture.x;
  let fixtureEndPosition = fixtureStartPosition + startingFixture.width;
  let d, h, x;

  d = h = x = 0;

  const facingEndPosition = facingStartPosition + facingWidth * horizontalFacings;

  if (startingFixtureId !== endingFixtureId) {
    fixtureEndPosition = endingFixture.x + endingFixture.width;
  }

  if (
    facingStartPosition < fixtureStartPosition &&
    facingEndPosition > fixtureEndPosition
  ) {
    const d1 = fixtureStartPosition - facingStartPosition;
    const d2 = facingEndPosition - fixtureEndPosition;
    const diff = facingWidth * horizontalFacings - d1 - d2;
    const h1 = Math.ceil(d1 / facingWidth);
    const diff1 = facingWidth * h1;

    h = Math.trunc(diff / facingWidth);
    x = fixture.x + facingStartPosition + diff1 - fixtureStartPosition;
  } else if (facingStartPosition > fixtureStartPosition) {
    d = fixtureEndPosition - facingStartPosition;
    h = Math.trunc(d / facingWidth);
    x = fixture.x + facing.x - fixtureStartPosition;
  }
  else if (facingEndPosition > fixtureEndPosition) {
    d = fixtureEndPosition - facingStartPosition;
    h = Math.trunc(d / facingWidth);
    x = facing.x;
  }
  else if (facingEndPosition < fixtureEndPosition) {
    d = facingEndPosition - fixtureStartPosition;
    h = Math.trunc(d / facingWidth);
    x = fixture.x + d - facingWidth * h;
  }

  return {
    ...facing,
    id: generateUniqueId(),
    x,
    y,
    horizontal_facings: h,
    segment: fixture.segment,
    located_fixture_id: fixture.id
  };
};

const processHangingFacing = (
  groupedFixtures: Array<Array<number>>,
  facing: FacingGroupExtended,
  selectedFixtures: Array<number>,
  locatedFixturesById: Record<string, LocatedFixture>,
  newFixtureToOriginId: Record<string, LocatedFixture>,
  y: number,
  isEslPog: boolean = false
) => {
  return groupedFixtures.reduce((acc, val) => {
    const isSelected = val.every(item => selectedFixtures.includes(item));
    const startingFixture = locatedFixturesById[val[0]];
    const endingFixture = locatedFixturesById[val[val.length - 1]];
    const newFixture = newFixtureToOriginId[val[0]];

    if (isSelected) {
      const newFacing = createNewFacingFromHangingFacing(
        facing,
        startingFixture.id,
        endingFixture.id,
        locatedFixturesById,
        newFixture,
        y,
        isEslPog
      );

      return acc.concat(newFacing);
    }

    return acc;
  }, [] as Array<FacingGroupExtended>);
};

const rearangeFacingFixtures = (
  occupiedFixtures: Array<number>,
  selectedFixtures: Array<number>,
  locatedFixturesById: Record<string, LocatedFixture>
) => {
  const fixtureFacingSequence = occupiedFixtures
    .map(f => locatedFixturesById[f])
    .sort((a, b) => a.x - b.x)
    .map(item => item.id);

  return fixtureFacingSequence.reduce((acc, val, index, array) => {
    const previousGroup = acc[acc.length - 1];

    if (!previousGroup) {
      acc[acc.length] = [val];
    } else {
      const previousGroupValue = previousGroup[previousGroup.length - 1];
      const i = array.indexOf(previousGroupValue);
      const isRightPosition = array.indexOf(val) - 1 === i;
      const isPreviousValueSelected = selectedFixtures.includes(previousGroupValue);
      const isCurrentValueSelected = selectedFixtures.includes(val);

      if (isCurrentValueSelected) {
        if (isRightPosition && isPreviousValueSelected) {
          acc[acc.length - 1].push(val);
        } else {
          acc[acc.length] = [val];
        }
      } else {
        if (isRightPosition && !isPreviousValueSelected) {
          acc[acc.length - 1].push(val);
        } else {
          acc[acc.length] = [val];
        }
      }
    }

    return acc;
  }, [] as Array<Array<number>>);
};

export const processDroppingFixtureItems = (
  locatedFixturesById: Record<string, LocatedFixture>,
  collectionIds: Array<number>
) => collectionIds.map(id => locatedFixturesById[id]);

const processCreatingFacingsFromFixtureClipboard = (
  newFixture: LocatedFixture,
  fixture: LocatedFixture,
  facing: FacingGroupExtended,
  locatedFixturesById: Record<string, LocatedFixture>,
  x: number,
  y: number,
  isEslPog: boolean = false
) => {
  const facingWidth = facing.packaging.width * facing.horizontal_facings;
  const facingX1 = facing.x;
  const facingX2 = facingX1 + facingWidth;
  const fixtureX1 = fixture.x;
  const fixtureX2 = fixtureX1 + fixture.width;
  let newFacing: FacingGroupExtended | null = null;
  let newClipboardFacing: FacingGroupExtended | null = null;

  if (facingX1 >= fixtureX1 && facingX2 <= fixtureX2) {
    newFacing = {
      ...facing,
      id: generateUniqueId(),
      x,
      y,
      located_fixture_id: newFixture.id
    };
  } else {
    if (facing.horizontal_facings === 1) {
      newClipboardFacing = {
        ...facing, id: generateUniqueId(), x, y
      };
    } else {
      newFacing = createNewFacingFromHangingFacing(
        facing,
        fixture.id,
        fixture.id,
        locatedFixturesById,
        newFixture,
        y
      );
      const h = facing.horizontal_facings - newFacing.horizontal_facings;

      newClipboardFacing = {
        ...facing,
        id: generateUniqueId(),
        horizontal_facings: h
      };
    }
  }

  return { newFacing: newFacing, newClipboardFacing };
};

const processCreatingFacingsInPog = (
  occupiedFixtures: Array<number>,
  selectedFixtureIds: Array<number>,
  locatedFixturesById: Record<string, LocatedFixture>,
  facing: FacingGroupExtended,
  newFixturesCollection: Record<string, LocatedFixture>,
  yPosition: number,
  isEslPog: boolean = false
) => {
  if (facing.horizontal_facings === 1) {
    return {
      newFacings: [],
      newClipboardFacing: facing
    };
  }

  const groupedFixtures = rearangeFacingFixtures(
    occupiedFixtures,
    selectedFixtureIds,
    locatedFixturesById
  );
  const newFacings = processHangingFacing(
    groupedFixtures,
    facing,
    selectedFixtureIds,
    locatedFixturesById,
    newFixturesCollection,
    yPosition,
    isEslPog
  );
  const h = facing.horizontal_facings;
  const h1 = getHorizontalFacings(newFacings);
  const newClipboardFacing = {
    ...facing,
    horizontal_facings: h - h1
  };

  return {
    newFacings,
    newClipboardFacing
  };
};

const getHorizontalFacings = (facings: Array<FacingGroupExtended>): number => {
  return facings.reduce((amount, facing) => amount + facing.horizontal_facings, 0);
};

export const processDroppingFixturesFacings = (
  facingGroupsByIds: Record<string, FacingGroupExtended>,
  locatedFixtureIdToFacingIds: Record<string, Array<string>>,
  removedFixtureIds: Array<number>
) => removedFixtureIds.reduce((acc, id) => {
  const ids = locatedFixtureIdToFacingIds[id] || [];
  const newFacings = ids.reduce((collection, facingId) =>
    collection.concat({
      ...facingGroupsByIds[facingId],
      id: generateUniqueId(),
      located_fixture_id: id
    }), [] as Array<FacingGroupExtended>);

  acc.newClipboardFacings.push(...newFacings);
  acc.newClipboardFixturesFacings.push(...newFacings);
  acc.removedId.push(...ids);

  return acc;
}, {
  removedId: [] as Array<string>,
  newClipboardFixturesFacings: [] as Array<FacingGroupExtended>,
  newClipboardFacings: [] as Array<FacingGroupExtended>
});

export const getFixtureBgColor = (
  isDisable: boolean,
  isDashed: boolean,
  isFilled: boolean,
  isCrossbar: boolean,
  isFlatbar: boolean,
  isPegBoard: boolean,
  isSelected: boolean,
  isSpecialty: boolean
) => {
  let bgColor = ShelfHighlightColors.default;

  if (isDisable) bgColor = ShelfHighlightColors.default;
  if (isDashed && !isFilled) bgColor = ShelfHighlightColors.default;
  if (isCrossbar) bgColor = ShelfHighlightColors.crossbar;
  if (isFlatbar) bgColor = ShelfHighlightColors.crossbar;
  if (isPegBoard) bgColor = ShelfHighlightColors.pegboard;
  if (isSelected) bgColor = ShelfHighlightColors.selected;
  if (isSpecialty) bgColor = ShelfHighlightColors.specialty;

  return bgColor;
};

export const processRemovingSkusConnectedToFixture = (
  newFacings: Array<FacingGroup>,
  clipboardSkuToFacingGroups: Record<string, FacingGroup>,
) => combineBySku(newFacings).reduce((acc, val) => {
  const h = val.horizontal_facings;
  const clipboardFacing = clipboardSkuToFacingGroups[val.product_num];

  if (!clipboardFacing) {
    return acc;
  }

  const clipboardHorizontalFacings = clipboardFacing.horizontal_facings;

  if (clipboardHorizontalFacings === h || clipboardHorizontalFacings < h) {
    acc.removedClipboardIds.push(clipboardFacing.id);
  }

  if (clipboardHorizontalFacings > h) {
    const facingsDiff = clipboardHorizontalFacings - h;

    const newClipboardFacing = {
      ...clipboardFacing,
      horizontal_facings: facingsDiff,
      id: generateUniqueId()
    };

    const removedClipboardId = clipboardFacing.id;

    acc.newClipboardFacings.push(newClipboardFacing);
    acc.removedClipboardIds.push(removedClipboardId);
  }

  return acc;
}, {
  removedClipboardIds: [] as Array<string>,
  newClipboardFacings: [] as Array<FacingGroup>
});

export const combineBySku = (facings: Array<FacingGroup>) => {
  const groupedClipboardFacings = facings.reduce<Record<string, FacingGroup>>((acc, facing) => {
    return {
      ...acc,
      [facing.product_num]: {
        ...facing,
        'horizontal_facings': acc[facing.product_num]
          ? acc[facing.product_num]?.horizontal_facings + facing.horizontal_facings
          : facing.horizontal_facings,
      }
    };
  }, {});

  return Object.values(groupedClipboardFacings);
};

const getNewFixturesFromClipboardItems = (
  clipboardFixtures: Array<DraggableFixture>,
  locatedFixturesById: Record<string, LocatedFixture>,
  currentFixtureNumberId: number,
  currentFixtureSegment: number,
  y: number
) => clipboardFixtures.reduce((acc, val, index) => {
  const fixture = locatedFixturesById[val.id];
  const fixtureStartPosition = (currentFixtureSegment - 1) * SEGMENT_WIDTH;
  const locatedFixtureId = currentFixtureNumberId + index + 1;

  const newFixture = {
    ...fixture,
    segment: currentFixtureSegment,
    id: locatedFixtureId,
    x: fixtureStartPosition,
    y: index === 0 ? y : y + val.bottom
  };

  return { ...acc, [val.id]: newFixture };
}, {} as Record<string, LocatedFixture>);

const getNewFixtureFromPogItems = (
  locatedFixturesById: Record<string, LocatedFixture>,
  currentFixture: LocatedFixture,
  fixtureIds: Array<number>,
  segmentShift: number,
  currentFixtureNumberId: number,
  currentFixtureSegment: number,
  id: number,
  y: number,
) => fixtureIds.reduce((acc, val, index) => {
  const fixture = locatedFixturesById[val];
  const segment = fixture.segment;
  const fixtureSegment = segment + segmentShift;
  const newSegment = val === id ? currentFixtureSegment : fixtureSegment;
  const locatedFixtureId = currentFixtureNumberId + index + 1;
  const fixtureX = (val === id ? currentFixtureSegment - 1 : fixtureSegment - 1) * SEGMENT_WIDTH;
  const fixtureY = y - currentFixture.y + fixture.y;

  const newFixture = {
    ...fixture,
    segment: newSegment,
    id: locatedFixtureId,
    x: fixtureX,
    y: fixtureY
  };

  return { ...acc, [val]: newFixture };
}, {} as Record<string, LocatedFixture>);

const draggablePegItems = (
  x: number,
  y: number,
  facingGroups: Record<string, FacingGroup>,
  facingIds: Array<string>,
  id: string,
  isEsl: boolean = false
) => facingIds
  .reduce((acc, facingId) => {
    const draggableItem = facingGroups[id];
    const facing = facingGroups[facingId];

    if (facing && draggableItem) {
      const width = facing.packaging.width * facing.horizontal_facings;
      const eslHeight = isEsl ? EXTRA_ESL_HEIGHT_OUTSIDE_FACING : 0;
      const height = facing.packaging.height + eslHeight;
      const draggableHeight = draggableItem.packaging.height + eslHeight;
      const x1 = x + facing.x - draggableItem.x;
      const y2 = y - (draggableItem.y - facing.y) + (id === facingId ? 0 : height - draggableHeight) + eslHeight;
      const x2 = x1 + width;
      const y1 = y2 - height;
      const peghole = facing?.packaging.pegholes[0];
      const p = transformPegholes(peghole, width, 1);

      acc.push({ id: facing.id, x1, x2, y1, y2, height, width, peghole: p });
    }

    return acc;
  }, [] as Array<DraggablePegItem>);

export const isFixturesOverlap = (
  newFixtures: Array<LocatedFixture>,
  locatedFixtures: Array<LocatedFixture>,
  removedFixtureIds: Array<number>
) => {
  return locatedFixtures.filter(fixture => {
    const isShelf = fixture.type === FIXTURE_TYPES.SHELF;
    const isBasket = fixture.type === FIXTURE_TYPES.BASKET;
    const isAcceptableFixtureType = isShelf || isBasket;
    const isDisplayOnly = fixture.is_display_only;
    const height = isDisplayOnly
      ? DISPLAY_HEIGHT
      : Math.max(fixture.grille_height, fixture.height);

    if (!isAcceptableFixtureType || removedFixtureIds.includes(fixture.id)) {
      return false;
    }

    const pogFixture = {
      x1: fixture.x,
      y1: fixture.y,
      x2: fixture.x + fixture.width,
      y2: fixture.y + height
    };

    return newFixtures.filter(newFixture => {
      // const isNewFixtureBasket = newFixture.type === FIXTURE_TYPES.BASKET;
      const isDisplayOnly = newFixture.is_display_only;
      const heightSlope = adjacentHeightToShelfSlopeAngle(newFixture.shelf_slope, newFixture.depth);
      const height = isDisplayOnly
        ? DISPLAY_HEIGHT
        : Math.max(newFixture.grille_height, newFixture.height, heightSlope);

      if (fixture.segment !== newFixture.segment) {
        return false;
      }

      const ghostFixture = {
        x1: newFixture.x,
        y1: newFixture.y,
        x2: newFixture.x + newFixture.width,
        y2: newFixture.y + height
      };
      const bottom = Math.max(ghostFixture.y1, pogFixture.y1);
      const top = Math.min(ghostFixture.y2, pogFixture.y2);

      return top > bottom;
    }).length > 0;
  });
};

export const isFixturesOverlapWithFacings = (
  newFixturesCollection: Record<string, LocatedFixture>,
  facingsWithoutSelected: Array<ItemCoordinates>,
  removedFixtureIds: Array<number>
) => facingsWithoutSelected.filter(facing =>
  removedFixtureIds.filter(fixtureId => {
    const fixture = newFixturesCollection[fixtureId];
    const ghostFixture = {
      x1: fixture.x,
      y1: fixture.y,
      x2: fixture.x + fixture.width,
      y2: fixture.y + fixture.height
    };
    const left = Math.max(ghostFixture.x1, facing.x1);
    const bottom = Math.max(ghostFixture.y1, facing.y1);
    const right = Math.min(ghostFixture.x2, facing.x2);
    const top = Math.min(ghostFixture.y2, facing.y2);

    return right > left && top > bottom;
  }).length > 0
);

export const isFacingsOverlapWithFixture = (
  newItems: Array<Coordinates>,
  locatedFixtures: Array<LocatedFixture>
) => newItems.filter(facing => locatedFixtures.filter(f => {
  if (f.type !== FIXTURE_TYPES.SHELF) {
    return false;
  }

  const ghostFixture = {
    x1: f.x,
    y1: f.y,
    x2: f.x + f.width,
    y2: f.y + f.height,
  };
  const left = Math.max(ghostFixture.x1, facing.x1);
  const bottom = Math.max(ghostFixture.y1, facing.y1);
  const right = Math.min(ghostFixture.x2, facing.x2);
  const top = Math.min(ghostFixture.y2, facing.y2);

  return right > left && top > bottom;
}).length > 0);

export const getUserAlert = (
  visualizerAlert: WarningData
) => {
  let userAlerts = null;
  const { warningType, data } = visualizerAlert;

  switch (warningType) {
    case 'move_basket_to_shelf_warnings':
      userAlerts = data as Array<MoveBasketToSelfWarnings>;
      break;

    case 'move_crossbar_to_peg_warnings':
      userAlerts = data as Array<MoveCrossbarToPegWarnings>;
      break;

    default:
      userAlerts = null;
      break;
  }

  return userAlerts;
};

export const getWhiteSpaceAlert = (
  visualizerAlert: WarningData,
  pixelsPerInch: number,
  fixtureSpaces: Array<FixtureSpace>,
  maxFacingHeightFixture: Record<string, number>,
  locatedFixtures: Array<LocatedFixture>,
  locatedFixtureIdToFacingIds: Record<string, Array<string>>,
  facingGroupsByIds: Record<string, FacingGroup>
) => {
  let whiteSpaceAlerts = null;
  const { warningType, data } = visualizerAlert;

  if (warningType === 'linear_whitespace_warnings') {
    const selectedAlert = data as Array<LinearWhitespaceWarnings>;
    const fixtureExistInUI = locatedFixtures.map((fixture) => fixture.id);

    whiteSpaceAlerts = selectedAlert.map((item) => {
      const updatedFixtureIds = item.located_fixture_ids.reduce((acc, id) => {
        if (fixtureExistInUI.includes(id)) acc.push(id);

        return acc;
      }, [] as Array<number>);

      if (updatedFixtureIds.length) {
        const { height, crossbarOverlappingY, pegboardOverlappingY } = getFixturesMinWhiteSpaceHeight(
          maxFacingHeightFixture,
          fixtureSpaces,
          item.located_fixture_ids,
          locatedFixtureIdToFacingIds,
          facingGroupsByIds,
          locatedFixtures
        );

        let availableHeight = crossbarOverlappingY > 0 ? crossbarOverlappingY - (item?.y || 0) : height;

        if (pegboardOverlappingY > 0 && pegboardOverlappingY < crossbarOverlappingY) {
          availableHeight = pegboardOverlappingY - (item?.y || 0);
        }

        return {
          left: (item?.x || 0) * pixelsPerInch,
          bottom: (item?.y || 0) * pixelsPerInch,
          width: (item?.width || 0) * pixelsPerInch,
          height: availableHeight * pixelsPerInch,
        };
      }

      return {
        left: 0,
        bottom: 0,
        width: 0,
        height: 0,
      };
    });
  }

  return whiteSpaceAlerts;
};

export const getLinkedProductsAlert = (visualizerAlert: WarningData, isAssortmentSweep: boolean, includeHeavyProduct: boolean) => {
  let productAlerts = null;
  const { warningType, data } = visualizerAlert;

  const getProducts = (data: any[], key: string) => {
    const products: Array<string> = [];

    for (const item of data) {
      Array.isArray(item[key]) ? products.push(...item[key]) : products.push(item[key]);
    }

    return products;
  };

  switch (warningType) {
    case 'excluded_product_warnings_no_fit':
    case 'excluded_product_warnings_too_tight':
      const excludedAlert = data as Array<ExcludedProductWarnings>;
      const excludedProducts = excludedAlert.filter((item) => item.in_clipboard).map((item) => item.product_num);

      productAlerts = Array.from(new Set(excludedProducts));
      break;

    case 'product_missing_blocking_data_warnings':
      const missingBlockingAttrAlert = data as Array<ProductMissingBlockingDataWarnings>;
      const missingBlockingAttrProducts = getProducts(missingBlockingAttrAlert, 'product_nums');

      productAlerts = Array.from(new Set(missingBlockingAttrProducts));
      break;

    case 'product_packaging_warnings':
    case 'too_much_supply_warnings':
    case 'move_crossbar_to_peg_warnings':
    case 'move_basket_to_shelf_warnings':
    case 'fd_dwo_bincap_warnings':
    case 'no_longer_available_after_refresh_warnings':
    case 'removed_from_aisle_assortment_after_refresh_warnings': {
      const products = getProducts(data, 'product_num');

      productAlerts = Array.from(new Set(products));
      break;
    }
    case 'assortment_sweep_warnings':
      const assortmentSweepProducts = getProducts(data, 'product_num');

      productAlerts = isAssortmentSweep ? Array.from(new Set(assortmentSweepProducts)) : null;
      break;
    case 'heavy_product_height_warnings': {
      if (includeHeavyProduct) {
        const products = getProducts(data, 'product_num');

        productAlerts = Array.from(new Set(products));
      }

      break;
    }
    default:
      productAlerts = null;
      break;
  }

  return productAlerts;
};

export const getHeavyProductAlerts = (
  visualizerAlert: WarningData,
) => {
  let heavyProductAlerts = null;
  const { warningType, data } = visualizerAlert;

  if (warningType === 'heavy_product_height_warnings') {
    const selectedAlert = data as Array<HeavyProductHeightWarnings>;

    heavyProductAlerts = selectedAlert;
  }

  return heavyProductAlerts;
};

export const getWeightRestrictionAlert = (
  visualizerAlert: WarningData,
) => {
  let weightRestrictionAlerts = null;
  const { warningType, data } = visualizerAlert;

  if (warningType === 'weight_restriction_warnings') {
    const selectedAlert = data as Array<WeightRestrictionWarnings>;

    weightRestrictionAlerts = selectedAlert;
  }

  return weightRestrictionAlerts;
};

export const isProductHasAlert = (
  productAlerts: Array<string> | null,
  assortmentSweepAlerts: Array<string> | null,
  userAlerts: Array<MoveBasketToSelfWarnings> | null,
  facingGroup: FacingGroup,
  weightRestrictionAlerts: Array<WeightRestrictionWarnings> | null,
  fixtureIdToFacingIds: Record<string, string[]>,
  heavyProductHeightAlerts: Array<HeavyProductHeightWarnings> | null,
  locatedFixtures: Array<LocatedFixture>
) => {
  let isWarning = productAlerts !== null && productAlerts.includes(facingGroup.product_num);
  let warningGroup: AlertType = 'nonCritical';

  if (userAlerts) {
    const {
      product_num: fGProductNum,
      x: fGX,
      y: fGY,
      fixtureType: fGFixtureType,
    } = facingGroup;
    const matchedProduct = userAlerts.filter(
      (a) => fGProductNum === a.product_num
    );
    const matchedWarning = userAlerts.filter((a) => {
      const {
        product_num: alertProductNum,
        x: alertPositionX,
        y: alertPositionY,
        to_fixture_type: alertFixtureType,
      } = a;

      return (
        fGProductNum === alertProductNum &&
        fGX === alertPositionX &&
        fGY === alertPositionY &&
        fGFixtureType === alertFixtureType
      );
    });

    if (matchedProduct.length) isWarning = false;
    if (matchedWarning.length) isWarning = true;
  }

  if (weightRestrictionAlerts) {
    const fixtureIdsInAlert = weightRestrictionAlerts.map(alert => alert.fixture_id);

    const facingGroupsIds = fixtureIdsInAlert.reduce((acc, id) => {
      if (fixtureIdToFacingIds[id]) {
        acc = [...acc, ...fixtureIdToFacingIds[id]];
      }

      return acc;
    }, [] as string[]);

    if (facingGroupsIds.includes(facingGroup.id)) {
      warningGroup = 'critical';
      isWarning = true;
    }
  }

  if (!isWarning && assortmentSweepAlerts && assortmentSweepAlerts.length) {
    if (assortmentSweepAlerts.includes(facingGroup.product_num)) {
      warningGroup = 'critical';
      isWarning = true;
    }
  }

  if (heavyProductHeightAlerts) {
    const locatedFixture = locatedFixtures.filter(lF => facingGroup.located_fixture_id === lF.id);
    const fixtureId = locatedFixture.length > 0 ? locatedFixture[0].fixture_id : null;
    const heavyAlertForFacing = heavyProductHeightAlerts.filter(
      hA => hA.product_num === facingGroup.product_num && hA.fixture_id === fixtureId && facingGroup.y >= 48
    );

    isWarning = heavyAlertForFacing.length > 0;
  }

  return { isWarning, warningGroup };
};

export const isFixtureHasAlert = (weightRestrictionAlerts: Array<WeightRestrictionWarnings> | null, id: number) => {
  if (!weightRestrictionAlerts) return false;

  const isWarningExistForFixtureId = weightRestrictionAlerts.filter(alert => Number(alert.fixture_id) === id);

  return isWarningExistForFixtureId.length > 0;
};

export const getAlertTabData = (tabs: Array<TabInfo>, alerts: WarningsData) => {
  if (alerts.length === 0) return tabs;

  const criticalWarnings = alerts.filter(alert => criticalAlertTypes.includes(alert.warningType));
  const nonCriticalWarnings = alerts.filter(alert => !criticalAlertTypes.includes(alert.warningType));
  const newTabs = [...tabs];

  newTabs[0].warnings = criticalWarnings.length ? criticalWarnings : [];
  newTabs[1].warnings = nonCriticalWarnings.length ? nonCriticalWarnings : [];

  return newTabs;
};

export const getIndexToHighlight = (tabs: Array<TabInfo>) => {
  return tabs[0].warnings && tabs[0].warnings.length > 0 ? 0 : 1;
};

export const isNewFacingsOverlapWithFixtures = (
  newFacings: Array<Coordinates>,
  locatedFixtures: Array<LocatedFixture>,
) => locatedFixtures.filter(fixture => {
  const isShelf = fixture.type === FIXTURE_TYPES.SHELF;
  const isBasket = fixture.type === FIXTURE_TYPES.BASKET;
  const isAcceptableFixtureType = isShelf || isBasket;

  if (!isAcceptableFixtureType) {
    return false;
  }

  const pogFixture = {
    x1: fixture.x,
    y1: fixture.y,
    x2: fixture.x + fixture.width,
    y2: fixture.y + (isBasket ? fixture.grille_height : fixture.height)
  };

  return newFacings.filter(facing => {
    const left = Math.max(pogFixture.x1, facing.x1);
    const bottom = Math.max(pogFixture.y1, facing.y1);
    const right = Math.min(pogFixture.x2, facing.x2);
    const top = Math.min(pogFixture.y2, facing.y2);

    return right > left && top > bottom;
  }).length > 0;
});

export const groupAlertsByFixtureType = (
  weightRestrictionAlerts: Array<WeightRestrictionWarnings>,
  locatedFixtures: Array<LocatedFixture>
) => {
  const groupedAlertsByFixtureType = weightRestrictionAlerts.reduce((acc, alert) => {
    const fixtures = locatedFixtures.filter(fixture => fixture.id === Number(alert.fixture_id));

    if (fixtures.length) {
      const fixture = fixtures[0];

      if (acc[fixture.type]) {
        acc[fixture.type] = [...acc[fixture.type], alert];
      } else {
        acc[fixture.type] = [alert];
      }
    }

    return acc;
  }, {} as Record<string, Array<WeightRestrictionWarnings>>);

  return groupedAlertsByFixtureType;
};

export const isSpecialityFixture = (fixtureType: string) =>
  fixtureType === FIXTURE_TYPES.SPECIALTY;

export const isPeggableFixture = (fixtureType: string) =>
  fixtureType === FIXTURE_TYPES.CROSSBAR ||
  fixtureType === FIXTURE_TYPES.PEGBOARD ||
  fixtureType === FIXTURE_TYPES.FLATBAR;

export const checkIsIdenticalFixtures = (
  fixturesSpaces: Array<FixtureSpace>,
  facing: Coordinates,
  locatedFixturesById: Record<string, LocatedFixture>
) => {
  const fixtures = fixturesSpaces.filter(({ x1, x2, y1, y2 }) => (
    facing.x1 <= x2 && facing.x2 >= x1 && facing.y2 <= y2 && facing.y1 >= y1
  )).sort((a, b) => a.y2 - b.y2);

  const [fixture] = fixtures;
  const isIdenticalFixtures = fixtures.every(f =>
    f.y1 === fixture.y1 &&
    locatedFixturesById[f.id].type === locatedFixturesById[fixture.id].type &&
    locatedFixturesById[f.id].shelf_slope === locatedFixturesById[fixture.id].shelf_slope
  );

  return isIdenticalFixtures;
};

export const filterByUniqueSku = (
  facingGroups: Array<FacingGroup>,
  uniquePlanogramFacings: Record<string, number>
) => {
  const groupClipboard = facingGroups.reduce<Record<string, FacingGroupExtended>>((acc, facing) => {
    return {
      ...acc,
      [facing.product_num]: {
        ...facing,
        id: generateUniqueId(),
        'vertical_facings': acc[facing.product_num]
          ? Math.min(acc[facing.product_num].vertical_facings, facing.vertical_facings)
          : facing.vertical_facings,
        'horizontal_facings': acc[facing.product_num]
          ? acc[facing.product_num]?.horizontal_facings + facing.horizontal_facings
          : facing.horizontal_facings,
        'depth_facings': acc[facing.product_num]
          ? Math.min(acc[facing.product_num].depth_facings, facing.depth_facings)
          : facing.depth_facings,
        'capped_horizontal_facings': acc[facing.product_num]
          ? acc[facing.product_num]?.horizontal_facings + facing.horizontal_facings
          : facing.horizontal_facings,
        'capped_vertical_facings': acc[facing.product_num]
          ? Math.min(acc[facing.product_num].capped_vertical_facings, facing.capped_vertical_facings)
          : facing.capped_vertical_facings,
        'capped_depth_facings': acc[facing.product_num]
          ? Math.min(acc[facing.product_num].capped_depth_facings, facing.capped_depth_facings)
          : facing.capped_depth_facings,
      }
    };
  }, {});

  const groupNewClipboardFacings: Array<FacingGroupExtended> = Object.values(groupClipboard)
    .filter(item => uniquePlanogramFacings[item.product_num] <= item.horizontal_facings);

  return groupNewClipboardFacings;
};

export const contextSegmentMenuBoundary = (
  container: HTMLDivElement,
  sourceClient: CoordinatePoint,
  zoom: number
) => {
  const { left, height, top, width } = container.getBoundingClientRect();
  const { x, y } = sourceClient;
  const currentX = x - left;
  const currentY = height - y + top;
  const bottom = PEGBOARD_TEMPLATE.y * zoom;

  return currentX > 0 &&
    currentX < width &&
    currentY > bottom &&
    currentY < height;
};

export const correctionPositionPegholes = (
  facingSpace: {
    id: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  },
  fixtureHeight: Array<{
    id: number;
    fixtureId: string | number;
    segment: number;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    z: number;
    isDisplayOnly: boolean;
    isCrossbar: boolean;
    isPegboard: boolean;
  }>,
  peghole: Peghole,
  pegboardInitialConfig: PegboardStartHoles
) => {
  const [occupiedFixture] = fixtureHeight.filter(fixture => {
    const left = Math.max(facingSpace.x1, fixture.x1);
    const bottom = Math.max(facingSpace.y1, fixture.y1);
    const right = Math.min(facingSpace.x2, fixture.x2);
    const top = Math.min(facingSpace.y2, fixture.y2);

    return right > left && top > bottom && fixture.isPegboard;
  });
  const absolutePegholeX = facingSpace.x1 + peghole.peghole_x;
  const absolutePegholeY = facingSpace.y2 - peghole.peghole_y;
  const { startHoleX, startHoleY } = pegboardInitialConfig;

  const shiftPegholeX = absolutePegholeX - occupiedFixture.x1 - startHoleX;
  const shiftPegholeY = absolutePegholeY - occupiedFixture.y1 - startHoleY;

  const extremumPegholeX = occupiedFixture.x2 - occupiedFixture.x1 - startHoleX;
  const extremumPegholeY = occupiedFixture.y2 - occupiedFixture.y1 - startHoleY;

  const correctionPegholeX1 = Math.floor(Math.min(shiftPegholeX, extremumPegholeX));
  const correctionPegholeY1 = Math.floor(Math.min(shiftPegholeY, extremumPegholeY));

  const correctionPegholeX2 = Math.ceil(Math.min(shiftPegholeX, extremumPegholeX));
  const correctionPegholeY2 = Math.ceil(Math.min(shiftPegholeY, extremumPegholeY));

  const correctionLeftBottom: Point = {
    x: correctionPegholeX1,
    y: correctionPegholeY1,
  };

  const correctionLeftTop: Point = {
    x: correctionPegholeX1,
    y: correctionPegholeY2,
  };

  const correctionRightBottom: Point = {
    x: correctionPegholeX2,
    y: correctionPegholeY1,
  };

  const correctionRightTop: Point = {
    x: correctionPegholeX2,
    y: correctionPegholeY2,
  };

  const distanceLeftBottom = Math.sqrt(Math.pow(correctionLeftBottom.x - shiftPegholeX, 2) + Math.pow(correctionLeftBottom.y - shiftPegholeY, 2));
  const distanceLeftTop = Math.sqrt(Math.pow(correctionLeftTop.x - shiftPegholeX, 2) + Math.pow(correctionLeftTop.y - shiftPegholeY, 2));
  const distanceRightBottom = Math.sqrt(Math.pow(correctionRightBottom.x - shiftPegholeX, 2) + Math.pow(correctionRightBottom.y - shiftPegholeY, 2));
  const distanceRightTop = Math.sqrt(Math.pow(correctionRightTop.x - shiftPegholeX, 2) + Math.pow(correctionRightTop.y - shiftPegholeY, 2));

  const foundMinDistance = Math.min(distanceLeftBottom, distanceLeftTop, distanceRightBottom, distanceRightTop);

  let minPeghole: Point = {
    x: 0,
    y: 0,
  };

  switch (foundMinDistance) {
    case distanceLeftBottom:
      minPeghole = correctionLeftBottom;
      break;
    case distanceLeftTop:
      minPeghole = correctionLeftTop;
      break;
    case distanceRightBottom:
      minPeghole = correctionRightBottom;
      break;
    case distanceRightTop:
      minPeghole = correctionRightTop;
      break;
  }

  const shift: Point = {
    x: minPeghole.x - shiftPegholeX,
    y: minPeghole.y - shiftPegholeY,
  };

  const x = facingSpace.x1 + shift.x;
  const y = facingSpace.y1 + shift.y;

  return {
    x,
    y,
    occupiedFixture,
  };
};

export const processDroppingWorkbenchItems = (
  processedMousePositions: ReturnType<typeof getProcessedPositionsFromMonitor>,
  monitor: DropTargetMonitor,
  selectedFacingsGroup: Record<string, Array<number>>,
  facingGroupsByIds: Record<string, FacingGroupExtended>,
  locatedFixturesIdToType: Record<string, FixtureType>,
  fixtureCrossbar: FixtureSpace[],
  fixtureFlatbar: FixtureSpace[],
  fixturePegboard: FixtureSpace[],
  fixtureSpaces: FixtureSpace[],
  clipboardSkuToFacingGroups: Record<string, FacingGroup>,
  aisleHeight: number,
  scale: number,
  selectedFixtureType: string,
  isWorkbenchOpened: boolean,
  viewContainer: DOMRect,
  scrollHeight: number,
  isScroll: boolean
) => {
  const type = monitor.getItemType() as string;
  const dropEffect = getDropEffectFromStorage();
  const isCopy = dropEffect === DROP_EFFECTS.COPY;

  if (!processedMousePositions || !type) {
    return false;
  }

  let {
    itemRootPosition: { x, y },
    correctedClipboardItem,
    containerBoundary: {
      right: containerRightPosition,
      top: containerTopPosition
    },
  } = processedMousePositions;
  const zoom = isWorkbenchOpened
    ? scale
    : (scale * aisleHeight - WORKBENCH_EXTRA_HEIGHT) / aisleHeight;

  y = isWorkbenchOpened ? y : WORKBENCH_HEIGHT;
  const { left, right } = viewContainer;
  const draggableItem = monitor.getItem() as ClipboardDraggableObject;

  if (!draggableItem) return false;

  let {
    rearangedItems: items,
    facingsIds: activeItems,
    groupedWidth: itemsWidth,
    hmax,
    facingsIds,
    clipboardOffsetTop = 0,
    clipboardOffsetRight = 0,
    lastItemWidthScaled = 0,
  } = draggableItem;
  const width = itemsWidth / zoom;
  const horizontalSpacing =
    facingsIds?.length > 1 ? HORIZONTAL_SPACING * zoom : 0;
  const xOffset = correctedClipboardItem.x + clipboardOffsetRight - itemsWidth + lastItemWidthScaled + horizontalSpacing;
  const clipboardXOffset = getClipboardXOffset(xOffset, type);
  const clipboardYOffset = Object.values(DraggableSkuClipboardItems).includes(type)
    ? clipboardOffsetTop - correctedClipboardItem.y
    : 0;
  const isClipboardType = isClipboardItem(type);
  const isWorkbenchType = isWorkbenchItem(type);

  if (isWorkbenchOpened) {
    if (x + itemsWidth >= containerRightPosition) {
      x = containerRightPosition - itemsWidth;
    } else if (x < 0) {
      x = 0;
    }

    if (y >= containerTopPosition - WORKBENCH_MARGIN_TOP) {
      y = containerTopPosition - WORKBENCH_MARGIN_TOP;
    }
  }

  if (!isWorkbenchOpened) {
    if (x + itemsWidth >= right) {
      x = right - (itemsWidth / scale) * zoom - left;
    } else if (x < left) {
      x = 0;
    } else {
      x = x - left;
    }
  }

  const x1 = (x + clipboardXOffset) / zoom;
  const y1 = (y + clipboardYOffset) / zoom;

  const height = hmax / scale;

  const draggableItemRect = makeDraggableRect (
    x1,
    y1,
    width,
    height
  );

  const { draggableLeft, draggableBottom } = draggableItemRect;

  let removedId: Array<string> = [];
  let facings: Array<FacingGroupExtended> = [];
  let removedClipboardId: Array<string> = [];
  let newClipboardFacings: Array<FacingGroupExtended> = [];
  let newClipboardFixturesFacings: Array<FacingGroupExtended> = [];
  let removedClipboardFixturesFacings: Array<string> = [];
  let removedWorkbenchFacingId: Array<string> = [];
  let newWorkbenchFacings: Array<FacingGroupExtended> = [];

  const extraMarginTop = isScroll ? scrollHeight + WORKBENCH_MARGIN_TOP : WORKBENCH_MARGIN_TOP;
  const isPegboard = selectedFixtureType === FIXTURE_TYPES.PEGBOARD;

  items = isPegboard ? splitPegboardFacingsGroup(items, facingGroupsByIds, selectedFacingsGroup) : items;
  const recalculatedPositions: Array<FacingGroupExtended> = items.reduce(
    (acc, val, index) => {
      const draggableItem = facingGroupsByIds[val.id];
      const facings = selectedFacingsGroup[val.id];
      const {
        horizontal_facings: h,
        cap_nested: capNested,
        capped_horizontal_facings: cappedHorFacing,
      } = draggableItem;
      const isYCapped = capNested === Nesting.YCapped;
      const isFacingGroupSelected =
        facings.length === 0 || facings.length === h;
      const newHorizontalFacings = isPegboard ? 1 : isFacingGroupSelected
        ? h
        : facings.length;
      const xPosition =
        index === 0
          ? draggableLeft
          : acc[index - 1].x +
            acc[index - 1].packaging.width *
              acc[index - 1].horizontal_facings +
            HORIZONTAL_SPACING;
      const yPosition = isWorkbenchOpened ? draggableBottom : draggableBottom - extraMarginTop / zoom;

      acc.push({
        ...facingGroupsByIds[val.id],
        id: generateUniqueId(),
        x: xPosition,
        y: yPosition,
        horizontal_facings: newHorizontalFacings,
        capped_horizontal_facings: isYCapped
          ? newHorizontalFacings
          : cappedHorFacing
      });

      return acc;
    },
    [] as Array<FacingGroupExtended>
  );

  const newRemainingFacings = Object.keys(selectedFacingsGroup).reduce(
    (acc, val) => {
      const facingGroup = facingGroupsByIds[val];
      const { horizontal_facings: h } = facingGroup;
      const facings = selectedFacingsGroup[val];
      const facingsNumber = facings.length;

      if (facingsNumber === 0 || facingsNumber === h) {
        return acc;
      }

      const newFacingsCollection = processCreatingBoardFacings(
        facingGroup,
        facings,
        fixtureSpaces,
        locatedFixturesIdToType,
        selectedFixtureType,
        fixtureCrossbar,
        fixtureFlatbar,
        fixturePegboard
      );

      acc.push(...newFacingsCollection);

      return acc;
    },
    [] as Array<FacingGroupExtended>
  );

  switch (type) {
    case DraggableWorkbenchItems.DefauldItem:
    case DraggableWorkbenchItems.ZNested:
    case DraggableWorkbenchItems.YCapped: {
      newWorkbenchFacings = [...recalculatedPositions, ...newRemainingFacings];
      facings = [];
    }

      break;

    default: {
      newWorkbenchFacings = recalculatedPositions;
      facings = newRemainingFacings;
    }
  }

  if (isWorkbenchType) {
    removedWorkbenchFacingId = isCopy ? [] : activeItems;
  } else if (isClipboardType) {
    removedClipboardId = activeItems;

    const {
      removedClipboardItemIds, newClipboardItems
    } = processRemovingClipbordItems(selectedFacingsGroup, facingGroupsByIds);

    switch (type) {
      case DraggableClipboardFixtureSkuItems.FixtureDefauldItem:
      case DraggableClipboardFixtureSkuItems.FixtureYCapped:
      case DraggableClipboardFixtureSkuItems.FixtureYNested:
      case DraggableClipboardFixtureSkuItems.FixtureZNested: {

        const connectedSkus = processRemovingSkusConnectedToFixture(
          newWorkbenchFacings,
          clipboardSkuToFacingGroups
        );

        newClipboardFixturesFacings = removedClipboardItemIds.reduce((acc, id) => {
          const facing = facingGroupsByIds[id];
          const selectedFacings = selectedFacingsGroup[id].length;

          if (selectedFacings === facing.horizontal_facings || selectedFacings === 0) return acc;

          const h = facing.horizontal_facings - selectedFacings;
          const isYCapped = facing.cap_nested === Nesting.YCapped;

          return acc.concat({
            ...facing,
            id: generateUniqueId(),
            horizontal_facings: h,
            capped_horizontal_facings: isYCapped
              ? h
              : facing.capped_horizontal_facings
          });
        }, [] as Array<FacingGroupExtended>);

        removedClipboardId = removedClipboardId.concat(connectedSkus.removedClipboardIds);
        removedClipboardFixturesFacings = removedClipboardItemIds;
        newClipboardFacings = newClipboardFacings.concat(connectedSkus.newClipboardFacings);
      }

        break;

      default: {
        removedClipboardId = activeItems;
        newClipboardFacings = newClipboardFacings.concat(newClipboardItems);
      }
    }
  } else {
    removedId = isCopy ? [] : activeItems;
  }

  return {
    ...historyStepItem,
    removedId,
    newFacings: facings,
    newWorkbenchFacings,
    removedClipboardId,
    removedWorkbenchFacingId,
    newClipboardFixturesFacings,
    newClipboardFacings,
    removedClipboardFixturesFacings
  };
};

export const processMovingFacingsToWorkbench = (
  collectionIds: Record<string, Array<number>>,
  facingsByIds: Record<string, FacingGroupExtended>,
  fixturesCrossbar: FixtureSpace[],
  fixturesFlatbar: FixtureSpace[],
  fixturesPegboard: FixtureSpace[],
  fixtureSpaces: FixtureSpace[],
  locatedFixturesIdToType: Record<string, FixtureType>,
  selectedFixtureType: FixtureType | string,
  zoom: number,
  scrollHeight: number,
  isScroll: boolean,
  workbenchScrollLeft: number,
  pogScrollLeft: number
) => {
  const { newFacings, newWorkbenchFacings } = Object.keys(collectionIds).reduce(
    (acc, val) => {
      const facingGroup = facingsByIds[val];
      const facings = collectionIds[val];
      const facingsNumber = facings.length;
      const horizontalFacings = facingGroup.horizontal_facings;
      let firstSelectedFacing = 0;

      if (facingsNumber === 0 || horizontalFacings === facingsNumber) {
        const newClipboardItem = processCreatingWorkbenchItem(
          facingGroup,
          horizontalFacings,
          zoom,
          firstSelectedFacing,
          scrollHeight,
          isScroll,
          workbenchScrollLeft,
          pogScrollLeft
        );

        acc.newWorkbenchFacings.push(newClipboardItem);
      } else {
        firstSelectedFacing = Math.min(...facings);
        const newFacingItem = processCreatingBoardFacings(
          facingGroup,
          facings,
          fixtureSpaces,
          locatedFixturesIdToType,
          selectedFixtureType,
          fixturesCrossbar,
          fixturesFlatbar,
          fixturesPegboard
        );
        const newWorkbenchFacings = processCreatingWorkbenchItem(
          facingGroup,
          facingsNumber,
          zoom,
          firstSelectedFacing,
          scrollHeight,
          isScroll,
          workbenchScrollLeft,
          pogScrollLeft
        );

        acc.newFacings.push(...newFacingItem);
        acc.newWorkbenchFacings.push(newWorkbenchFacings);
      }

      return acc;
    },
    {
      newFacings: [] as Array<FacingGroupExtended>,
      newWorkbenchFacings: [] as Array<FacingGroupExtended>,
    }
  );

  return {
    newFacings,
    newWorkbenchFacings,
  };
};

export const processCreatingWorkbenchItem = (
  facingGroup: FacingGroupExtended,
  numberOfSelectedFacings: number,
  zoom: number,
  facingPosition: number,
  scrollHeight: number,
  isScroll: boolean,
  workbenchScrollLeft: number,
  pogScrollLeft: number
) => {
  const isYCapped = facingGroup.cap_nested === Nesting.YCapped;
  const height = getFacingHeight(
    facingGroup?.packaging?.height,
    facingGroup?.vertical_facings,
    facingGroup?.cap_nested,
    facingGroup?.packaging?.nested_height,
    facingGroup?.cappedPackaging?.height,
    facingGroup?.capped_vertical_facings
  );
  const WORKBENCH_HEIGHT = isScroll ? 177 - scrollHeight : 177;
  const MARGIN_TOP = 12;
  const POG_LEFT_MARGIN = 90;
  const scrollLeft1 = workbenchScrollLeft / zoom;
  const scrollLeft2 = pogScrollLeft / zoom;
  const y = WORKBENCH_HEIGHT / zoom - height - MARGIN_TOP / zoom;
  const x = scrollLeft1 + (facingGroup.x - scrollLeft2) + facingPosition * facingGroup.packaging.width + POG_LEFT_MARGIN / zoom;
  const worbenchFacing: FacingGroupExtended = {
    ...facingGroup,
    y,
    x,
    id: generateUniqueId(),
    horizontal_facings: numberOfSelectedFacings,
    capped_horizontal_facings: isYCapped
      ? numberOfSelectedFacings
      : facingGroup.capped_horizontal_facings
  };

  return worbenchFacing;
};

type FacingType = Coordinates & {
  id: string;
  height: number;
};

export const correctionWorkbenchItems = (
  workbenchFacings: Array<FacingGroupExtended>,
  newWorkbenchFacings: Array<FacingGroupExtended>,
  zoom: number
) => {
  const newWorkbenchItems = workbenchFacings.map(createFacingItem);
  const MARGIN = 4;
  const margin = MARGIN / zoom;

  const recalculatedWorkbenchItemsPosition = newWorkbenchFacings
    .sort((f1, f2) => f1.x - f2.x)
    .reduce((acc, val, index) => {
      let itemCoordinates = createFacingItem(val);

      if (index > 0) {
        const isOverlapping = acc.some((item) => {
          const left = Math.max(item.x1, itemCoordinates.x1);
          const bottom = Math.max(item.y1, itemCoordinates.y1);
          const right = Math.min(item.x2, itemCoordinates.x2);
          const top = Math.min(item.y2, itemCoordinates.y2);

          return right > left && top > bottom;
        });

        if (isOverlapping) {
          const prevItem = acc[index - 1];
          const y1 = prevItem.y1 - itemCoordinates.height - margin;

          itemCoordinates = {
            ...itemCoordinates,
            y1,
            y2: prevItem.y1 - margin
          };
        }
      }

      acc.push(itemCoordinates);

      return acc;
    }, [] as Array<FacingType>)
    .reduce((collection, f) => {
      collection[f.id] = f;

      return collection;
    }, {} as Record<string, FacingType>);

  return newWorkbenchFacings
    .map((item) => ({
      ...item,
      id: item.id,
      y: recalculatedWorkbenchItemsPosition[item.id].y1,
    }))
    .reduce((acc, val) => {
      const itemCoordinates = createFacingItem(val);

      let newFacings = [] as Array<FacingType>;

      if (acc.length) {
        newFacings = acc.map(createFacingItem);
      }

      const isItemOverlap = newWorkbenchItems
        .concat(newFacings)
        .some((item) => {
          const left = Math.max(item.x1, itemCoordinates.x1);
          const bottom = Math.max(item.y1, itemCoordinates.y1);
          const right = Math.min(item.x2, itemCoordinates.x2);
          const top = Math.min(item.y2, itemCoordinates.y2);
          const isOverlap = right > left && top > bottom;

          return isOverlap;
        });

      let facing = val;

      if (isItemOverlap) {
        const workbenchFacingsBoundaryOverlaps = newWorkbenchItems
          .concat(newFacings)
          .filter((item) => {
            const left = Math.max(item.x1, itemCoordinates.x1);
            const right = Math.min(item.x2, itemCoordinates.x2);

            return right > left;
          });

        const bottomPositions = workbenchFacingsBoundaryOverlaps
          .filter((item) => {
            return newWorkbenchItems.concat(newFacings).some((wItem) => {
              const left = Math.max(wItem.x1, itemCoordinates.x1);
              const bottom = Math.max(
                wItem.y1,
                item.y1 - itemCoordinates.height - margin
              );
              const right = Math.min(wItem.x2, itemCoordinates.x2);
              const top = Math.min(wItem.y2, item.y1 - margin);
              const isOverlap = right > left && top > bottom;

              return isOverlap;
            });
          })
          .map((item) => item.y1);

        const availableYCoordinates = workbenchFacingsBoundaryOverlaps
          .filter((item) => !bottomPositions.includes(item.y1))
          .map((item) => item.y1);

        if (availableYCoordinates.length) {
          const topPosition = Math.max(...availableYCoordinates);
          const y = topPosition - itemCoordinates.height - margin;

          facing = {
            ...val,
            y
          };
        }
      }

      acc.push(facing);

      return acc;
    }, [] as Array<FacingGroup>);
};

const createFacingItem = (facingGroup: FacingGroup) => {
  const {
    horizontal_facings: h,
    vertical_facings: v,
    packaging: { height, nested_height: nH, width },
    cap_nested: cN,
    capped_vertical_facings: cV,
    cappedPackaging: { height: cH },
    id,
    x,
    y,
  } = facingGroup;
  const facingHeight = getFacingHeight(height, v, cN, nH, cH, cV);
  const facingWidth = width * h;

  return {
    id,
    x1: x,
    x2: x + facingWidth,
    y1: y,
    y2: y + facingHeight,
    height: facingHeight,
  };
};

export const correctWorkbenchFacing = (facingGroup: FacingGroup, zoom: number) => {
  const {
    y,
    vertical_facings: v,
    packaging: { height, nested_height: nH },
    cap_nested: cN,
    capped_vertical_facings: cV,
    cappedPackaging: { height: cH }
  } = facingGroup;
  const facingHeight = getFacingHeight(height, v, cN, nH, cH, cV);
  const topPosition = (y + facingHeight) * zoom;
  let correctedY = y;

  if (topPosition > 155) {
    correctedY = y - (topPosition - 155) / zoom;
  }

  return {
    ...facingGroup,
    y: correctedY
  };
};

const splitPegboardFacingsGroup = (
  items: Array<FacingPack>,
  facingGroupsByIds: Record<string, FacingGroup>,
  selectedFacings: Record<string, Array<number>>,
) => {
  return items.reduce((acc, item) => {
    const facingGroup = facingGroupsByIds[item.id];
    const {
      id,
      horizontal_facings: h,
      packaging: { width },
      x,
    } = facingGroup;

    if (h === 1) return acc.concat(item);
    const facings = selectedFacings[id].length;
    const length = facings === 0 || facings === h ? h : facings;
    const newFacingPack = Array.from({ length }).map((_, k) => ({
      ...item,
      x: x + width * k + HORIZONTAL_SPACING
    }));

    return acc.concat(newFacingPack);
  }, [] as Array<FacingPack>);
};

export const getFacingGroupByAltOrientation = (facingGroup: FacingGroup, product: Product) => {
  if (facingGroup && product) {
    const { orientation_code } = facingGroup;
    const { alt_orientations } = product;
    const isAlt = alt_orientations.includes(orientation_code);
    const merch_style = isAlt ? 'ALTERNATE' : 'UNIT';
    const updateFacingGroup = {
      ...facingGroup,
      packaging: {
        ...facingGroup.packaging,
        merch_style,
      }
    };

    return updateFacingGroup;
  }

  return facingGroup;
};

const chunk = (arr: Array<number>, size: number) => {
  const length = Math.floor(arr.length / size);
  const isOdd = arr.length % 2 !== 0;

  return Array.from({ length }, (_, i) =>
    arr.slice(
      i * size,
      isOdd && length - 1 === i ? (i + 2) * size : i * size + size
    )
  );
};

export const groupByObstructedRows = (obstructedRows: number[] = []) =>
  obstructedRows
    .reduce((acc, val) => {
      const previousGroup = acc[acc.length - 1];

      if (
        previousGroup &&
        previousGroup[previousGroup.length - 1] + 1 === val
      ) {
        acc[acc.length - 1].push(val);
      } else {
        acc[acc.length] = [val];
      }

      return acc;
    }, [] as Array<Array<number>>)
    .reduce((collection, row) => {
      if (row.length === 1) return collection;

      collection.push(...chunk(row, 2));

      return collection;
    }, [] as Array<Array<number>>);

export const removePhantomElement = () => {
  const el = document.querySelector('#workbench-drop-zone .workbench-phantom-facing') as HTMLElement;

  if (el) {
    el.remove();
  }
};

export const getProductPositionInWorkbench = (
  monitor: DragLayerMonitor | DropTargetMonitor,
  parentElement: HTMLDivElement | null,
  viewScale: number,
  minWorkbenchPosition: number,
  isWorkbenchOpened: boolean
) => {
  if (!isWorkbenchOpened) {
    return false;
  }

  const sourceClientOffset = monitor.getSourceClientOffset() as CoordinatePoint;
  const dragObject = monitor.getItem();
  const positions = getProcessedPositionsFromMonitor(
    monitor,
    parentElement,
  );
  const type = monitor.getItemType() as string;
  const isDraggableFixtureItem = [
    ...Object.values(DraggableFixtureItems),
    ...Object.values(DraggableClipboardFixtureItems)
  ].includes(type);

  if (sourceClientOffset && dragObject && positions && !isDraggableFixtureItem) {
    const {
      itemRootPosition: { x: rootX, y: rootY },
      correctedClipboardItem,
    } = positions;
    const {
      hmax,
      groupedWidth,
      clipboardOffsetRight,
      lastItemWidthScaled,
      clipboardOffsetTop,
      facingsIds = [],
    } = dragObject;
    const horizontalSpacing = facingsIds?.length > 1 ? HORIZONTAL_SPACING : 0;
    const xOffset = correctedClipboardItem.x + clipboardOffsetRight - groupedWidth + lastItemWidthScaled + horizontalSpacing * viewScale;
    const clipboardXOffset = getClipboardXOffset(xOffset, type);
    const clipboardYOffset = Object.values(DraggableSkuClipboardItems).includes(
      type
    )
      ? clipboardOffsetTop - correctedClipboardItem.y
      : 0;
    const x = (rootX + clipboardXOffset) / viewScale;
    const y = (rootY + clipboardYOffset) / viewScale;
    const width = groupedWidth / viewScale;
    const height = hmax / viewScale;
    const draggableItem = makeDraggableRect(x, y, width, height);
    const { draggableBottom } = draggableItem;

    if (
      (minWorkbenchPosition > 0 && draggableBottom < 0) ||
      (minWorkbenchPosition < 0 && draggableBottom < minWorkbenchPosition)
    ) {
      if (!document.querySelector('#workbench-drop-zone .workbench-phantom-facing')) {
        const el = document.getElementById(
          'workbench-drop-zone'
        ) as HTMLElement;
        const div = document.createElement('div');
        const y = minWorkbenchPosition * viewScale - WORKBENCH_FACING_SPACE - hmax;

        div.classList.add('workbench-phantom-facing');
        div.style.bottom = `${y}px`;

        if (el) {
          el.appendChild(div);
        }
      }
    }

    if (draggableBottom > minWorkbenchPosition) {
      if (document.querySelector('#workbench-drop-zone .workbench-phantom-facing')) {
        removePhantomElement();
      }
    }

    return true;
  }

  return false;
};

export const setActiveSplicerClasses = (ids: string[]) => {
  ids.forEach(id => {
    const el = document.getElementById(`${id}`) as HTMLElement;

    if (el) {
      el.classList.add('active');
    }
  });
};

export const removeActiveSplicerClasses = (ids: string[]) => {
  ids?.forEach(id => {
    const el = document.getElementById(`${id}`) as HTMLElement;

    if (el) {
      el.classList.remove('active');
    }
  });
};

export const intToHSL = (amount: number) => {
  const colors = [];
  const hueStep = 360 / amount;

  for (let i = 0; i < amount; i++) {
    const hue = (i * hueStep) % 360;
    const saturation = 50 + (i % 5) * 10;
    const lightness = 30 + (i % 5) * 10;
    const alpha = 1;
    const color = `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`;

    colors.push(color);
  }

  return colors;
};

export const normalizeExportSolverOutput = (facingGroups: Array<FacingGropOrigin>) => {
  return facingGroups.map((fg: FacingGropOrigin) => ({
    x: fg.x,
    y: fg.y,
    segment: fg.segment,
    packaging: fg.packaging,
    id: fg.id,
    located_fixture_id: fg.located_fixture_id,
    product_num: fg.product_num,
    horizontal_facings: fg.horizontal_facings,
    vertical_facings: fg.vertical_facings,
    depth_facings: fg.depth_facings,
    orientation_code: fg.orientation_code,
    cap_nested: fg.cap_nested,
    capped_horizontal_facings: fg.capped_horizontal_facings,
    capped_vertical_facings: fg.capped_vertical_facings,
    capped_depth_facings: fg.capped_depth_facings,
    capped_orientation_code: fg.capped_orientation_code,
    peg_id: fg.peg_id,
    esl_peg_id: fg.esl_peg_id,
  }));
};
